import React, { useEffect, useState, useCallback, useMemo, useRef, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  Radio,
  InputLabel,
  Paper,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PrintIcon from "@mui/icons-material/Print";
import BlockIcon from "@mui/icons-material/Block";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import { FaCopy, FaEdit, FaGlobe, FaPaperclip, FaSave, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";
import ModalSelecionarImovel from "../components/ModalSelecionarImovel";
import CampoSelecionar from "../components/CampoSelecionar";
import PropostaPrintTemplate from "../components/PropostaPrintTemplate";
import DialogInutilizarProposta from "../components/DialogInutilizarProposta";
import PainelAprovacao from "../components/Aprovacao/PainelAprovacao";
import DialogReprovarAprovacao from "../components/Aprovacao/DialogReprovarAprovacao";
import DialogQuestionarAprovacao from "../components/Aprovacao/DialogQuestionarAprovacao";
import DialogResponderAprovacao from "../components/Aprovacao/DialogResponderAprovacao";
import { limparCnpj, limparCpfCnpj, mascararCnpj, pareceCnpj, validarCnpj } from "../utils/documento";
import DocumentoEstrangeiroCampos, { FlagEstrangeiro } from "../components/DocumentoEstrangeiroCampos";
import { DOCUMENTO_ESTRANGEIRO_VAZIO, descreverDocumentoEstrangeiro, validarDocumentoEstrangeiro } from "../utils/documentoEstrangeiro";

const TIPOS_LOCACAO = ["RURAL", "URBANO"];

const TIPOS_IMOVEL_LOCACAO = ["TERRENO", "RESIDENCIAL", "CHÁCARA RESIDENCIAL", "COMERCIAL", "CHÁCARA COMERCIAL", "INDUSTRIAL/LOGÍSTICO"];

const TIPOS_GARANTIA = ["Sem Garantia", "Caução", "Seguro Fiança", "Fiador", "Imobiliária", "Título de Capitalização", "A Ser Determinado"];
// Rótulos de exibição — os valores internos ("Caução"/"Imobiliária") continuam os mesmos
// pra não quebrar propostas já salvas; só o texto mostrado ao usuário muda.
const LABELS_GARANTIA = { Caução: "Caução em Dinheiro", Imobiliária: "Caução em Imóvel" };
const TIPOS_PESSOA = ["PJ", "PF"];
const EXTENSOES_GARANTIA_DOC = [".pdf", ".jpg", ".jpeg", ".png", ".xlsx", ".doc", ".docx"];
const MAX_MB_GARANTIA_DOC = 15;
const NOMES_MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const OPCOES_APLICABILIDADE = ["APLICÁVEL", "NÃO APLICÁVEL"];

const TIPOS_PRIMEIRO_VENCIMENTO = ["IMEDIATO", "NÃO IMEDIATO"];

const TIPOS_MEDICAO_AGUA = ["RELÓGIO DEDICADO", "RELÓGIO COMPARTILHADO", "POÇO/FOSSA DEDICADO", "POÇO/FOSSA COMPARTILHADO"];

const TIPOS_MEDICAO_ENERGIA = ["RELÓGIO DEDICADO", "RELÓGIO COMPARTILHADO"];
function medicaoBloqueiaDemaisCampos(chave, valor) {
  const medicao = String(valor || "")
    .trim()
    .toUpperCase();

  if (chave === "agua") {
    return ["RELÓGIO DEDICADO", "POÇO/FOSSA", "POÇO/FOSSA DEDICADO", "POÇO/FOSSA COMPARTILHADO"].includes(medicao);
  }

  if (chave === "energia") {
    return medicao === "RELÓGIO DEDICADO";
  }

  return false;
}

const EMAIL_HELPER_TEXT = 'Para informar mais de um e-mail, separe por ";" — exemplo: "joao@empresa.com; maria@empresa.com; pedro@empresa.com"';

const fichaVazia = {
  tipoPessoa: "PJ",
  razaoSocial: "",
  cnpj: "",
  cpf: "",
  email: "",
  ...DOCUMENTO_ESTRANGEIRO_VAZIO,
  // Endereço — não editável nesta tela, apenas guardado (vindo da consulta de CNPJ) para
  // pré-preencher a Ficha de Cadastro (Etapa 2) quando a ficha completa for aberta.
  endereco: "",
  numero: "",
  bairro: "",
  cidade: "",
  uf: "",
  cep: "",
  complemento: "",
};

const formVazio = {
  complementoLocacao: "",
  areaTotalLocada: "",
  areaConstruidaTotalLocada: "",
  areaLocada: "",
  tipoLocacao: "",
  tipoImovelLocacao: "",
  prazo: "",
  dataInicio: "",
  dataFim: "",
  vAluguelMensal: "",
  vCondominio: "",
  vIPTU: "",
  vAguaEsgoto: "",
  vEnergia: "",
  vDesconto: "",
  descrDesconto: "",
  carencia: "",
  vInternet: "",
  vTotem: "",
  // ALUGUEL
  mesReajuste: "",
  indiceReajusteId: "",
  tipoVencimento: "Pós",
  diaVencimento: "",
  tipoPrimeiroVencimento: "",
  primeiroVencimento: "",

  // IPTU
  iptuAplicabilidade: "",
  iptuTipoVencimento: "",
  iptuDiaVencimento: "",
  iptuMesReajuste: "",
  iptuPrimeiroVencimento: "",

  // CONDOMÍNIO
  condominioAplicabilidade: "",
  condominioTipoVencimento: "",
  condominioDiaVencimento: "",
  condominioMesReajuste: "",
  condominioPrimeiroVencimento: "",

  // ÁGUA E ESGOTO
  aguaEsgotoTipoMedicao: "",
  aguaEsgotoTipoVencimento: "",
  aguaEsgotoDiaVencimento: "",
  aguaEsgotoPrimeiroVencimento: "",

  // ENERGIA
  energiaTipoMedicao: "",
  energiaTipoVencimento: "",
  energiaDiaVencimento: "",
  energiaPrimeiroVencimento: "",

  // INTERNET
  internetAplicabilidade: "",
  internetTipoVencimento: "",
  internetDiaVencimento: "",
  internetMesReajuste: "",
  internetIndiceReajusteId: "",
  internetPrimeiroVencimento: "",

  // TOTEM
  totemAplicabilidade: "",
  totemTipoVencimento: "",
  totemDiaVencimento: "",
  totemMesReajuste: "",
  totemIndiceReajusteId: "",
  totemPrimeiroVencimento: "",

  garantia: "",
  vCaucao: "",
  qtdeAluguelCaucao: "",
  boleto: "",
  boletoQuantidade: "",
  vBoleto: "",
  fiadorTipoGarantia: "",

  fiadorImovelTipo: "",
  fiadorImovelCidadeCartorioRgi: "",
  fiadorImovelNomeCartorioRgi: "",
  fiadorImovelRgi: "",
  fiadorImovelMatricula: "",
  fiadorImovelCartorio: "",
  fiadorImovelLivro: "",
  fiadorImovelFolha: "",
  fiadorImovelEndereco: "",
  fiadorImovelNumero: "",
  fiadorImovelBairro: "",
  fiadorImovelCidade: "",
  fiadorImovelUf: "",
  fiadorImovelCep: "",
  fiadorImovelComplemento: "",
  fiadorImovelUrlGoogleMaps: "",

  garantiaDescricaoImovel: "",
  garantiaCadastroMunicipal: "",
  garantiaSetor: "",
  garantiaQuarteirao: "",

  garantiaTipoImovel: "",
  garantiaRgi: "",
  garantiaMatricula: "",
  garantiaCartorio: "",
  garantiaLivro: "",
  garantiaFolha: "",
  garantiaEndereco: "",
  garantiaNumero: "",
  garantiaBairro: "",
  garantiaCidade: "",
  garantiaUf: "",
  garantiaCep: "",
  garantiaComplemento: "",
  garantiaUrlGoogleMaps: "",
  consideracoes: "",
};

const CAMPOS_VALOR = ["vAluguelMensal", "vCondominio", "vIPTU", "vAguaEsgoto", "vEnergia", "vDesconto", "vInternet", "vTotem", "vCaucao", "vBoleto"];

function apenasDigitos(v) {
  return v.replace(/\D/g, "");
}

function mascararCPF(v) {
  const d = apenasDigitos(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

// CNPJ numérico ou alfanumérico (letras A-Z nas 12 primeiras posições).
function mascararCNPJ(v) {
  return mascararCnpj(v);
}

function mascararCEP(v) {
  return apenasDigitos(v)
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
}

// MÁSCARA DE METRAGEM (pt-BR) — 4 casas decimais
// Mesmo padrão utilizado no Cadastro de Imóveis.
const METRAGEM_DECIMAIS = 4;

function stripLeadingZeros(intDigits) {
  if (!intDigits) return "";

  const stripped = intDigits.replace(/^0+(?=\d)/, "");

  return stripped || "0";
}

function formatThousands(intDigits) {
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function maskMetragemTypingBR(input) {
  const s = String(input ?? "").trim();

  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return intFormatted;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS);

  return `${intFormatted},${decDigits}`;
}

function maskMetragem4BR(input) {
  const s = String(input ?? "").trim();

  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return `${intFormatted},0000`;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS).padEnd(METRAGEM_DECIMAIS, "0");

  return `${intFormatted},${decDigits}`;
}

function formatMetragemBancoBR(input) {
  if (input === null || input === undefined || input === "") {
    return "";
  }

  const numero = Number(input);

  if (Number.isNaN(numero)) {
    return "";
  }

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: METRAGEM_DECIMAIS,
    maximumFractionDigits: METRAGEM_DECIMAIS,
  });
}

function formatMetragemExibicaoBR(input) {
  if (input === null || input === undefined || input === "") {
    return "—";
  }

  let numero;

  if (typeof input === "number") {
    numero = input;
  } else {
    let texto = String(input).trim();

    if (!texto) {
      return "—";
    }

    // Se já veio no formato brasileiro:
    // 1.866,0000 -> 1866.0000
    if (texto.includes(",")) {
      texto = texto.replace(/\./g, "").replace(",", ".");
    }

    numero = Number(texto);
  }

  if (!Number.isFinite(numero)) {
    return "—";
  }

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

// Consulta ViaCEP — mesma rotina já usada na tela de Ficha de Cadastro.
async function buscarEnderecoPorCep(cep) {
  const digitos = apenasDigitos(cep);
  if (digitos.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
    const data = await res.json();
    if (data.erro) return null;
    return { endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, uf: data.uf };
  } catch {
    return null;
  }
}

const garantiasDe = (str) => (str || "").split(",").filter(Boolean);

function todoZeros(doc) {
  const d = limparCpfCnpj(doc);
  return d.length > 0 && /^0+$/.test(d);
}

function validarCPF(cpf) {
  const d = apenasDigitos(cpf);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(d[i]) * (10 - i);
  let r = (soma * 10) % 11;
  if (r >= 10) r = 0;
  if (r !== parseInt(d[9])) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(d[i]) * (11 - i);
  r = (soma * 10) % 11;
  if (r >= 10) r = 0;
  return r === parseInt(d[10]);
}

function validarCNPJ(cnpj) {
  return validarCnpj(cnpj);
}

async function buscarRazaoSocialPorCnpj(cnpjMascarado) {
  const digitos = limparCnpj(cnpjMascarado ?? "");
  if (digitos.length !== 14) return { ok: false, motivo: "invalido", mensagem: "" };

  const token = localStorage.getItem("token");
  try {
    const res = await fetch(`/api/proprietarios/consulta-documento?tipo=PJ&documento=${digitos}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const corpo = await res.json().catch(() => ({}));
    if (!res.ok) {
      const motivo = res.status === 404 ? "nao_encontrado" : res.status === 400 ? "invalido" : "servidor";
      return { ok: false, motivo, mensagem: corpo?.erro || "Erro ao consultar CNPJ." };
    }
    if (!corpo.nome) return { ok: false, motivo: "vazio", mensagem: "CNPJ localizado, porém sem razão social retornada." };
    return {
      ok: true,
      razaoSocial: corpo.nome,
      endereco: corpo.rua || "",
      numero: corpo.numero || "",
      bairro: corpo.bairro || "",
      cidade: corpo.cidade || "",
      uf: corpo.estado || "",
      cep: corpo.cep || "",
      complemento: corpo.complemento || "",
    };
  } catch (err) {
    console.error("Erro ao consultar CNPJ:", err);
    return { ok: false, motivo: "rede", mensagem: "Erro de conexão ao consultar CNPJ." };
  }
}

function formatBRL(v) {
  if (v === "" || v === null || v === undefined) return "";
  let s = String(v);
  // Formato BR com vírgula decimal (ex: "1.234,56") → normaliza para float
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const num = parseFloat(s);
  if (isNaN(num)) return "";
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function truncarTexto(texto, max) {
  if (!texto) return "";
  return texto.length > max ? `${texto.slice(0, max)}…` : texto;
}

// Nome do arquivo sugerido pelo navegador ao "Salvar como PDF" — usa o <title> da
// página impressa. Padrão: Código_ApelidoImóvel_PrimeiroNomeLocatária_DataInício_PC(ou FC).
function primeiroNome(texto) {
  return (texto || "").trim().split(/\s+/)[0] || "";
}

function dataParaNomeArquivo(val) {
  if (!val) return "";
  const s = String(val).substring(0, 10);
  const [ano, mes, dia] = s.split("-");
  if (!ano || !mes || !dia) return "";
  return `${dia}${mes}${ano}`;
}

function nomeArquivoImpressao(proposta, sufixo) {
  const codigo = proposta?.codigo || "";
  const apelido = proposta?.imovel?.apelido || "";
  const locataria = (proposta?.fichaPessoas || []).find((f) => f.campoProposta === "Locataria");
  const nomeLocataria = primeiroNome(locataria?.razaoSocial).toUpperCase();
  const dataInicio = dataParaNomeArquivo(proposta?.dataInicio);
  return [codigo, apelido, nomeLocataria, dataInicio, sufixo].filter(Boolean).join("_");
}

// No PDF gerado no backend o nome já vem no Content-Disposition (mesmo padrão de
// nomeArquivoImpressao, montado por gerarPropostaDocx.js).
function nomeArquivoDaResposta(resposta, padrao) {
  const disposicao = resposta.headers.get("content-disposition") || "";
  const match = disposicao.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return padrao;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function limitarAnoData(value) {
  // input type="date" não trava a digitação do ano em 4 dígitos; força o corte aqui
  if (!value) return value;
  const [ano, mes, dia] = value.split("-");
  if (ano && ano.length > 4) return `${ano.slice(0, 4)}-${mes || "01"}-${dia || "01"}`;
  return value;
}

function calcularDataFim(dataInicio, prazoMeses) {
  const meses = parseInt(prazoMeses, 10);
  if (!dataInicio || !meses || meses <= 0) return "";
  // input type="date" permite digitar ano com mais de 4 dígitos, gerando Date inválida
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dataInicio)) return "";
  const d = new Date(dataInicio + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  const diaInicio = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + meses);
  const ultimoDia = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(diaInicio, ultimoDia));
  if (isNaN(d.getTime())) return "";
  return d.toISOString().substring(0, 10);
}

function textoPrimeiroVencimentoNaoImediato(tipoVencimento, diaVencimento) {
  const dia = parseInt(diaVencimento, 10);

  if (!Number.isInteger(dia) || dia < 1 || dia > 31) {
    return "";
  }

  const tipo = String(tipoVencimento || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  if (tipo === "pos") {
    return `Primeiro vencimento será no dia ${dia} do mês posterior ao mês da entrega da chave.`;
  }

  if (tipo === "corrente") {
    return `Primeiro vencimento será no dia ${dia} do mês corrente da entrega da chave.`;
  }

  return "";
}

function calcularSugestaoPrimeiroVencimento(dataInicio, tipoVencimento, diaVencimento) {
  if (!dataInicio || !tipoVencimento || !diaVencimento) {
    return "";
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dataInicio)) {
    return "";
  }

  const [anoInicio, mesInicio, diaInicio] = dataInicio.split("-").map(Number);

  const diaPagamento = parseInt(diaVencimento, 10);

  if (!anoInicio || !mesInicio || !diaInicio || !diaPagamento || diaPagamento < 1 || diaPagamento > 31) {
    return "";
  }

  const tipo = String(tipoVencimento)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  if (!["pos", "corrente"].includes(tipo)) {
    return "";
  }

  let adicionarMes = 0;

  if (tipo === "pos") {
    adicionarMes = 1;
  } else if (tipo === "corrente" && diaPagamento < diaInicio) {
    adicionarMes = 1;
  }

  const mesBase = new Date(Date.UTC(anoInicio, mesInicio - 1 + adicionarMes, 1));

  const anoVencimento = mesBase.getUTCFullYear();

  const mesVencimento = mesBase.getUTCMonth();

  const ultimoDiaMes = new Date(Date.UTC(anoVencimento, mesVencimento + 1, 0)).getUTCDate();

  const diaVencimentoFinal = Math.min(diaPagamento, ultimoDiaMes);

  return `${anoVencimento}-${String(mesVencimento + 1).padStart(2, "0")}-${String(diaVencimentoFinal).padStart(2, "0")}`;
}

function parseBRL(v) {
  if (!v && v !== 0) {
    return "";
  }

  return String(v).replace(/\./g, "").replace(",", ".");
}

function onChangeValor(campo, ro, setForm) {
  return (e) => {
    if (ro) return;
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      setForm((p) => ({ ...p, [campo]: "" }));
      return;
    }
    const formatted = (parseInt(digits, 10) / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setForm((p) => ({ ...p, [campo]: formatted }));
  };
}

function formatarData(val) {
  if (!val) return "—";
  const s = String(val).substring(0, 10);
  const [ano, mes, dia] = s.split("-").map(Number);
  if (!ano || !mes || !dia) return "—";
  return `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}/${ano}`;
}

function formatarDataHora(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_GERAL_CONFIG = {
  EmDigitacao: { label: "Em Digitação", cor: "#555" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1998a8" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },

  ComPendencias: {
    label: "Com Pendências",
    cor: "#ed6c02",
  },

  AguardandoAssinatura: {
    label: "Aguard. Assinatura",
    cor: "#7b1fa2",
  },

  Concluido: { label: "Concluído", cor: "#004d40" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
  Inutilizada: { label: "Inutilizada", cor: "#4e342e" },
};

const LABEL_STATUS_APROVACAO = { Aprovar: "A Aprovar", Responder: "A Responder", Aguardando: "Aguardando Aprovação" };

function chipStatusGeral(status) {
  const cfg = STATUS_GERAL_CONFIG[status] ?? { label: status ?? "—", cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

const STATUS_FICHAS_CONFIG = {
  EmPreenchimento: { label: "Em Preenchimento", cor: "#1998a8" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1976d2" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
  Inutilizada: { label: "Inutilizada", cor: "#4e342e" },
};

const STATUS_ANALISE_CREDITO_CONFIG = {
  AguardandoUpload: { label: "Em Preenchimento", cor: "#1998a8" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1976d2" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
};

function chipStatusFichas(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  const cfg = STATUS_FICHAS_CONFIG[status] ?? { label: status, cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

function chipStatusAnaliseCredito(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  const cfg = STATUS_ANALISE_CREDITO_CONFIG[status] ?? { label: status, cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

// Colunas cujo campo ainda não existe no backend (previstas para uma etapa futura do fluxo:
// minuta de contrato, vistorias, entrega de chave, aviso prévio, distrato).
// Enquanto o campo não existir em `p`, exibe "—"; quando passar a existir, mostra o valor cru.
function chipStatusGenerico(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  return <Chip label={status} size="small" sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }} />;
}

// statusFluxo do processo de Assinatura Final (dbo.AssinaturaProcesso) vinculado à proposta
const STATUS_ASSINATURA_CONFIG = {
  RASCUNHO: {
    label: "Assinatura em Rascunho",
    cor: "#555",
  },

  PRONTO_PARA_ENVIO: {
    label: "Assinatura Pronta para Envio",
    cor: "#1976d2",
  },

  ENVIADO: {
    label: "Em Assinatura",
    cor: "#1976d2",
  },

  AGUARDANDO_ASSINATURAS: {
    label: "Em Assinatura",
    cor: "#7b1fa2",
  },

  PARCIALMENTE_ASSINADO: {
    label: "Parcialmente Assinada",
    cor: "#ed6c02",
  },

  EM_ASSINATURA_FISICA: {
    label: "Em Assinatura Física",
    cor: "#ed6c02",
  },

  ASSINADO: {
    label: "Assinada",
    cor: "#2e7d32",
  },

  ARQUIVADO: {
    label: "Arquivada",
    cor: "#004d40",
  },

  CANCELADO: {
    label: "Assinatura Cancelada",
    cor: "#d32f2f",
  },
};

function chipStatusAssinatura(status) {
  if (!status) {
    return (
      <Chip
        label="—"
        size="small"
        sx={{
          backgroundColor: "#444",
          color: "#aaa",
        }}
      />
    );
  }

  const cfg = STATUS_ASSINATURA_CONFIG[status] ?? {
    label: status,
    cor: "#444",
  };

  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.cor,
        color: "#fff",
        fontWeight: "bold",
      }}
    />
  );
}

const STATUS_MINUTA_CONTRATO_CONFIG = {
  EmElaboracao: {
    label: "Em Elaboração",
    cor: "#555",
  },

  EmAprovacao: {
    label: "Em Aprovação",
    cor: "#1998a8",
  },

  Aprovado: {
    label: "Aprovado",
    cor: "#2e7d32",
  },

  Reprovado: {
    label: "Reprovado",
    cor: "#d32f2f",
  },

  Inutilizado: {
    label: "Inutilizado",
    cor: "#4e342e",
  },

  Invalidado: {
    label: "Invalidado",
    cor: "#4e342e",
  },
};

function chipStatusProposta(statusProposta, statusAssinatura) {
  /*
   * Quando existe processo de assinatura,
   * ele representa o estágio atual da proposta.
   */
  if (statusAssinatura) {
    return chipStatusAssinatura(statusAssinatura);
  }

  return chipStatusGeral(statusProposta ?? "EmDigitacao");
}

function chipStatusMinutaContrato(statusMinuta, statusAssinatura) {
  /*
   * Depois que a minuta entra em assinatura,
   * a coluna passa a mostrar o estágio da assinatura.
   */
  if (statusAssinatura) {
    return chipStatusAssinatura(statusAssinatura);
  }

  if (!statusMinuta) {
    return (
      <Chip
        label="—"
        size="small"
        sx={{
          backgroundColor: "#444",
          color: "#aaa",
        }}
      />
    );
  }

  const cfg = STATUS_MINUTA_CONTRATO_CONFIG[statusMinuta] ?? {
    label: statusMinuta,
    cor: "#444",
  };

  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.cor,
        color: "#fff",
        fontWeight: "bold",
      }}
    />
  );
}
// ── Subcomponente: sublista inline de FichaPessoa ─────────────────────────────

const SublistaFichas = memo(function SublistaFichas({
  fichas,
  setFichas,
  somenteLeitura,
  podeCadastrar,
  permitirEditarEmail = false,
  permitirRemover = false,

  // Usado especificamente no bloco LOCADORAS
  acaoLinha = null,
  usarBuscaProprietario = false,
  proprietariosBusca = [],

  titulo,
  mostrarSnack,
}) {
  const [novaFicha, setNovaFicha] = useState({ ...fichaVazia });
  // Nome do flag conforme o bloco (locadores vêm do cadastro de proprietários).
  const rotuloFlagEstrangeiro = usarBuscaProprietario ? "Locador estrangeiro" : "Locatário estrangeiro";
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState({ ...fichaVazia });

  const [modalProprietarioEdicaoAberto, setModalProprietarioEdicaoAberto] = useState(false);

  const [buscandoNovaFicha, setBuscandoNovaFicha] = useState(false);
  const [ultimoCnpjNovaFicha, setUltimoCnpjNovaFicha] = useState("");
  const [buscandoEditForm, setBuscandoEditForm] = useState(false);
  const [ultimoCnpjEditForm, setUltimoCnpjEditForm] = useState("");

  useEffect(() => {
    if (somenteLeitura) {
      setEditIdx(null);
      setEditForm({ ...fichaVazia });
      setUltimoCnpjEditForm("");
    }
  }, [somenteLeitura]);

  const consultarCnpjEAplicar = async (masked, digitos, setState, setBuscando, setUltimoCnpj) => {
    if (digitos === "") return;
    setUltimoCnpj(digitos);
    setBuscando(true);
    const resultado = await buscarRazaoSocialPorCnpj(masked);
    setBuscando(false);
    if (resultado.ok) {
      setState((p) => ({
        ...p,
        razaoSocial: resultado.razaoSocial.toUpperCase(),
        endereco: resultado.endereco ? resultado.endereco.toUpperCase() : p.endereco,
        numero: resultado.numero ? resultado.numero.toUpperCase() : p.numero,
        bairro: resultado.bairro ? resultado.bairro.toUpperCase() : p.bairro,
        cidade: resultado.cidade ? resultado.cidade.toUpperCase() : p.cidade,
        uf: resultado.uf ? resultado.uf.toUpperCase() : p.uf,
        cep: resultado.cep || p.cep,
        complemento: resultado.complemento ? resultado.complemento.toUpperCase() : p.complemento,
      }));
    } else if (resultado.motivo !== "invalido") {
      mostrarSnack(resultado.mensagem, ["nao_encontrado", "vazio"].includes(resultado.motivo) ? "warning" : "error");
    }
  };

  const mostrarAcoes = !somenteLeitura && (podeCadastrar || permitirEditarEmail || permitirRemover);

  // Locadora (permitirEditarEmail=true) vem pré-preenchida a partir do proprietário do imóvel;
  // quando não há CNPJ/CPF cadastrado pra ele, o tipo exibido vira "Nome Fantasia" em vez de PJ/PF.
  // Sem CPF/CNPJ = Nome Fantasia, exceto locador estrangeiro (identificado pelo documento estrangeiro).
  const labelTipoLocadora = (ficha) => (permitirEditarEmail && !ficha.cnpj && !ficha.cpf && !ficha.estrangeiro ? "Nome Fantasia" : ficha.tipoPessoa);

  const validarDocumento = (ficha) => {
    // Estrangeiro: CPF/CNPJ opcional; sem ele, o documento estrangeiro é obrigatório.
    const errosEstrangeiro = Object.values(
      validarDocumentoEstrangeiro(ficha, { temDocumentoNacional: Boolean(ficha.tipoPessoa === "PJ" ? ficha.cnpj : ficha.cpf) }),
    );
    if (errosEstrangeiro.length) {
      mostrarSnack(errosEstrangeiro[0], "error");
      return false;
    }
    if (ficha.tipoPessoa === "PJ" && ficha.cnpj && !todoZeros(ficha.cnpj)) {
      if (!validarCNPJ(ficha.cnpj)) {
        mostrarSnack("CNPJ inválido.", "error");
        return false;
      }
    }
    if (ficha.tipoPessoa === "PF" && ficha.cpf && !todoZeros(ficha.cpf)) {
      if (!validarCPF(ficha.cpf)) {
        mostrarSnack("CPF inválido.", "error");
        return false;
      }
    }
    return true;
  };

  const handleAdicionar = () => {
    if (!novaFicha.razaoSocial.trim()) return;
    if (!validarDocumento(novaFicha)) return;
    setFichas((prev) => [...prev, { ...novaFicha }]);
    setNovaFicha({ ...fichaVazia });
    setUltimoCnpjNovaFicha("");
  };

  const handleSalvarEdit = () => {
    if (!editForm.razaoSocial.trim()) return;
    // CNPJ/CPF só é revalidado quando o campo é de fato editável
    if (podeCadastrar && !validarDocumento(editForm)) return;
    setFichas((prev) => prev.map((f, i) => (i === editIdx ? { ...editForm } : f)));
    setEditIdx(null);
    setUltimoCnpjEditForm("");
  };

  const handleRemover = (idx) => {
    setFichas((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSelecionarProprietarioEdicao = (proprietario) => {
    if (!proprietario) return;

    const ehFisica = proprietario.tipoPessoa === "fisica";

    const ehJuridica = proprietario.tipoPessoa === "juridica";

    const tipoPessoa = ehFisica ? "PF" : "PJ";

    setEditForm((prev) => ({
      ...prev,

      proprietarioId: proprietario.id ?? proprietario._id ?? null,

      tipoPessoa,

      razaoSocial: String(proprietario.nome || "").toUpperCase(),

      cnpj: ehJuridica && proprietario.cnpj ? mascararCNPJ(proprietario.cnpj) : "",

      cpf: ehFisica && proprietario.cpf ? mascararCPF(proprietario.cpf) : "",

      // Proprietário estrangeiro (vem do cadastro do proprietário).
      estrangeiro: Boolean(proprietario.estrangeiro),
      documentoEstrangeiroTipo: proprietario.documentoEstrangeiroTipo || "",
      documentoEstrangeiroNumero: proprietario.documentoEstrangeiroNumero || "",
      documentoEstrangeiroPais: proprietario.documentoEstrangeiroPais || "",

      // IMPORTANTE:
      // não apaga o e-mail já digitado durante a troca.
      email: prev.email || "",
    }));

    setModalProprietarioEdicaoAberto(false);
  };

  const inputSx = (readOnly) => ({
    "& .MuiOutlinedInput-root": {
      color: "var(--ig-t-ffffff)",
      backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
      "& fieldset": { borderColor: readOnly ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
    },
    "& input": { color: "var(--ig-t-ffffff)" },
    "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" },
  });

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1 }}>
        {titulo}
      </Typography>

      <Table
        size="small"
        sx={{
          width: "100%",
          tableLayout: "fixed",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
                // largura para o CPF/CNPJ + interruptor "estrangeiro" ao lado
                width: podeCadastrar && !somenteLeitura ? 360 : 190,
              }}
            >
              CPF / CNPJ
            </TableCell>

            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
                width: 70,
              }}
            >
              Tipo
            </TableCell>

            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
                width: 650,
              }}
            >
              Nome / Razão Social
            </TableCell>
            {mostrarAcoes && (
              <TableCell
                align="right"
                sx={{
                  backgroundColor: "var(--ig-f-252525)",
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  py: 0.8,
                  width: 150,
                  whiteSpace: "nowrap",
                }}
              >
                Ações
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {fichas.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={mostrarAcoes ? 4 : 3}
                sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
              >
                Nenhum registro adicionado.
              </TableCell>
            </TableRow>
          )}

          {fichas.map((f, idx) =>
            editIdx === idx ? (
              <React.Fragment key={idx}>
                <TableRow>
                  <TableCell sx={{ borderBottom: "none", py: 0.5, color: "var(--ig-t-cccccc)" }}>
                    {podeCadastrar ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <TextField
                          size="small"
                          value={editForm.tipoPessoa === "PJ" ? editForm.cnpj : editForm.cpf}
                          onChange={(e) => {
                            const digitos = limparCpfCnpj(e.target.value);
                            const pj = pareceCnpj(digitos);
                            const masked = pj ? mascararCNPJ(digitos) : mascararCPF(digitos);
                            setEditForm((p) => ({ ...p, tipoPessoa: pj ? "PJ" : "PF", cnpj: pj ? masked : "", cpf: pj ? "" : masked }));
                            if (pj && digitos.length === 14 && digitos !== ultimoCnpjEditForm) {
                              consultarCnpjEAplicar(masked, digitos, setEditForm, setBuscandoEditForm, setUltimoCnpjEditForm);
                            }
                          }}
                          placeholder="CPF ou CNPJ"
                          slotProps={{
                            input: {
                              endAdornment:
                                editForm.tipoPessoa === "PJ" && buscandoEditForm ? (
                                  <InputAdornment position="end">
                                    <CircularProgress size={16} />
                                  </InputAdornment>
                                ) : null,
                            },
                          }}
                          sx={{ minWidth: 170, ...inputSx(false) }}
                        />
                        {!usarBuscaProprietario && (
                          <FlagEstrangeiro
                            valor={editForm}
                            onChange={(campo, valor) => setEditForm((p) => ({ ...p, [campo]: valor }))}
                            rotulo={rotuloFlagEstrangeiro}
                          />
                        )}
                      </Box>
                    ) : editForm.tipoPessoa === "PJ" ? (
                      editForm.cnpj || "—"
                    ) : (
                      editForm.cpf || "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 0.5, color: "var(--ig-t-cccccc)" }}>
                    {editForm.estrangeiro && !editForm.cnpj && !editForm.cpf && !usarBuscaProprietario && podeCadastrar ? (
                      <TextField
                        select
                        size="small"
                        value={editForm.tipoPessoa}
                        onChange={(e) => setEditForm((p) => ({ ...p, tipoPessoa: e.target.value }))}
                        sx={{ minWidth: 80, ...inputSx(false) }}
                      >
                        <MenuItem value="PF">PF</MenuItem>
                        <MenuItem value="PJ">PJ</MenuItem>
                      </TextField>
                    ) : (
                      labelTipoLocadora(editForm)
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "none",
                      py: 0.5,
                      color: "var(--ig-t-ffffff)",
                    }}
                  >
                    {usarBuscaProprietario ? (
                      <TextField
                        label="Nome / Razão Social"
                        size="small"
                        value={editForm.razaoSocial || ""}
                        onClick={() => setModalProprietarioEdicaoAberto(true)}
                        slotProps={{
                          input: {
                            readOnly: true,

                            endAdornment: (
                              <InputAdornment position="end">
                                <SearchIcon
                                  sx={{
                                    color: "#f44336",
                                    fontSize: 20,
                                    cursor: "pointer",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{
                          width: "100%",
                          maxWidth: 600,

                          "& .MuiInputLabel-root": {
                            color: "var(--ig-t-ffffff)",
                            fontWeight: "bold",
                          },

                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "var(--ig-f-2a2a2a)",
                            color: "var(--ig-t-ffffff)",
                            cursor: "pointer",

                            "& fieldset": {
                              borderColor: "var(--ig-b-444444)",
                            },

                            "&:hover fieldset": {
                              borderColor: "var(--ig-b-888888)",
                            },
                          },

                          "& input": {
                            color: "var(--ig-t-ffffff)",
                            cursor: "pointer",
                          },
                        }}
                      />
                    ) : podeCadastrar ? (
                      <TextField
                        size="small"
                        value={editForm.razaoSocial}
                        fullWidth
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            razaoSocial: e.target.value.toUpperCase(),
                          }))
                        }
                        slotProps={{
                          htmlInput: {
                            maxLength: 255,
                          },
                        }}
                        sx={{
                          ...inputSx(false),

                          "& input": {
                            color: "var(--ig-t-ffffff)",
                            textTransform: "uppercase",
                          },
                        }}
                      />
                    ) : (
                      editForm.razaoSocial
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: "none", py: 0.5, whiteSpace: "nowrap" }}>
                    <Tooltip title="Confirmar">
                      <IconButton
                        size="small"
                        onClick={handleSalvarEdit}
                        sx={{
                          backgroundColor: "#2e7d32",
                          color: "#fff",
                          borderRadius: "4px",
                          mx: 0.25,
                          width: "auto",
                          "&:hover": { backgroundColor: "#1b5e20" },
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancelar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditIdx(null);
                          setUltimoCnpjEditForm("");
                        }}
                        sx={{ backgroundColor: "#555", color: "#fff", borderRadius: "4px", mx: 0.25, width: "auto", "&:hover": { backgroundColor: "#444" } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {editForm.estrangeiro && (
                  <TableRow>
                    <TableCell colSpan={mostrarAcoes ? 4 : 3} sx={{ borderBottom: "none", py: 0.5 }}>
                      <DocumentoEstrangeiroCampos
                        mostrarFlag={false}
                        obrigatorio={!(editForm.cnpj || editForm.cpf)}
                        valor={editForm}
                        onChange={(campo, valor) => setEditForm((p) => ({ ...p, [campo]: valor }))}
                        somenteLeitura={!podeCadastrar || usarBuscaProprietario}
                        campoSx={() => inputSx(false)}
                      />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={mostrarAcoes ? 4 : 3} sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                    {podeCadastrar || permitirEditarEmail ? (
                      <TextField
                        label="E-mail"
                        size="small"
                        fullWidth
                        helperText={EMAIL_HELPER_TEXT}
                        value={editForm.email}
                        onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                        slotProps={{ htmlInput: { maxLength: 500 } }}
                        sx={inputSx(false)}
                      />
                    ) : (
                      <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.875rem" }}>E-mail: {editForm.email || "—"}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ) : (
              <React.Fragment key={idx}>
                <TableRow sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                  <TableCell sx={{ borderBottom: "none", color: "var(--ig-t-cccccc)", py: 0.8 }}>
                    {(f.tipoPessoa === "PJ" ? f.cnpj : f.cpf) || descreverDocumentoEstrangeiro(f) || "—"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "var(--ig-t-cccccc)", py: 0.8 }}>{labelTipoLocadora(f)}</TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "var(--ig-t-ffffff)", py: 0.8 }}>{f.razaoSocial}</TableCell>
                  {mostrarAcoes && (
                    <TableCell
                      align="right"
                      sx={{
                        borderBottom: "none",
                        py: 0.8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {acaoLinha?.(f, idx)}

                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditIdx(idx);

                              setEditForm({
                                ...f,
                              });

                              setUltimoCnpjEditForm(limparCnpj(f.cnpj || ""));
                            }}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",

                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {(podeCadastrar || permitirRemover) && (!usarBuscaProprietario || idx > 0) && (
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              onClick={() => handleRemover(idx)}
                              sx={{
                                backgroundColor: "#d32f2f",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",

                                "&:hover": {
                                  backgroundColor: "#c62828",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                  <TableCell
                    colSpan={mostrarAcoes ? 4 : 3}
                    sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)", py: 0.8, fontSize: "0.875rem" }}
                  >
                    E-mail: {f.email || "—"}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ),
          )}

          {/* Linha de adição */}
          {!somenteLeitura && podeCadastrar && (
            <>
              <TableRow>
                <TableCell sx={{ borderBottom: "none", py: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TextField
                      size="small"
                      value={novaFicha.tipoPessoa === "PJ" ? novaFicha.cnpj : novaFicha.cpf}
                      onChange={(e) => {
                        const digitos = limparCpfCnpj(e.target.value);
                        const pj = pareceCnpj(digitos);
                        const masked = pj ? mascararCNPJ(digitos) : mascararCPF(digitos);
                        setNovaFicha((p) => ({ ...p, tipoPessoa: pj ? "PJ" : "PF", cnpj: pj ? masked : "", cpf: pj ? "" : masked }));
                        if (pj && digitos.length === 14 && digitos !== ultimoCnpjNovaFicha) {
                          consultarCnpjEAplicar(masked, digitos, setNovaFicha, setBuscandoNovaFicha, setUltimoCnpjNovaFicha);
                        }
                      }}
                      placeholder="CPF ou CNPJ"
                      slotProps={{
                        input: {
                          endAdornment:
                            novaFicha.tipoPessoa === "PJ" && buscandoNovaFicha ? (
                              <InputAdornment position="end">
                                <CircularProgress size={16} />
                              </InputAdornment>
                            ) : null,
                        },
                      }}
                      sx={{ minWidth: 170, ...inputSx(false) }}
                    />
                    <FlagEstrangeiro
                      valor={novaFicha}
                      onChange={(campo, valor) => setNovaFicha((p) => ({ ...p, [campo]: valor }))}
                      rotulo={rotuloFlagEstrangeiro}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: "none", py: 0.5, color: "var(--ig-t-cccccc)" }}>
                  {novaFicha.estrangeiro && !novaFicha.cnpj && !novaFicha.cpf && !usarBuscaProprietario && podeCadastrar ? (
                    <TextField
                      select
                      size="small"
                      value={novaFicha.tipoPessoa}
                      onChange={(e) => setNovaFicha((p) => ({ ...p, tipoPessoa: e.target.value }))}
                      sx={{ minWidth: 80, ...inputSx(false) }}
                    >
                      <MenuItem value="PF">PF</MenuItem>
                      <MenuItem value="PJ">PJ</MenuItem>
                    </TextField>
                  ) : (
                    novaFicha.tipoPessoa
                  )}
                </TableCell>
                <TableCell sx={{ borderBottom: "none", py: 0.5 }}>
                  <TextField
                    size="small"
                    value={novaFicha.razaoSocial}
                    fullWidth
                    onChange={(e) => setNovaFicha((p) => ({ ...p, razaoSocial: e.target.value.toUpperCase() }))}
                    placeholder="Nome / Razão Social"
                    slotProps={{ htmlInput: { maxLength: 255 } }}
                    sx={{ ...inputSx(false), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", py: 0.5, whiteSpace: "nowrap" }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAdicionar}
                    sx={{
                      backgroundColor: "#1998a8",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#1565c0" },
                      textTransform: "none",
                      fontWeight: "bold",
                      width: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Adicionar Locatária
                  </Button>
                  {(novaFicha.razaoSocial || novaFicha.cnpj || novaFicha.cpf || novaFicha.email) && (
                    <Tooltip title="Limpar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNovaFicha({ ...fichaVazia });
                          setUltimoCnpjNovaFicha("");
                        }}
                        sx={{ ml: 0.5, backgroundColor: "#555", color: "#fff", borderRadius: "4px", width: "auto", "&:hover": { backgroundColor: "#444" } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
              {novaFicha.estrangeiro && (
                <TableRow>
                  <TableCell colSpan={mostrarAcoes ? 4 : 3} sx={{ borderBottom: "none", py: 0.5 }}>
                    <DocumentoEstrangeiroCampos
                      mostrarFlag={false}
                      obrigatorio={!(novaFicha.cnpj || novaFicha.cpf)}
                      valor={novaFicha}
                      onChange={(campo, valor) => setNovaFicha((p) => ({ ...p, [campo]: valor }))}
                      somenteLeitura={false}
                      campoSx={() => inputSx(false)}
                    />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={mostrarAcoes ? 4 : 3} sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <TextField
                    label="E-mail"
                    size="small"
                    fullWidth
                    helperText={EMAIL_HELPER_TEXT}
                    value={novaFicha.email}
                    onChange={(e) => setNovaFicha((p) => ({ ...p, email: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                    sx={inputSx(false)}
                  />
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>

      {usarBuscaProprietario && (
        <ModalSelecionarProprietario
          open={modalProprietarioEdicaoAberto}
          onClose={() => setModalProprietarioEdicaoAberto(false)}
          proprietarios={proprietariosBusca}
          onSelect={handleSelecionarProprietarioEdicao}
        />
      )}
    </Box>
  );
});

// ── Modal: selecionar proprietário para Locadora ─────────────────────────────

const ModalSelecionarProprietario = memo(function ModalSelecionarProprietario({ open, onClose, onSelect, proprietarios }) {
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);

  useEffect(() => {
    if (!open) {
      setFiltro("");
      setPagina(0);
    }
  }, [open]);

  const listaFiltrada = useMemo(() => {
    const termo = String(filtro || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

    if (!termo) {
      return proprietarios;
    }

    return proprietarios.filter((p) => {
      const documento = (p.tipoPessoa === "fisica" ? mascararCPF(p.cpf || "") : mascararCNPJ(p.cnpj || "")) || descreverDocumentoEstrangeiro(p);

      const tipo = p.tipoPessoa === "fisica" ? "PF" : p.tipoPessoa === "juridica" ? "PJ" : "Nome Fantasia";

      const texto = [documento, tipo, p.nome]
        .join(" ")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

      return texto.includes(termo);
    });
  }, [filtro, proprietarios]);

  const paginaAtual = listaFiltrada.slice(pagina * porPagina, pagina * porPagina + porPagina);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--ig-f-2c2c2c)",
          color: "var(--ig-t-ffffff)",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "var(--ig-t-ffffff)",
          fontWeight: 700,
        }}
      >
        Selecionar Proprietário
        <IconButton type="button" onClick={onClose}>
          <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 2,
            pt: 1,
          }}
        >
          <TextField
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPagina(0);
            }}
            placeholder="Pesquisar por CPF, CNPJ, tipo ou nome."
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{
                        color: "var(--ig-t-9ca3af)",
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "var(--ig-f-2a2a2a)",
                color: "var(--ig-t-ffffff)",
              },
            }}
          />
        </Box>

        <Table
          size="small"
          sx={{
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                }}
              >
                CPF / CNPJ
              </TableCell>

              <TableCell
                sx={{
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                }}
              >
                Tipo
              </TableCell>

              <TableCell
                sx={{
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                }}
              >
                Nome / Razão Social
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginaAtual.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    color: "var(--ig-t-888888)",
                    py: 3,
                  }}
                >
                  Nenhum proprietário encontrado.
                </TableCell>
              </TableRow>
            ) : (
              paginaAtual.map((p) => {
                const id = p.id ?? p._id;

                const tipo = p.tipoPessoa === "fisica" ? "PF" : p.tipoPessoa === "juridica" ? "PJ" : "Nome Fantasia";

                const documento =
                  (p.tipoPessoa === "fisica" ? mascararCPF(p.cpf || "") : p.tipoPessoa === "juridica" ? mascararCNPJ(p.cnpj || "") : "") ||
                  descreverDocumentoEstrangeiro(p);

                return (
                  <TableRow
                    key={id}
                    hover
                    onClick={() => {
                      onSelect(p);
                      onClose();
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "var(--ig-t-cccccc)",
                      }}
                    >
                      {documento || "—"}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "var(--ig-t-cccccc)",
                      }}
                    >
                      {tipo}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        fontWeight: 600,
                      }}
                    >
                      {String(p.nome || "").toUpperCase()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={listaFiltrada.length}
          page={pagina}
          onPageChange={(_, novaPagina) => setPagina(novaPagina)}
          rowsPerPage={porPagina}
          onRowsPerPageChange={(e) => {
            setPorPagina(parseInt(e.target.value, 10));

            setPagina(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Proprietários por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{
            color: "var(--ig-t-ffffff)",

            "& .MuiTablePagination-selectIcon": {
              color: "var(--ig-t-ffffff)",
            },

            "& .MuiTablePagination-actions button": {
              color: "var(--ig-t-ffffff)",
              width: "auto",
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          sx={{
            color: "var(--ig-t-ffffff)",
            borderColor: "#64748b",
            textTransform: "none",
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// ── Subcomponente: formulário de pessoa única (Seguradora / Fiador) ────────────

const FormPessoaUnica = memo(function FormPessoaUnica({
  label,
  value,
  onChange,
  somenteLeitura,
  podeCadastrar,
  tipoFixo = false,
  ocultarTipo = false,
  tipoAuto = false,
  mostrarEmail = true,
  labelDocumentoAuto = "CPF ou CNPJ",
  labelEmail = "E-mail",
  onBlurObrigatorio,
  erro = false,
  erroDocumento = "",
  erroEmail = "",
  mostrarSnack,
}) {
  const [erroCpfCnpj, setErroCpfCnpj] = useState("");
  const [buscandoRazaoSocial, setBuscandoRazaoSocial] = useState(false);
  const [ultimoCnpjBusca, setUltimoCnpjBusca] = useState("");

  const inputSx = (readOnly, temErro = false) => ({
    "& .MuiInputLabel-root": { color: temErro ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
    "& .MuiOutlinedInput-root": {
      backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
      color: "var(--ig-t-ffffff)",
      "& fieldset": { borderColor: temErro ? "#f44336" : readOnly ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
      "&:hover fieldset": { borderColor: temErro ? "#f44336" : readOnly ? "var(--ig-b-333333)" : "var(--ig-b-888888)" },
    },
    "& input": { color: "var(--ig-t-ffffff)" },
  });

  const selectSx = (readOnly) => ({
    color: "var(--ig-t-ffffff)",
    backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: readOnly ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: readOnly ? "var(--ig-b-333333)" : "var(--ig-b-888888)" },
    "& .MuiSvgIcon-root": { color: readOnly ? "transparent" : "var(--ig-t-ffffff)" },
  });

  const ro = somenteLeitura || !podeCadastrar;

  return (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid size={{ xs: 12, sm: 3 }}>
        {tipoAuto ? (
          <TextField
            label={labelDocumentoAuto}
            size="small"
            fullWidth
            sx={inputSx(ro, !!(erroCpfCnpj || erroDocumento))}
            value={value.tipoPessoa === "PJ" ? value.cnpj : value.cpf}
            placeholder="CPF ou CNPJ"
            error={!!(erroCpfCnpj || erroDocumento)}
            helperText={erroCpfCnpj || erroDocumento}
            slotProps={{
              input: {
                readOnly: ro,
                endAdornment: buscandoRazaoSocial ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              },
            }}
            onChange={async (e) => {
              if (ro) return;
              setErroCpfCnpj("");
              const digitos = limparCpfCnpj(e.target.value);
              const pj = pareceCnpj(digitos);
              const masked = pj ? mascararCNPJ(digitos) : mascararCPF(digitos);
              onChange({ ...value, tipoPessoa: pj ? "PJ" : "PF", cnpj: pj ? masked : "", cpf: pj ? "" : masked });
              if (pj && digitos.length === 14 && digitos !== ultimoCnpjBusca) {
                setUltimoCnpjBusca(digitos);
                setBuscandoRazaoSocial(true);
                const resultado = await buscarRazaoSocialPorCnpj(masked);
                setBuscandoRazaoSocial(false);
                if (resultado.ok) {
                  onChange({
                    ...value,
                    tipoPessoa: "PJ",
                    cnpj: masked,
                    cpf: "",
                    razaoSocial: resultado.razaoSocial.toUpperCase(),
                    endereco: resultado.endereco ? resultado.endereco.toUpperCase() : value.endereco,
                    numero: resultado.numero ? resultado.numero.toUpperCase() : value.numero,
                    bairro: resultado.bairro ? resultado.bairro.toUpperCase() : value.bairro,
                    cidade: resultado.cidade ? resultado.cidade.toUpperCase() : value.cidade,
                    uf: resultado.uf ? resultado.uf.toUpperCase() : value.uf,
                    cep: resultado.cep || value.cep,
                    complemento: resultado.complemento ? resultado.complemento.toUpperCase() : value.complemento,
                  });
                } else if (resultado.motivo !== "invalido") {
                  mostrarSnack(resultado.mensagem, ["nao_encontrado", "vazio"].includes(resultado.motivo) ? "warning" : "error");
                }
              }
            }}
            onBlur={() => {
              if (value.tipoPessoa === "PJ" && value.cnpj && !validarCNPJ(value.cnpj)) setErroCpfCnpj("CNPJ inválido.");
              if (value.tipoPessoa === "PF" && value.cpf && !validarCPF(value.cpf)) setErroCpfCnpj("CPF inválido.");
            }}
          />
        ) : value.tipoPessoa === "PJ" ? (
          <TextField
            label="CNPJ"
            size="small"
            fullWidth
            sx={inputSx(ro, !!(erroCpfCnpj || erroDocumento))}
            value={value.cnpj}
            placeholder="00.000.000/0000-00"
            error={!!(erroCpfCnpj || erroDocumento)}
            helperText={erroCpfCnpj || erroDocumento}
            slotProps={{
              input: {
                readOnly: ro,
                endAdornment: buscandoRazaoSocial ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              },
            }}
            onChange={async (e) => {
              if (ro) return;
              setErroCpfCnpj("");
              const masked = mascararCNPJ(e.target.value);
              onChange({ ...value, cnpj: masked });
              const digitos = limparCnpj(masked);
              if (digitos.length === 14 && digitos !== ultimoCnpjBusca) {
                setUltimoCnpjBusca(digitos);
                setBuscandoRazaoSocial(true);
                const resultado = await buscarRazaoSocialPorCnpj(masked);
                setBuscandoRazaoSocial(false);
                if (resultado.ok) {
                  onChange({ ...value, cnpj: masked, razaoSocial: resultado.razaoSocial.toUpperCase() });
                } else if (resultado.motivo !== "invalido") {
                  mostrarSnack(resultado.mensagem, ["nao_encontrado", "vazio"].includes(resultado.motivo) ? "warning" : "error");
                }
              }
            }}
            onBlur={() => {
              if (value.cnpj && !validarCNPJ(value.cnpj)) setErroCpfCnpj("CNPJ inválido.");
            }}
          />
        ) : (
          <TextField
            label="CPF"
            size="small"
            fullWidth
            sx={inputSx(ro, !!(erroCpfCnpj || erroDocumento))}
            value={value.cpf}
            placeholder="000.000.000-00"
            error={!!(erroCpfCnpj || erroDocumento)}
            helperText={erroCpfCnpj || erroDocumento}
            slotProps={{ input: { readOnly: ro } }}
            onChange={(e) => {
              if (!ro) {
                setErroCpfCnpj("");
                onChange({ ...value, cpf: mascararCPF(e.target.value) });
              }
            }}
            onBlur={() => {
              if (value.cpf && !validarCPF(value.cpf)) setErroCpfCnpj("CPF inválido.");
            }}
          />
        )}
      </Grid>

      {!ocultarTipo && (
        <Grid size={{ xs: 12, sm: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={value.tipoPessoa}
              onChange={(e) => !ro && !tipoFixo && onChange({ ...value, tipoPessoa: e.target.value })}
              inputProps={{ readOnly: ro || tipoFixo }}
              sx={selectSx(ro || tipoFixo)}
            >
              {TIPOS_PESSOA.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid size={{ xs: 12, sm: ocultarTipo ? 9 : 7 }}>
        <TextField
          label="Nome / Razão Social*"
          size="small"
          fullWidth
          sx={{
            ...inputSx(ro, !!erro),
            "& input": {
              color: "var(--ig-t-ffffff)",
              textTransform: "uppercase",
            },
          }}
          value={value.razaoSocial}
          error={!!erro}
          helperText={typeof erro === "string" ? erro : ""}
          onChange={(e) =>
            !ro &&
            onChange({
              ...value,
              razaoSocial: e.target.value.toUpperCase(),
            })
          }
          onBlur={() => {
            if (!ro && !value.razaoSocial?.trim()) {
              onBlurObrigatorio?.();
            }
          }}
          slotProps={{
            input: {
              readOnly: ro,
            },
            htmlInput: {
              maxLength: 255,
            },
          }}
        />
      </Grid>

      {mostrarEmail && (
        <Grid size={12}>
          <TextField
            label={labelEmail}
            size="small"
            fullWidth
            sx={inputSx(ro, !!erroEmail)}
            error={!!erroEmail}
            helperText={erroEmail || EMAIL_HELPER_TEXT}
            value={value.email}
            onChange={(e) =>
              !ro &&
              onChange({
                ...value,
                email: e.target.value,
              })
            }
            slotProps={{
              input: {
                readOnly: ro,
              },
              htmlInput: {
                maxLength: 500,
              },
            }}
          />
        </Grid>
      )}
    </Grid>
  );
});

// ── Subcomponente: grade de propostas ─────────────────────────────────────────
const GradeDePropostas = memo(function GradeDePropostas({
  gridMinimizado,
  carregando,
  propostas,
  total,
  pagina,
  porPagina,
  editandoId,
  podeVisualizar,
  podeInutilizar,
  onVisualizar,
  onNavigateFicha,
  onImprimir,
  onAbrirInutilizar,
  onAbrirAtivar,
  setPagina,
  setPorPagina,
}) {
  return (
    <Collapse in={!gridMinimizado}>
      <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
        {carregando ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table size="small" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ações
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Cód
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    width: "100%",
                  }}
                >
                  Imóvel
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Complemento
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Valor Aluguel
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Locatária
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Data Início
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Data Fim
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Proposta
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Ficha
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Análise Crédito
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Minuta Contrato
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Vistoria Inicial
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Entrega Chave
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Aditivos
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Vistoria Intermed.
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Confissão Dívida
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Aviso Prévio
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Vistoria Final
                </TableCell>

                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Distrato
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {propostas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                    Nenhuma proposta encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                propostas.map((p) => (
                  <TableRow
                    key={p.id}
                    sx={{
                      "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                      backgroundColor: editandoId === p.id ? "rgba(25,152,168,0.08)" : "transparent",
                    }}
                  >
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                      {podeVisualizar && (
                        <Tooltip title="Visualizar / Editar">
                          <IconButton
                            size="small"
                            onClick={() => onVisualizar(p)}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#1565c0" },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeVisualizar && (
                        <Tooltip title="Ficha de Cadastro (Etapa 2)">
                          <IconButton
                            size="small"
                            onClick={() => onNavigateFicha(p.id)}
                            sx={{
                              backgroundColor: "#4a235a",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#6a1b9a" },
                            }}
                          >
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeVisualizar && (
                        <Tooltip title="Imprimir Proposta">
                          <IconButton
                            size="small"
                            onClick={() => onImprimir(p.id)}
                            sx={{
                              backgroundColor: "#e65100",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#bf360c" },
                            }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeInutilizar && p.statusGeral !== "Inutilizada" && (
                        <Tooltip title="Inutilizar">
                          <IconButton
                            size="small"
                            onClick={() => onAbrirInutilizar(p.id)}
                            sx={{
                              backgroundColor: "#d32f2f",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#c62828" },
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeInutilizar && p.statusGeral === "Inutilizada" && (
                        <Tooltip title="Ativar">
                          <IconButton
                            size="small"
                            onClick={() => onAbrirAtivar(p.id, p.motivoInutilizacao)}
                            sx={{
                              backgroundColor: "#2e7d32",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#1b5e20" },
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                      {p.codigo ?? `PC${p.id}`}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>
                      <Typography sx={{ color: "var(--ig-t-ffffff)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        {[p.imovel?.codigoMatricula, p.imovel?.apelido].filter(Boolean).join(" – ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                      {p.complementoLocacao || "—"}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                      {p.vAluguelMensal ? `R$ ${formatBRL(p.vAluguelMensal)}` : "—"}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                      {(() => {
                        const nomeLocataria = (p.fichaPessoas || [])
                          .filter((f) => f.campoProposta === "Locataria")
                          .map((f) => f.razaoSocial)
                          .filter(Boolean)
                          .join(", ");
                        return nomeLocataria ? (
                          <Tooltip title={nomeLocataria}>
                            <span>{truncarTexto(nomeLocataria, 20)}</span>
                          </Tooltip>
                        ) : (
                          "—"
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                      {formatarData(p.dataInicio)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                      {formatarData(p.dataFim)}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipStatusProposta(p.statusGeral, p.statusAssinatura)}
                    </TableCell>

                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipStatusFichas(p.statusFichas)}
                    </TableCell>

                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipStatusAnaliseCredito(p.statusAnaliseCredito)}
                    </TableCell>

                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipStatusMinutaContrato(p.statusMinutaContrato, p.statusAssinaturaContrato)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusVistoriaInicial)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusEntregaChave)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusAditivos)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusVistoriaIntermediaria)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusConfissaoDivida)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusAvisoPrevio)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusVistoriaFinal)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusGenerico(p.statusDistrato)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        {!carregando && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, p) => setPagina(p)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(e) => {
              setPorPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Propostas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            sx={{
              color: "var(--ig-t-ffffff)",
              borderTop: "1px solid var(--ig-b-444444)",
              "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
              "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
              "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
            }}
          />
        )}
      </TableContainer>
    </Collapse>
  );
});

// ── Subcomponente: checklist de documentos de comprovação da Garantia Imobiliária ──

const ChecklistGarantiaImobiliaria = memo(function ChecklistGarantiaImobiliaria({
  ro,
  editandoId,
  arquivoGarantia,
  enviando,
  garantiaDocs,
  garantiaDocsPendentes,
  onSelecionarArquivo,
  onEnviarOuAdicionar,
  onVisualizar,
  onDownload,
  onDelete,
  onVisualizarPendente,
  onRemoverPendente,
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.82rem", mb: 1 }}>Documentos de comprovação (matrícula, IPTU, etc.):</Typography>

      {!ro && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<FaPaperclip />}
            size="small"
            sx={{ width: "auto", backgroundColor: "#007bff", fontWeight: 700, "&:hover": { backgroundColor: "#0069d9" }, textTransform: "none" }}
          >
            GED
            <input type="file" hidden onChange={onSelecionarArquivo} />
          </Button>
          {arquivoGarantia && <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.8rem" }}>{arquivoGarantia.name}</Typography>}
          {arquivoGarantia && (
            <Button
              variant="contained"
              size="small"
              onClick={onEnviarOuAdicionar}
              disabled={enviando}
              sx={{ width: "auto", backgroundColor: "#1998a8", textTransform: "none", fontWeight: "bold" }}
            >
              {enviando ? "Enviando..." : editandoId ? "Enviar" : "Adicionar"}
            </Button>
          )}
        </Box>
      )}

      {(garantiaDocs.length > 0 || garantiaDocsPendentes.length > 0) && (
        <Box>
          {garantiaDocs.map((doc) => (
            <Box
              key={doc.id}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid var(--ig-b-2a2a2a)" }}
            >
              <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.82rem" }}>
                {doc.nomeOriginal}
                <Typography component="span" sx={{ color: "var(--ig-t-666666)", fontSize: "0.75rem", ml: 1 }}>
                  {doc.dataUpload ? formatarData(doc.dataUpload) : ""}
                </Typography>
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Visualizar">
                  <IconButton size="small" onClick={() => onVisualizar(doc.id)} sx={{ color: "var(--ig-t-64b5f6)" }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Baixar">
                  <IconButton size="small" onClick={() => onDownload(doc.id, doc.nomeOriginal)} sx={{ color: "#1998a8" }}>
                    <UploadFileIcon fontSize="small" sx={{ transform: "rotate(180deg)" }} />
                  </IconButton>
                </Tooltip>
                {!ro && (
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => onDelete(doc.id)} sx={{ color: "#e57373" }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
          {garantiaDocsPendentes.map((pend) => (
            <Box
              key={pend.tempId}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid var(--ig-b-2a2a2a)" }}
            >
              <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.82rem", fontStyle: "italic" }}>
                {pend.file.name}
                <Typography component="span" sx={{ color: "var(--ig-t-f4a259)", fontSize: "0.75rem", ml: 1 }}>
                  (pendente de envio)
                </Typography>
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Visualizar">
                  <IconButton size="small" onClick={() => onVisualizarPendente(pend.file)} sx={{ color: "var(--ig-t-64b5f6)" }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!ro && (
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => onRemoverPendente(pend.tempId)} sx={{ color: "#e57373" }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});

// ── Componente principal ──────────────────────────────────────────────────────

export default function PropostaComercial() {
  const navigate = useNavigate();

  // ── Listagem
  const [propostas, setPropostas] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);

  // ── Filtro + paginação
  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  // ── Filtro vindo do Dashboard de Contratos (via query params)
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtroDashboard, setFiltroDashboard] = useState(() => {
    const statusAprovacao = searchParams.get("statusAprovacao");
    const minhasPropostas = searchParams.get("minhasPropostas") === "true";
    const escopoGeral = searchParams.get("escopo") === "geral";
    if (statusAprovacao) return { tipo: "statusAprovacao", valor: statusAprovacao, geral: escopoGeral };
    if (minhasPropostas) return { tipo: "minhasPropostas", valor: true };
    return null;
  });

  // ── Imóvel selecionado na proposta
  const [imovelSelecionado, setImovelSelecionado] = useState(null);
  const [modalImovelAberto, setModalImovelAberto] = useState(false);

  // ── Área construída do IPTU Anual da mesma matrícula
  const [areaConstruidaIptu, setAreaConstruidaIptu] = useState(null);

  // ── Buscar Área Construída do IPTU Anual mais recente da mesma matrícula
  useEffect(() => {
    let cancelado = false;

    const codigoMatricula = String(imovelSelecionado?.codigoMatricula || "")
      .trim()
      .toUpperCase();

    // Sempre limpa o valor anterior ao trocar/remover o imóvel
    setAreaConstruidaIptu(null);

    if (!codigoMatricula) {
      return () => {
        cancelado = true;
      };
    }

    const carregarAreaConstruidaIptu = async () => {
      try {
        const tk = localStorage.getItem("token");

        const headers = {
          Authorization: `Bearer ${tk}`,
        };

        // 1. Descobre todos os anos existentes no IPTU Anual
        const resAnos = await fetch("/api/iptu-anual/listar-anos", {
          headers,
        });

        if (!resAnos.ok) {
          throw new Error("Erro ao consultar os anos do IPTU Anual.");
        }

        const dadosAnos = await resAnos.json();

        const anos = (Array.isArray(dadosAnos) ? dadosAnos : [])
          .map(Number)
          .filter(Number.isInteger)
          .sort((a, b) => b - a);

        // 2. Procura do ano mais recente para o mais antigo
        for (const ano of anos) {
          const resAno = await fetch(`/api/iptu-anual/${ano}`, {
            headers,
          });

          if (!resAno.ok) {
            continue;
          }

          const dadosAno = await resAno.json();

          const registros = Array.isArray(dadosAno) ? dadosAno : [];

          const registro = registros.find(
            (item) =>
              String(item?.matriculaCodigo || "")
                .trim()
                .toUpperCase() === codigoMatricula,
          );

          if (registro) {
            console.log("[PC][IPTU] Área Construída encontrada:", {
              codigoMatricula,
              ano,
              areaConstruidaRaw: registro.areaConstruida,
              registro,
            });

            setAreaConstruidaIptu(registro.areaConstruida ?? null);

            return;
          }
        }

        // Nenhum IPTU Anual encontrado para essa matrícula
        if (!cancelado) {
          setAreaConstruidaIptu(null);
        }
      } catch (erro) {
        console.error("Erro ao buscar Área Construída do IPTU Anual:", erro);

        if (!cancelado) {
          setAreaConstruidaIptu(null);
        }
      }
    };

    carregarAreaConstruidaIptu();

    return () => {
      cancelado = true;
    };
  }, [imovelSelecionado?.codigoMatricula]);

  // ── Formulário
  const [editandoId, setEditandoId] = useState(null);
  const [codigoProposta, setCodigoProposta] = useState("");
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });
  const [errosForm, setErrosForm] = useState({});

  const entregaChaveNaAssinatura = form.tipoPrimeiroVencimento === "IMEDIATO" ? "SIM" : form.tipoPrimeiroVencimento === "NÃO IMEDIATO" ? "NÃO" : "";

  // ── Caução: Valor Caução = Quantidade de Aluguéis × Valor do Aluguel Mensal
  useEffect(() => {
    if (!garantiasDe(form.garantia).includes("Caução")) return;
    const qtde = parseInt(form.qtdeAluguelCaucao, 10);
    const aluguel = parseFloat(parseBRL(form.vAluguelMensal));
    const novoValor = qtde > 0 && !isNaN(aluguel) ? (qtde * aluguel).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
    setForm((p) => (p.vCaucao === novoValor ? p : { ...p, vCaucao: novoValor }));
  }, [form.garantia, form.qtdeAluguelCaucao, form.vAluguelMensal]);

  // ── Sublistas
  // ── Sublistas
  const [locadoras, setLocadoras] = useState([]);

  const [proprietariosLocador, setProprietariosLocador] = useState([]);

  const [locadorSelecionado, setLocadorSelecionado] = useState(null);

  const [emailNovoLocador, setEmailNovoLocador] = useState("");

  const [modalProprietarioAberto, setModalProprietarioAberto] = useState(false);

  const [adicionandoLocador, setAdicionandoLocador] = useState(false);

  const [locatarias, setLocatarias] = useState([]);

  const [seguradora, setSeguradora] = useState({
    ...fichaVazia,
    tipoPessoa: "PJ",
  });

  const [corretorSeguro, setCorretorSeguro] = useState({
    ...fichaVazia,
    tipoPessoa: "PF",
  });

  const [fiador, setFiador] = useState({
    ...fichaVazia,
    tipoPessoa: "PF",
  });

  const [caucionante, setCaucionante] = useState({
    ...fichaVazia,
    tipoPessoa: "PJ",
  });

  // ── Documentos de comprovação da Garantia Imobiliária
  const [garantiaDocs, setGarantiaDocs] = useState([]);
  const [garantiaDocsPendentes, setGarantiaDocsPendentes] = useState([]); // [{ tempId, file }] — anexados antes de a proposta existir
  const [arquivoGarantia, setArquivoGarantia] = useState(null);
  const [enviandoGarantiaDoc, setEnviandoGarantiaDoc] = useState(false);

  // ── UI
  const [abaAtiva, setAbaAtiva] = useState("proposta");
  const [emAprovacao, setEmAprovacao] = useState(false);
  const [editandoPendenciaGarantia, setEditandoPendenciaGarantia] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [aprovacaoInfo, setAprovacaoInfo] = useState({
    aprovacao: null,
    autorizacoes: [],
    historicos: [],
    statusGeral: "EmDigitacao",
    statusFichas: null,
    usuarioEhAprovador: false,
    motivoInutilizacao: null,
    dataInutilizacao: null,
    usuarioInutilizacao: null,
    dataReativacao: null,
    motivoReativacao: null,
    usuarioReativacao: null,
  });
  const [usuariosAprovadores, setUsuariosAprovadores] = useState([]);
  const [indicesReajuste, setIndicesReajuste] = useState([]);
  const [dialogResponder, setDialogResponder] = useState({ aberto: false, resposta: "", pergunta: "", arquivo: null });
  const [dialogReprovar, setDialogReprovar] = useState({ aberto: false, motivo: "" });
  const [dialogQuestionar, setDialogQuestionar] = useState({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
  const [confirmInutilizar, setConfirmInutilizar] = useState({ aberto: false, id: null });
  const [confirmAtivar, setConfirmAtivar] = useState({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" });
  const [propostaParaImprimir, setPropostaParaImprimir] = useState(null);
  const [quadroResumoParaImprimir, setQuadroResumoParaImprimir] = useState(null);
  const imovelInputRef = useRef(null);
  const codigoInputRef = useRef(null);
  const [confirmRevisar, setConfirmRevisar] = useState({ aberto: false, motivo: "" });
  const [confirmReabrir, setConfirmReabrir] = useState({ aberto: false, motivo: "" });
  const [processandoAprovacao, setProcessandoAprovacao] = useState(false);
  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = useCallback((msg, severity = "success") => setSnack({ aberto: true, msg, severity }), []);

  // O PDF vem pronto do backend (docx montado no servidor + LibreOffice), mesmo
  // caminho do Contrato — o documento não é mais montado no navegador. Assim a
  // impressão e o documento enviado ao DocuSign saem do mesmo lugar e não divergem.
  const handleImprimirProposta = useCallback(
    async (id) => {
      // O window.open tem que vir ANTES do await: o navegador só libera pop-up
      // dentro do gesto do clique, e depois de um await o bloqueador barra.
      const janelaImpressao = window.open("", "_blank", "width=1000,height=800");

      if (!janelaImpressao) {
        mostrarSnack("Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.", "error");
        return;
      }

      // Aviso enquanto o backend monta o docx e converte — a janela já está
      // aberta e ficaria em branco durante a conversão.
      janelaImpressao.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Gerando PDF…</title></head>` +
          `<body style="margin:0;padding:24px;font:14px Arial,sans-serif">Gerando o PDF da proposta…</body></html>`,
      );

      try {
        const tk = localStorage.getItem("token");
        const resPdf = await fetch(`/api/propostas/${id}/pdf`, { headers: { Authorization: `Bearer ${tk}` } });

        if (!resPdf.ok) {
          if (!janelaImpressao.closed) {
            janelaImpressao.close();
          }

          const dados = await resPdf.json().catch(() => ({}));
          mostrarSnack(dados.erro || "Erro ao gerar o PDF da proposta.", "error");
          return;
        }

        const blob = await resPdf.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const nomeArquivo = nomeArquivoDaResposta(resPdf, `proposta-${id}.pdf`);

        // A pessoa pode ter fechado a janela durante a conversão.
        if (janelaImpressao.closed) {
          window.URL.revokeObjectURL(blobUrl);
          return;
        }

        // O PDF entra num iframe em vez de ir direto na location: assim o <title>
        // da janela é o nome do arquivo, que é o que o navegador sugere ao salvar.
        const doc = janelaImpressao.document;

        doc.open();

        doc.write(
          `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <title>${nomeArquivo}</title>

      <style>
        html, body {
          margin: 0;
          height: 100%;
        }

        iframe {
          border: 0;
          width: 100%;
          height: 100%;
        }
      </style>
    </head>

    <body>
      <iframe src="${blobUrl}" title="${nomeArquivo}"></iframe>
    </body>
  </html>`,
        );

        doc.close();

        janelaImpressao.onafterprint = () => {
          janelaImpressao.close();
        };

        janelaImpressao.focus();

        // Revogar antes da janela fechar deixaria o visualizador sem o arquivo.
        janelaImpressao.addEventListener("unload", () => window.URL.revokeObjectURL(blobUrl), { once: true });
      } catch {
        if (!janelaImpressao.closed) {
          janelaImpressao.close();
        }

        mostrarSnack("Erro de conexão.", "error");
      }
    },
    [mostrarSnack],
  );

  const handleTemplatePC = useCallback(() => {
    const janelaImpressao = window.open("", "_blank", "width=1000,height=800");

    if (!janelaImpressao) {
      mostrarSnack("Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.", "error");
      return;
    }

    try {
      const propostaVazia = {
        __templatePC: true,

        codigo: "",
        tipoLocacao: "",
        tipoImovelLocacao: "",
        enderecoImovel: "",
        areaTotalLocada: "",
        areaConstruidaTotalLocada: "",
        areaLocada: "",
        prazo: "",
        dataInicio: "",
        dataFim: "",
        vAluguelMensal: "",
        vCondominio: "",
        vIPTU: "",
        vAguaEsgoto: "",
        vEnergia: "",
        vDesconto: "",
        descrDesconto: "",
        carencia: "",
        vInternet: "",
        vTotem: "",
        mesReajuste: "",
        indiceReajuste: null,
        tipoVencimento: "",
        diaVencimento: "",
        garantia: "",
        vCaucao: "",
        qtdeAluguelCaucao: "",
        boleto: "",
        boletoQuantidade: "",
        vBoleto: "",
        consideracoes: "",

        fichaPessoas: [],
        locadoras: [],
        locatarias: [],
        seguradora: null,
        fiador: null,

        fiadorTipoGarantia: "",
        fiadorImovelTipo: "",
        fiadorImovelCidadeCartorioRgi: "",
        fiadorImovelNomeCartorioRgi: "",
        fiadorImovelRgi: "",
        fiadorImovelMatricula: "",
        fiadorImovelCartorio: "",
        fiadorImovelLivro: "",
        fiadorImovelFolha: "",
        fiadorImovelEndereco: "",
        fiadorImovelNumero: "",
        fiadorImovelBairro: "",
        fiadorImovelCidade: "",
        fiadorImovelUf: "",
        fiadorImovelCep: "",
        fiadorImovelComplemento: "",
        fiadorImovelUrlGoogleMaps: "",

        garantiaDescricaoImovel: "",
        garantiaCadastroMunicipal: "",
        garantiaSetor: "",
        garantiaQuarteirao: "",

        garantiaTipoImovel: "",
        garantiaRgi: "",
        garantiaMatricula: "",
        garantiaCartorio: "",
        garantiaLivro: "",
        garantiaFolha: "",
        garantiaEndereco: "",
        garantiaNumero: "",
        garantiaBairro: "",
        garantiaCidade: "",
        garantiaUf: "",
        garantiaCep: "",
        garantiaComplemento: "",
        garantiaUrlGoogleMaps: "",
      };

      const quadroVazio = [];

      setPropostaParaImprimir(propostaVazia);
      setQuadroResumoParaImprimir(quadroVazio);

      setTimeout(() => {
        const el = document.getElementById("print-proposta");

        if (!el || janelaImpressao.closed) {
          janelaImpressao.close();
          mostrarSnack("Não foi possível gerar o template da proposta.", "error");
          return;
        }

        const nomeArquivo = "Template PC";
        const doc = janelaImpressao.document;

        doc.open();

        doc.write(
          `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>${nomeArquivo}</title>

            <style>
              @page {
                size: A4 portrait;
                margin: 0;
              }

              body {
                margin: 0;
                padding: 0;
                background: #fff;
              }

              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            </style>
          </head>

          <body>
            ${el.outerHTML}
          </body>
        </html>`,
        );

        doc.close();

        janelaImpressao.onafterprint = () => {
          janelaImpressao.close();
        };

        setTimeout(() => {
          janelaImpressao.focus();
          janelaImpressao.print();
        }, 300);
      }, 400);
    } catch (erro) {
      if (!janelaImpressao.closed) {
        janelaImpressao.close();
      }

      console.error("Erro ao gerar TEMPLATE PC:", erro);

      mostrarSnack("Erro ao gerar template da proposta.", "error");
    }
  }, [mostrarSnack]);

  const handleNavigateFicha = useCallback((id) => navigate(`/ficha-cadastro?proposta=${id}`), [navigate]);

  // ── Permissões + userId do token
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  let userId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      userId = decoded.id ?? null;
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeInutilizar = ["gestor", "administrador"].includes(tipoUsuario);

  // ── Carregar propostas
  const carregarPropostas = useCallback(() => {
    const tk = localStorage.getItem("token");
    const params = new URLSearchParams({ filtro, pagina, porPagina });
    if (filtroDashboard?.tipo === "statusAprovacao") {
      params.set("statusAprovacao", filtroDashboard.valor);
      if (filtroDashboard.geral) params.set("escopo", "geral");
    }
    if (filtroDashboard?.tipo === "minhasPropostas") params.set("minhasPropostas", "true");
    setCarregando(true);
    fetch(`/api/propostas?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setPropostas(Array.isArray(data) ? data : []);
        setTotal(t ?? 0);
      })
      .catch((err) => console.error("Erro ao buscar propostas:", err))
      .finally(() => setCarregando(false));
  }, [filtro, pagina, porPagina, filtroDashboard]);

  useEffect(() => {
    if (podeVisualizar) carregarPropostas();
  }, [podeVisualizar, carregarPropostas]);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  useEffect(() => {
    if (!podeVisualizar) return;
    const tk = localStorage.getItem("token");
    fetch("/api/propostas/usuarios-aprovadores", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setUsuariosAprovadores(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [podeVisualizar]);

  useEffect(() => {
    if (!podeVisualizar) return;
    const tk = localStorage.getItem("token");

    fetch("/api/indices-reajuste", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setIndicesReajuste(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [podeVisualizar]);

  // ── Carregar proprietários disponíveis para inclusão como Locadora
  useEffect(() => {
    if (!podeVisualizar) return;

    const tk = localStorage.getItem("token");

    fetch("/api/proprietarios", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.proprietarios) ? data.proprietarios : [];

        lista.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

        setProprietariosLocador(lista);
      })
      .catch((err) => {
        console.error("Erro ao carregar proprietários para Locadoras:", err);

        setProprietariosLocador([]);
      });
  }, [podeVisualizar]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  // =======================
  // COPIAR
  // =======================
  const handleCopiar = async () => {
    const texto = propostas
      .map((p) => {
        const codigo = p.codigo ?? `PC${p.id}`;
        const tipoLocacao = p.tipoLocacao || "";
        const imovel = p.imovel?.codigoMatricula || p.imovel?.apelido || "";
        const endereco = p.enderecoImovel || "";
        const dataInicio = formatarData(p.dataInicio);
        const dataFim = formatarData(p.dataFim);

        const statusProposta = STATUS_GERAL_CONFIG[p.statusGeral ?? "EmDigitacao"]?.label ?? p.statusGeral ?? "";

        const statusFichas = STATUS_FICHAS_CONFIG[p.statusFichas]?.label ?? p.statusFichas ?? "";

        const statusAnaliseCredito = STATUS_ANALISE_CREDITO_CONFIG[p.statusAnaliseCredito]?.label ?? p.statusAnaliseCredito ?? "";

        return [codigo, tipoLocacao, imovel, endereco, dataInicio, dataFim, statusProposta, statusFichas, statusAnaliseCredito].join(" - ");
      })
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = async () => {
    const BOM = "\uFEFF";
    const tk = localStorage.getItem("token");

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const pessoasPorPapel = (proposta, papel) => (proposta.fichaPessoas || []).filter((ficha) => ficha.campoProposta === papel);

    const juntarPessoas = (proposta, papel, campo) =>
      pessoasPorPapel(proposta, papel)
        .map((ficha) => ficha?.[campo])
        .filter(Boolean)
        .join(" | ");

    const juntarDocumentosPessoas = (proposta, papel) =>
      pessoasPorPapel(proposta, papel)
        .map((ficha) => ficha.cnpj || ficha.cpf || descreverDocumentoEstrangeiro(ficha) || "")
        .filter(Boolean)
        .join(" | ");

    const primeiraPessoa = (proposta, papel) => pessoasPorPapel(proposta, papel)[0] || {};

    const nomesDocumentos = (documentos) =>
      (documentos || [])
        .map((doc) => doc.nomeOriginal)
        .filter(Boolean)
        .join(" | ");

    try {
      // ============================================================
      // BUSCAR TODOS OS RESULTADOS DO FILTRO
      // A API permite até 100 por página, então percorremos todas.
      // ============================================================
      const todos = [];

      let paginaCSV = 0;
      let totalCSV = 0;

      const porPaginaCSV = 100;

      do {
        const params = new URLSearchParams({
          filtro: inputFiltro,
          pagina: String(paginaCSV),
          porPagina: String(porPaginaCSV),
        });

        if (filtroDashboard?.tipo === "statusAprovacao") {
          params.set("statusAprovacao", filtroDashboard.valor);

          if (filtroDashboard.geral) {
            params.set("escopo", "geral");
          }
        }

        if (filtroDashboard?.tipo === "minhasPropostas") {
          params.set("minhasPropostas", "true");
        }

        const res = await fetch(`/api/propostas?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${tk}`,
          },
        });

        const resultado = await res.json();

        if (!res.ok) {
          throw new Error(resultado.erro || "Erro ao buscar propostas para o CSV.");
        }

        const lote = Array.isArray(resultado.data) ? resultado.data : [];

        todos.push(...lote);

        totalCSV = Number(resultado.total ?? todos.length);

        paginaCSV += 1;

        if (lote.length === 0) {
          break;
        }
      } while (todos.length < totalCSV);

      if (!todos.length) {
        mostrarSnack("Nenhuma proposta encontrada para exportar.", "warning");

        return;
      }

      // ============================================================
      // CABEÇALHO
      // ============================================================
      const header = [
        "ID Proposta",
        "Código",

        "ID Imóvel",
        "Código Matrícula",
        "Apelido do Imóvel",
        "Endereço do Imóvel",

        "Complemento da Locação",
        "Área Construída m² Matrícula",
        "Área Construída m² IPTU",
        "Área Locada?",
        "Área do Imóvel",

        "Tipo de Locação",
        "Tipo do Imóvel",

        "Prazo (meses)",
        "Data Início",
        "Data Fim",

        "Aluguel Mensal",
        "Condomínio",
        "IPTU",
        "Água e Esgoto",
        "Energia",
        "Internet",
        "Totem",
        "Desconto",
        "Descrição do Desconto",
        "Carência",

        "Mês Reajuste",
        "Índice de Reajuste",
        "Tipo Vencimento",
        "Dia Vencimento",

        "Boleto",
        "Quantidade de Boletos",
        "Valor do Boleto",

        "Garantia",
        "Valor Caução",
        "Quantidade Aluguéis Caução",

        "Locadora - Tipo",
        "Locadora - Nome / Razão Social",
        "Locadora - CPF/CNPJ",
        "Locadora - E-mail",
        "Locadora - Endereço",
        "Locadora - Número",
        "Locadora - Complemento",
        "Locadora - Bairro",
        "Locadora - Cidade",
        "Locadora - UF",
        "Locadora - CEP",

        "Locatária - Tipo",
        "Locatária - Nome / Razão Social",
        "Locatária - CPF/CNPJ",
        "Locatária - E-mail",
        "Locatária - Endereço",
        "Locatária - Número",
        "Locatária - Complemento",
        "Locatária - Bairro",
        "Locatária - Cidade",
        "Locatária - UF",
        "Locatária - CEP",

        "Seguradora - Tipo",
        "Seguradora - Nome / Razão Social",
        "Seguradora - CPF/CNPJ",
        "Seguradora - E-mail",

        "Corretor de Seguros - Tipo",
        "Corretor de Seguros - Nome / Razão Social",
        "Corretor de Seguros - CPF/CNPJ",
        "Corretor de Seguros - E-mail",

        "Fiador - Tipo",
        "Fiador - Nome / Razão Social",
        "Fiador - CPF/CNPJ",
        "Fiador - E-mail",

        "Garantia do Fiador",
        "Fiador - Tipo do Imóvel",
        "Fiador - Cidade Cartório RGI",
        "Fiador - Nome Cartório RGI",
        "Fiador - Matrícula RGI",
        "Fiador - Matrícula IPTU",
        "Fiador - Cartório",
        "Fiador - Livro",
        "Fiador - Folha",
        "Fiador - Endereço",
        "Fiador - Número",
        "Fiador - Complemento",
        "Fiador - Bairro",
        "Fiador - Cidade",
        "Fiador - UF",
        "Fiador - CEP",
        "Fiador - Google Maps",

        "Caução Imóvel - Tipo do Imóvel",
        "Caução Imóvel - RGI",
        "Caução Imóvel - Matrícula",
        "Caução Imóvel - Cartório",
        "Caução Imóvel - Livro",
        "Caução Imóvel - Folha",
        "Caução Imóvel - Endereço",
        "Caução Imóvel - Número",
        "Caução Imóvel - Complemento",
        "Caução Imóvel - Bairro",
        "Caução Imóvel - Cidade",
        "Caução Imóvel - UF",
        "Caução Imóvel - CEP",
        "Caução Imóvel - Google Maps",

        "Status Proposta",
        "Status Fichas",
        "Status Análise de Crédito",

        "Considerações",

        "Documentos Análise de Crédito",
        "Documentos Garantia",

        "Data Criação",

        "Motivo Inutilização",
        "Data Inutilização",
        "Usuário Inutilização",

        "Motivo Reativação",
        "Data Reativação",
        "Usuário Reativação",
      ];

      // ============================================================
      // LINHAS
      // ============================================================
      const linhas = todos.map((p) => {
        const seguradora = primeiraPessoa(p, "Seguradora");

        const corretor = primeiraPessoa(p, "CorretorSeguro");

        const fiador = primeiraPessoa(p, "Fiador");

        const statusProposta = STATUS_GERAL_CONFIG[p.statusGeral ?? "EmDigitacao"]?.label ?? p.statusGeral ?? "";

        const statusFichas = STATUS_FICHAS_CONFIG[p.statusFichas]?.label ?? p.statusFichas ?? "";

        const statusAnaliseCredito = STATUS_ANALISE_CREDITO_CONFIG[p.statusAnaliseCredito]?.label ?? p.statusAnaliseCredito ?? "";

        return [
          p.id,
          p.codigo ?? `PC${p.id}`,

          p.imovelId ?? "",
          p.imovel?.codigoMatricula || "",
          p.imovel?.apelido || "",
          p.enderecoImovel || "",

          p.complementoLocacao || "",
          formatMetragemBancoBR(p.areaTotalLocada),
          formatMetragemBancoBR(p.areaConstruidaTotalLocada),
          p.areaLocada || "",
          formatMetragemBancoBR(p.areaImovel),

          p.tipoLocacao || "",
          p.tipoImovelLocacao || "",

          p.prazo ?? "",
          formatarData(p.dataInicio),
          formatarData(p.dataFim),

          p.vAluguelMensal ? `R$ ${formatBRL(p.vAluguelMensal)}` : "",
          p.vCondominio ? `R$ ${formatBRL(p.vCondominio)}` : "",
          p.vIPTU ? `R$ ${formatBRL(p.vIPTU)}` : "",
          p.vAguaEsgoto ? `R$ ${formatBRL(p.vAguaEsgoto)}` : "",
          p.vEnergia ? `R$ ${formatBRL(p.vEnergia)}` : "",
          p.vInternet ? `R$ ${formatBRL(p.vInternet)}` : "",
          p.vTotem ? `R$ ${formatBRL(p.vTotem)}` : "",
          p.vDesconto ? `R$ ${formatBRL(p.vDesconto)}` : "",
          p.descrDesconto || "",
          p.carencia || "",

          p.mesReajuste || "",
          p.indiceReajuste?.descricao || "",
          p.tipoVencimento || "",
          p.diaVencimento ?? "",

          p.boleto || "",
          p.boleto === "SIM" ? (p.boletoQuantidade ?? "") : "",
          p.boleto === "SIM" && p.vBoleto ? `R$ ${formatBRL(p.vBoleto)}` : "",

          p.garantia || "",
          p.vCaucao ? `R$ ${formatBRL(p.vCaucao)}` : "",
          p.qtdeAluguelCaucao ?? "",

          juntarPessoas(p, "Locadora", "tipoPessoa"),
          juntarPessoas(p, "Locadora", "razaoSocial"),
          juntarDocumentosPessoas(p, "Locadora"),
          juntarPessoas(p, "Locadora", "email"),
          juntarPessoas(p, "Locadora", "endereco"),
          juntarPessoas(p, "Locadora", "numero"),
          juntarPessoas(p, "Locadora", "complemento"),
          juntarPessoas(p, "Locadora", "bairro"),
          juntarPessoas(p, "Locadora", "cidade"),
          juntarPessoas(p, "Locadora", "uf"),
          juntarPessoas(p, "Locadora", "cep"),

          juntarPessoas(p, "Locataria", "tipoPessoa"),
          juntarPessoas(p, "Locataria", "razaoSocial"),
          juntarDocumentosPessoas(p, "Locataria"),
          juntarPessoas(p, "Locataria", "email"),
          juntarPessoas(p, "Locataria", "endereco"),
          juntarPessoas(p, "Locataria", "numero"),
          juntarPessoas(p, "Locataria", "complemento"),
          juntarPessoas(p, "Locataria", "bairro"),
          juntarPessoas(p, "Locataria", "cidade"),
          juntarPessoas(p, "Locataria", "uf"),
          juntarPessoas(p, "Locataria", "cep"),

          seguradora.tipoPessoa || "",
          seguradora.razaoSocial || "",
          seguradora.cnpj || seguradora.cpf || descreverDocumentoEstrangeiro(seguradora) || "",
          seguradora.email || "",

          corretor.tipoPessoa || "",
          corretor.razaoSocial || "",
          corretor.cnpj || corretor.cpf || descreverDocumentoEstrangeiro(corretor) || "",
          corretor.email || "",

          fiador.tipoPessoa || "",
          fiador.razaoSocial || "",
          fiador.cnpj || fiador.cpf || descreverDocumentoEstrangeiro(fiador) || "",
          fiador.email || "",

          p.fiadorTipoGarantia || "",
          p.fiadorImovelTipo || "",
          p.fiadorImovelCidadeCartorioRgi || "",
          p.fiadorImovelNomeCartorioRgi || "",
          p.fiadorImovelRgi || "",
          p.fiadorImovelMatricula || "",
          p.fiadorImovelCartorio || "",
          p.fiadorImovelLivro || "",
          p.fiadorImovelFolha || "",
          p.fiadorImovelEndereco || "",
          p.fiadorImovelNumero || "",
          p.fiadorImovelComplemento || "",
          p.fiadorImovelBairro || "",
          p.fiadorImovelCidade || "",
          p.fiadorImovelUf || "",
          p.fiadorImovelCep || "",
          p.fiadorImovelUrlGoogleMaps || "",

          p.garantiaTipoImovel || "",
          p.garantiaRgi || "",
          p.garantiaMatricula || "",
          p.garantiaCartorio || "",
          p.garantiaLivro || "",
          p.garantiaFolha || "",
          p.garantiaEndereco || "",
          p.garantiaNumero || "",
          p.garantiaComplemento || "",
          p.garantiaBairro || "",
          p.garantiaCidade || "",
          p.garantiaUf || "",
          p.garantiaCep || "",
          p.garantiaUrlGoogleMaps || "",

          statusProposta,
          statusFichas,
          statusAnaliseCredito,

          p.consideracoes || "",

          nomesDocumentos(p.analiseCreditoDocs),

          nomesDocumentos(p.garantiaImobiliariaDocs),

          p.dataCriacao ? formatarDataHora(p.dataCriacao) : "",

          p.motivoInutilizacao || "",
          p.dataInutilizacao ? formatarDataHora(p.dataInutilizacao) : "",
          p.usuarioInutilizacao?.nome || "",

          p.motivoReativacao || "",
          p.dataReativacao ? formatarDataHora(p.dataReativacao) : "",
          p.usuarioReativacao?.nome || "",
        ].map(csvEscape);
      });

      // ============================================================
      // GERAR ARQUIVO
      // ============================================================
      const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

      const blob = new Blob([BOM + csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", "propostas-comerciais.csv");

      document.body.appendChild(link);

      link.click();

      link.remove();

      URL.revokeObjectURL(url);

      mostrarSnack(`${todos.length} proposta(s) exportada(s) para CSV.`, "success");
    } catch (erro) {
      console.error("Erro ao gerar CSV de propostas:", erro);

      mostrarSnack(erro.message || "Erro ao gerar CSV.", "error");
    }
  };

  const handleLimparFiltroDashboard = () => {
    setFiltroDashboard(null);
    setPagina(0);
    setSearchParams({});
  };

  // ── Recarregar dados de aprovação
  const carregarAprovacao = useCallback(() => {
    if (!editandoId) return;
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${editandoId}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then((data) => {
        setAprovacaoInfo({
          aprovacao: (data.aprovacoes ?? []).find((a) => a.processoTipo === "Proposta") ?? null,
          autorizacoes: data.autorizacoes ?? [],
          historicos: data.historicos ?? [],
          statusGeral: data.statusGeral ?? "EmDigitacao",
          statusFichas: data.statusFichas ?? null,
          statusAnaliseCredito: data.statusAnaliseCredito ?? null,
          idUsuario: data.idUsuario ?? null,
          motivoInutilizacao: data.motivoInutilizacao ?? null,
          dataInutilizacao: data.dataInutilizacao ?? null,
          usuarioInutilizacao: data.usuarioInutilizacao ?? null,
          dataReativacao: data.dataReativacao ?? null,
          motivoReativacao: data.motivoReativacao ?? null,
          usuarioReativacao: data.usuarioReativacao ?? null,
          usuarioEhAprovador: data.usuarioEhAprovador ?? false,
        });
        const estaEmAprovacao = data.statusGeral !== "EmDigitacao";
        setEmAprovacao(estaEmAprovacao);
      })
      .catch(console.error);
  }, [editandoId]);

  // ── Abrir no form
  const handleVisualizar = useCallback((proposta) => {
    setEditandoId(proposta.id);
    setCodigoProposta(proposta.codigo ?? `PC${proposta.id}`);

    setSomenteLeitura(true);
    setEditandoPendenciaGarantia(false);

    setImovelSelecionado(null);
    if (proposta.imovelId) {
      const tk = localStorage.getItem("token");
      fetch(`/api/imoveis/${proposta.imovelId}`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((im) => {
          if (im) setImovelSelecionado(im);
        })
        .catch(() => {});
    }

    setForm({
      complementoLocacao: proposta.complementoLocacao || "",

      areaTotalLocada:
        proposta.areaTotalLocada !== null && proposta.areaTotalLocada !== undefined && proposta.areaTotalLocada !== ""
          ? formatMetragemBancoBR(proposta.areaTotalLocada)
          : "",

      areaConstruidaTotalLocada:
        proposta.areaConstruidaTotalLocada !== null && proposta.areaConstruidaTotalLocada !== undefined && proposta.areaConstruidaTotalLocada !== ""
          ? formatMetragemBancoBR(proposta.areaConstruidaTotalLocada)
          : "",
      areaLocada: proposta.areaLocada || "",
      tipoLocacao: proposta.tipoLocacao || "",
      tipoImovelLocacao: proposta.tipoImovelLocacao || "",
      prazo: proposta.prazo ?? "",
      dataInicio: proposta.dataInicio ? proposta.dataInicio.substring(0, 10) : "",
      dataFim: proposta.dataFim ? proposta.dataFim.substring(0, 10) : "",
      vAluguelMensal: formatBRL(proposta.vAluguelMensal),
      vCondominio: formatBRL(proposta.vCondominio),
      vIPTU: formatBRL(proposta.vIPTU),
      vAguaEsgoto: formatBRL(proposta.vAguaEsgoto),
      vEnergia: formatBRL(proposta.vEnergia),
      vDesconto: formatBRL(proposta.vDesconto),
      descrDesconto: proposta.descrDesconto || "",
      carencia: proposta.carencia || "",
      vInternet: formatBRL(proposta.vInternet),
      vTotem: formatBRL(proposta.vTotem),
      // ALUGUEL
      mesReajuste: proposta.mesReajuste || "",
      indiceReajusteId: proposta.indiceReajusteId ?? "",
      tipoVencimento: proposta.tipoVencimento || "Pós",
      diaVencimento: proposta.diaVencimento ?? "",

      tipoPrimeiroVencimento: proposta.tipoPrimeiroVencimento || "",

      primeiroVencimento: proposta.primeiroVencimento ? String(proposta.primeiroVencimento).substring(0, 10) : "",

      // IPTU
      // Compatibilidade com PCs antigas:
      // se já existe valor de IPTU, considera APLICÁVEL
      // enquanto o novo campo ainda não existir no banco.
      iptuAplicabilidade: proposta.iptuAplicabilidade || (proposta.vIPTU !== null && proposta.vIPTU !== undefined && proposta.vIPTU !== "" ? "APLICÁVEL" : ""),

      iptuTipoVencimento: proposta.iptuTipoVencimento || "",

      iptuDiaVencimento: proposta.iptuDiaVencimento ?? "",

      iptuMesReajuste: proposta.iptuMesReajuste || "",

      iptuPrimeiroVencimento: proposta.iptuPrimeiroVencimento ? String(proposta.iptuPrimeiroVencimento).substring(0, 10) : "",

      // CONDOMÍNIO
      condominioAplicabilidade:
        proposta.condominioAplicabilidade ||
        (proposta.vCondominio !== null && proposta.vCondominio !== undefined && proposta.vCondominio !== "" ? "APLICÁVEL" : ""),

      condominioTipoVencimento: proposta.condominioTipoVencimento || "",

      condominioDiaVencimento: proposta.condominioDiaVencimento ?? "",

      condominioMesReajuste: proposta.condominioMesReajuste || "",

      condominioPrimeiroVencimento: proposta.condominioPrimeiroVencimento ? String(proposta.condominioPrimeiroVencimento).substring(0, 10) : "",

      // ÁGUA E ESGOTO
      aguaEsgotoTipoMedicao: proposta.aguaEsgotoTipoMedicao || "",

      aguaEsgotoTipoVencimento: proposta.aguaEsgotoTipoVencimento || "",

      aguaEsgotoDiaVencimento: proposta.aguaEsgotoDiaVencimento ?? "",

      aguaEsgotoPrimeiroVencimento: proposta.aguaEsgotoPrimeiroVencimento ? String(proposta.aguaEsgotoPrimeiroVencimento).substring(0, 10) : "",

      // ENERGIA
      energiaTipoMedicao: proposta.energiaTipoMedicao || "",

      energiaTipoVencimento: proposta.energiaTipoVencimento || "",

      energiaDiaVencimento: proposta.energiaDiaVencimento ?? "",

      energiaPrimeiroVencimento: proposta.energiaPrimeiroVencimento ? String(proposta.energiaPrimeiroVencimento).substring(0, 10) : "",

      // INTERNET
      internetAplicabilidade:
        proposta.internetAplicabilidade || (proposta.vInternet !== null && proposta.vInternet !== undefined && proposta.vInternet !== "" ? "APLICÁVEL" : ""),

      internetTipoVencimento: proposta.internetTipoVencimento || "",

      internetDiaVencimento: proposta.internetDiaVencimento ?? "",

      internetMesReajuste: proposta.internetMesReajuste || "",

      internetIndiceReajusteId: proposta.internetIndiceReajusteId ?? "",

      internetPrimeiroVencimento: proposta.internetPrimeiroVencimento ? String(proposta.internetPrimeiroVencimento).substring(0, 10) : "",

      // TOTEM
      totemAplicabilidade:
        proposta.totemAplicabilidade || (proposta.vTotem !== null && proposta.vTotem !== undefined && proposta.vTotem !== "" ? "APLICÁVEL" : ""),

      totemTipoVencimento: proposta.totemTipoVencimento || "",

      totemDiaVencimento: proposta.totemDiaVencimento ?? "",

      totemMesReajuste: proposta.totemMesReajuste || "",

      totemPrimeiroVencimento: proposta.totemPrimeiroVencimento ? String(proposta.totemPrimeiroVencimento).substring(0, 10) : "",
      totemIndiceReajusteId: proposta.totemIndiceReajusteId ?? "",

      garantia: proposta.garantia || "",
      vCaucao: formatBRL(proposta.vCaucao),
      qtdeAluguelCaucao: proposta.qtdeAluguelCaucao ?? "",
      boleto: proposta.boleto || "",
      boletoQuantidade: proposta.boletoQuantidade ?? "",
      vBoleto: formatBRL(proposta.vBoleto),
      fiadorTipoGarantia: proposta.fiadorTipoGarantia || "",

      fiadorImovelTipo: proposta.fiadorImovelTipo || "",

      fiadorImovelCidadeCartorioRgi: proposta.fiadorImovelCidadeCartorioRgi || "",

      fiadorImovelNomeCartorioRgi: proposta.fiadorImovelNomeCartorioRgi || "",

      fiadorImovelRgi: proposta.fiadorImovelRgi || "",

      fiadorImovelMatricula: proposta.fiadorImovelMatricula || "",

      fiadorImovelCartorio: proposta.fiadorImovelCartorio || "",

      fiadorImovelLivro: proposta.fiadorImovelLivro || "",

      fiadorImovelFolha: proposta.fiadorImovelFolha || "",

      fiadorImovelEndereco: proposta.fiadorImovelEndereco || "",

      fiadorImovelNumero: proposta.fiadorImovelNumero || "",

      fiadorImovelBairro: proposta.fiadorImovelBairro || "",

      fiadorImovelCidade: proposta.fiadorImovelCidade || "",

      fiadorImovelUf: proposta.fiadorImovelUf || "",

      fiadorImovelCep: proposta.fiadorImovelCep || "",

      fiadorImovelComplemento: proposta.fiadorImovelComplemento || "",

      fiadorImovelUrlGoogleMaps: proposta.fiadorImovelUrlGoogleMaps || "",

      garantiaDescricaoImovel: proposta.garantiaDescricaoImovel || "",
      garantiaCadastroMunicipal: proposta.garantiaCadastroMunicipal || "",
      garantiaSetor: proposta.garantiaSetor || "",
      garantiaQuarteirao: proposta.garantiaQuarteirao || "",

      garantiaTipoImovel: proposta.garantiaTipoImovel || "",
      garantiaRgi: proposta.garantiaRgi || "",
      garantiaMatricula: proposta.garantiaMatricula || "",
      garantiaCartorio: proposta.garantiaCartorio || "",
      garantiaLivro: proposta.garantiaLivro || "",
      garantiaFolha: proposta.garantiaFolha || "",
      garantiaEndereco: proposta.garantiaEndereco || "",
      garantiaNumero: proposta.garantiaNumero || "",
      garantiaBairro: proposta.garantiaBairro || "",
      garantiaCidade: proposta.garantiaCidade || "",
      garantiaUf: proposta.garantiaUf || "",
      garantiaCep: proposta.garantiaCep || "",
      garantiaComplemento: proposta.garantiaComplemento || "",
      garantiaUrlGoogleMaps: proposta.garantiaUrlGoogleMaps || "",
      consideracoes: proposta.consideracoes || "",
    });
    setGarantiaDocs(proposta.garantiaImobiliariaDocs ?? []);
    setGarantiaDocsPendentes([]);

    const fichas = proposta.fichaPessoas ?? [];
    setLocadoras(fichas.filter((f) => f.campoProposta === "Locadora").map(({ id: _id, campoProposta: _c, ...rest }) => rest));
    setLocatarias(fichas.filter((f) => f.campoProposta === "Locataria").map(({ id: _id, campoProposta: _c, ...rest }) => rest));
    const seg = fichas.find((f) => f.campoProposta === "Seguradora");
    setSeguradora(
      seg
        ? { tipoPessoa: seg.tipoPessoa, razaoSocial: seg.razaoSocial, cnpj: seg.cnpj || "", cpf: seg.cpf || "", email: seg.email || "" }
        : { ...fichaVazia, tipoPessoa: "PJ" },
    );
    const corretor = fichas.find((f) => f.campoProposta === "CorretorSeguro");

    setCorretorSeguro(
      corretor
        ? {
            tipoPessoa: corretor.tipoPessoa || "PF",

            razaoSocial: corretor.razaoSocial || "",

            cnpj: corretor.cnpj || "",

            cpf: corretor.cpf || "",

            email: corretor.email || "",
          }
        : {
            ...fichaVazia,
            tipoPessoa: "PF",
          },
    );
    const fid = fichas.find((f) => f.campoProposta === "Fiador");
    setFiador(
      fid
        ? { tipoPessoa: fid.tipoPessoa, razaoSocial: fid.razaoSocial, cnpj: fid.cnpj || "", cpf: fid.cpf || "", email: fid.email || "" }
        : { ...fichaVazia, tipoPessoa: "PF" },
    );

    const cau = fichas.find((f) => f.campoProposta === "Caucionante");

    setCaucionante(
      cau
        ? {
            tipoPessoa: cau.tipoPessoa || "PJ",
            razaoSocial: cau.razaoSocial || "",
            cnpj: cau.cnpj || "",
            cpf: cau.cpf || "",
            email: cau.email || "",
            endereco: cau.endereco || "",
            numero: cau.numero || "",
            bairro: cau.bairro || "",
            cidade: cau.cidade || "",
            uf: cau.uf || "",
            cep: cau.cep || "",
            complemento: cau.complemento || "",
          }
        : {
            ...fichaVazia,
            tipoPessoa: "PJ",
          },
    );

    const estaEmAprovacao = proposta.statusGeral !== "EmDigitacao";
    setEmAprovacao(estaEmAprovacao);
    if (estaEmAprovacao) setSomenteLeitura(true);

    setAprovacaoInfo({
      aprovacao: (proposta.aprovacoes ?? []).find((a) => a.processoTipo === "Proposta") ?? null,
      autorizacoes: proposta.autorizacoes ?? [],
      historicos: proposta.historicos ?? [],
      statusGeral: proposta.statusGeral ?? "EmDigitacao",
      statusFichas: proposta.statusFichas ?? null,
      statusAnaliseCredito: proposta.statusAnaliseCredito ?? null,
      idUsuario: proposta.idUsuario ?? null,
      motivoInutilizacao: proposta.motivoInutilizacao ?? null,
      dataInutilizacao: proposta.dataInutilizacao ?? null,
      usuarioInutilizacao: proposta.usuarioInutilizacao ?? null,
      dataReativacao: proposta.dataReativacao ?? null,
      motivoReativacao: proposta.motivoReativacao ?? null,
      usuarioReativacao: proposta.usuarioReativacao ?? null,
      usuarioEhAprovador: proposta.usuarioEhAprovador ?? false,
    });

    setGridMinimizado(true);
    setTimeout(() => {
      codigoInputRef.current?.focus({ preventScroll: true });
    }, 300);
  }, []);

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => imovelInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = useCallback(() => {
    setEditandoId(null);
    setCodigoProposta("");
    setSomenteLeitura(false);
    setEditandoPendenciaGarantia(false);
    setImovelSelecionado(null);

    setForm({
      ...formVazio,
    });

    setLocatarias([]);

    setSeguradora({
      ...fichaVazia,
      tipoPessoa: "PJ",
    });

    setCorretorSeguro({
      ...fichaVazia,
      tipoPessoa: "PF",
    });

    setFiador({
      ...fichaVazia,
      tipoPessoa: "PF",
    });

    setCaucionante({
      ...fichaVazia,
      tipoPessoa: "PJ",
    });

    setGarantiaDocs([]);
    setGarantiaDocsPendentes([]);
    setArquivoGarantia(null);
    setErrosForm({});
    setAbaAtiva("proposta");
    setEmAprovacao(false);

    setAprovacaoInfo({
      aprovacao: null,
      autorizacoes: [],
      historicos: [],
    });

    setGridMinimizado(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ── Documentos de comprovação da Garantia Imobiliária ───────────────────────

  const handleUploadGarantiaDoc = useCallback(() => {
    if (!editandoId || !arquivoGarantia) return;
    const tk = localStorage.getItem("token");
    setEnviandoGarantiaDoc(true);
    const fd = new FormData();
    fd.append("arquivo", arquivoGarantia);
    fetch(`/api/propostas/${editandoId}/garantia-imobiliaria/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tk}` },
      body: fd,
    })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.erro || "Erro ao enviar.");
        setArquivoGarantia(null);
        setGarantiaDocs((prev) => [d, ...prev]);
        mostrarSnack("Documento enviado com sucesso!");
      })
      .catch((e) => mostrarSnack(e.message || "Erro ao enviar arquivo.", "error"))
      .finally(() => setEnviandoGarantiaDoc(false));
  }, [editandoId, arquivoGarantia, mostrarSnack]);

  // Anexa localmente (sem rede) enquanto a proposta ainda não foi salva/não tem id.
  const handleAdicionarGarantiaDocPendente = useCallback(() => {
    if (!arquivoGarantia) return;
    const ext = `.${arquivoGarantia.name.split(".").pop()?.toLowerCase() ?? ""}`;
    if (!EXTENSOES_GARANTIA_DOC.includes(ext)) {
      mostrarSnack(`Tipo de arquivo não permitido. Use: ${EXTENSOES_GARANTIA_DOC.join(", ")}.`, "error");
      return;
    }
    if (arquivoGarantia.size > MAX_MB_GARANTIA_DOC * 1024 * 1024) {
      mostrarSnack(`Arquivo maior que ${MAX_MB_GARANTIA_DOC}MB.`, "error");
      return;
    }
    setGarantiaDocsPendentes((prev) => [...prev, { tempId: crypto.randomUUID(), file: arquivoGarantia }]);
    setArquivoGarantia(null);
  }, [arquivoGarantia, mostrarSnack]);

  const handleRemoverGarantiaDocPendente = useCallback((tempId) => {
    setGarantiaDocsPendentes((prev) => prev.filter((d) => d.tempId !== tempId));
  }, []);

  const handleVisualizarGarantiaDocPendente = useCallback((file) => {
    const url = URL.createObjectURL(file);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);

  const handleDeleteGarantiaDoc = useCallback(
    (docId) => {
      if (!editandoId) return;
      const tk = localStorage.getItem("token");
      fetch(`/api/propostas/${editandoId}/garantia-imobiliaria/docs/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tk}` },
      })
        .then((r) => {
          if (r.ok) setGarantiaDocs((prev) => prev.filter((d) => d.id !== docId));
          else mostrarSnack("Erro ao excluir.", "error");
        })
        .catch(() => mostrarSnack("Erro ao excluir.", "error"));
    },
    [editandoId, mostrarSnack],
  );

  const handleDownloadGarantiaDoc = useCallback(
    (docId, nomeOriginal) => {
      if (!editandoId) return;
      const tk = localStorage.getItem("token");
      fetch(`/api/propostas/${editandoId}/garantia-imobiliaria/docs/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao baixar.")))
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = nomeOriginal;
          a.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => mostrarSnack("Erro ao baixar arquivo.", "error"));
    },
    [editandoId, mostrarSnack],
  );

  const handleVisualizarGarantiaDoc = useCallback(
    (docId) => {
      if (!editandoId) return;
      const tk = localStorage.getItem("token");
      fetch(`/api/propostas/${editandoId}/garantia-imobiliaria/docs/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao visualizar.")))
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        })
        .catch(() => mostrarSnack("Erro ao visualizar arquivo.", "error"));
    },
    [editandoId, mostrarSnack],
  );

  // Seleção do arquivo de garantia
  const handleSelecionarArquivoGarantia = useCallback((e) => {
    setArquivoGarantia(e.target.files[0] ?? null);
  }, []);

  // ── Ao selecionar imóvel: pré-popular locadora e tipo de locação
  const handleSelecionarImovel = useCallback((_, val) => {
    setImovelSelecionado(val);

    setForm((p) => ({
      ...p,
      tipoLocacao: val?.tipo || "",
    }));

    setLocadorSelecionado(null);
    setEmailNovoLocador("");
    setModalProprietarioAberto(false);
    setAdicionandoLocador(false);

    if (val?.nomeProprietarioMatricula) {
      const ehFisica = val.proprietarioTipoPessoa === "fisica";

      const documento = ehFisica ? val.proprietarioCpf : val.proprietarioCnpj;

      const tipoPessoa = ehFisica ? "PF" : "PJ";

      // O proprietário do imóvel já entra automaticamente
      // como primeira Locadora.
      setLocadoras([
        {
          tipoPessoa,

          razaoSocial: String(val.nomeProprietarioMatricula || "").toUpperCase(),

          cnpj: tipoPessoa === "PJ" ? (documento ? mascararCNPJ(documento) : "") : "",

          cpf: tipoPessoa === "PF" ? (documento ? mascararCPF(documento) : "") : "",

          // Proprietário estrangeiro (vem do cadastro do proprietário).
          estrangeiro: Boolean(val.proprietarioEstrangeiro),
          documentoEstrangeiroTipo: val.proprietarioDocumentoEstrangeiroTipo || "",
          documentoEstrangeiroNumero: val.proprietarioDocumentoEstrangeiroNumero || "",
          documentoEstrangeiroPais: val.proprietarioDocumentoEstrangeiroPais || "",

          email: "",
        },
      ]);
    } else {
      setLocadoras([]);
    }
  }, []);
  /*
   * =========================================================
   * ÁREA LOCADA IGUAL AO IMÓVEL
   * =========================================================
   *
   * SIM:
   * Área Locada - Terreno recebe automaticamente
   * a Área Terreno do imóvel selecionado.
   *
   * A Área Locada - Construção continua manual.
   *
   * NÃO:
   * Terreno e Construção continuam manuais.
   * =========================================================
   */

  useEffect(() => {
    if (form.areaLocada !== "SIM") {
      return;
    }

    const areaTerrenoImovel = imovelSelecionado?.areaTerreno;

    const areaTerrenoFormatada =
      areaTerrenoImovel === null || areaTerrenoImovel === undefined || areaTerrenoImovel === "" ? "" : formatMetragemExibicaoBR(areaTerrenoImovel);

    setForm((prev) => {
      if (prev.areaTotalLocada === areaTerrenoFormatada) {
        return prev;
      }

      return {
        ...prev,
        areaTotalLocada: areaTerrenoFormatada,
      };
    });
  }, [form.areaLocada, imovelSelecionado?.areaTerreno]);
  // ── Adicionar proprietário escolhido diretamente como Locadora
  const handleAdicionarLocador = useCallback(
    (proprietario) => {
      if (!proprietario) {
        mostrarSnack("Selecione um proprietário.", "warning");
        return;
      }

      const ehFisica = proprietario.tipoPessoa === "fisica";

      const ehJuridica = proprietario.tipoPessoa === "juridica";

      const tipoPessoa = ehFisica ? "PF" : "PJ";

      const novaLocadora = {
        proprietarioId: proprietario.id ?? proprietario._id ?? null,

        tipoPessoa,

        razaoSocial: String(proprietario.nome || "").toUpperCase(),

        cnpj: ehJuridica && proprietario.cnpj ? mascararCNPJ(proprietario.cnpj) : "",

        cpf: ehFisica && proprietario.cpf ? mascararCPF(proprietario.cpf) : "",

        // Proprietário estrangeiro (vem do cadastro do proprietário).
        estrangeiro: Boolean(proprietario.estrangeiro),
        documentoEstrangeiroTipo: proprietario.documentoEstrangeiroTipo || "",
        documentoEstrangeiroNumero: proprietario.documentoEstrangeiroNumero || "",
        documentoEstrangeiroPais: proprietario.documentoEstrangeiroPais || "",

        // O e-mail continua manual.
        // Depois de adicionar, pode ser informado pelo botão Editar.
        email: "",
      };

      // Mantém a regra já existente:
      // não permite incluir novamente o mesmo proprietário.
      const documentoNovo = limparCpfCnpj(novaLocadora.tipoPessoa === "PJ" ? novaLocadora.cnpj || "" : novaLocadora.cpf || "");

      const jaAdicionado = locadoras.some((locadora) => {
        const documentoAtual = limparCpfCnpj(locadora.tipoPessoa === "PJ" ? locadora.cnpj || "" : locadora.cpf || "");

        if (documentoNovo && documentoAtual) {
          return documentoNovo === documentoAtual;
        }

        return (
          String(locadora.razaoSocial || "")
            .trim()
            .toUpperCase() ===
          String(novaLocadora.razaoSocial || "")
            .trim()
            .toUpperCase()
        );
      });

      if (jaAdicionado) {
        mostrarSnack("Este proprietário já está adicionado como locador.", "warning");
        return;
      }

      setLocadoras((prev) => [...prev, novaLocadora]);

      setLocadorSelecionado(null);
      setEmailNovoLocador("");
      setAdicionandoLocador(false);
      setModalProprietarioAberto(false);

      mostrarSnack("Locador adicionado.", "success");
    },
    [locadoras, mostrarSnack],
  );

  const handleChangeSeguradora = useCallback((v) => {
    setSeguradora(v);

    setErrosForm((p) => ({
      ...p,
      seguradora: undefined,
    }));
  }, []);

  const handleBlurObrigatorioSeguradora = useCallback(() => {
    setErrosForm((p) => ({
      ...p,
      seguradora: "Dados da Seguradora são obrigatórios.",
    }));
  }, []);

  const handleChangeCorretorSeguro = useCallback((v) => {
    setCorretorSeguro(v);

    setErrosForm((p) => ({
      ...p,
      corretorSeguroNome: undefined,
      corretorSeguroDocumento: undefined,
      corretorSeguroEmail: undefined,
    }));
  }, []);

  const handleBlurObrigatorioCorretorSeguro = useCallback(() => {
    setErrosForm((p) => ({
      ...p,
      corretorSeguroNome: "Nome / Razão Social do Corretor de Seguros é obrigatório.",
    }));
  }, []);

  const handleChangeFiador = useCallback((v) => {
    setFiador(v);

    setErrosForm((p) => ({
      ...p,
      fiador: undefined,
    }));
  }, []);

  const handleBlurObrigatorioFiador = useCallback(() => {
    setErrosForm((p) => ({
      ...p,
      fiador: "Dados do Fiador são obrigatórios.",
    }));
  }, []);

  const handleChangeCaucionante = useCallback((v) => {
    setCaucionante(v);

    setErrosForm((p) => ({
      ...p,
      caucionante: undefined,
    }));
  }, []);

  const handleBlurObrigatorioCaucionante = useCallback(() => {
    setErrosForm((p) => ({
      ...p,
      caucionante: "Dados da Caucionante são obrigatórios.",
    }));
  }, []);

  // ── Campos de metragem da locação
  const handleMetragemLocadaChange = useCallback(
    (campo) => (e) => {
      if (somenteLeitura) return;

      setForm((prev) => ({
        ...prev,
        [campo]: maskMetragemTypingBR(e.target.value),
      }));
    },
    [somenteLeitura],
  );

  const handleMetragemLocadaBlur = useCallback(
    (campo) => (e) => {
      if (somenteLeitura) return;

      setForm((prev) => ({
        ...prev,
        [campo]: maskMetragem4BR(e.target.value),
      }));
    },
    [somenteLeitura],
  );

  const montarBody = useCallback(() => {
    const enderecoCompleto = [
      imovelSelecionado?.endereco,
      imovelSelecionado?.numero,
      imovelSelecionado?.complemento,
      imovelSelecionado?.bairro,
      imovelSelecionado?.cidade,
      imovelSelecionado?.uf,
    ]
      .filter(Boolean)
      .join(", ");

    const valoresDesformatados = CAMPOS_VALOR.reduce((acc, k) => {
      acc[k] = parseBRL(form[k]);
      return acc;
    }, {});

    return {
      imovelId: imovelSelecionado?.id,

      enderecoImovel: enderecoCompleto || null,

      areaImovel: imovelSelecionado?.areaConstruida ?? null,

      ...form,
      ...valoresDesformatados,

      locadoras,
      locatarias,

      seguradora: garantiasDe(form.garantia).includes("Seguro Fiança") ? seguradora : null,

      corretorSeguro: garantiasDe(form.garantia).includes("Seguro Fiança") ? corretorSeguro : null,

      caucionante: garantiasDe(form.garantia).includes("Imobiliária") ? caucionante : null,

      fiador: garantiasDe(form.garantia).includes("Fiador") ? fiador : null,
    };
  }, [imovelSelecionado, form, locadoras, locatarias, seguradora, corretorSeguro, fiador, caucionante]);

  // ── Validar campos obrigatórios
  const validar = useCallback(() => {
    const erros = {};
    if (!imovelSelecionado) erros.imovel = "Selecione um imóvel.";
    if (!locatarias.filter((l) => l.razaoSocial?.trim()).length) erros.locatarias = "Ao menos uma Locatária é obrigatória.";
    return erros;
  }, [imovelSelecionado, locatarias]);

  // ── Validar resolução da pendência de garantia
  const validarPendenciaGarantia = useCallback(() => {
    const erros = {};

    if (!form.garantia || form.garantia === "A Ser Determinado") {
      erros.garantia = "Defina uma garantia antes de reenviar a proposta para aprovação.";
    }

    const tiposGarantia = garantiasDe(form.garantia);

    if (tiposGarantia.includes("Caução") && !form.qtdeAluguelCaucao) {
      erros.qtdeAluguelCaucao = "Quantidade de Aluguéis é obrigatória.";
    }

    if (tiposGarantia.includes("Caução") && !form.vCaucao) {
      erros.vCaucao = "Valor do Caução é obrigatório.";
    }

    /* CORRETOR DE SEGUROS */
    if (tiposGarantia.includes("Seguro Fiança") && !corretorSeguro.razaoSocial?.trim()) {
      erros.corretorSeguroNome = "Nome / Razão Social do Corretor de Seguros é obrigatório.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && corretorSeguro.tipoPessoa === "PJ" && corretorSeguro.cnpj && !validarCNPJ(corretorSeguro.cnpj)) {
      erros.corretorSeguroDocumento = "CNPJ do Corretor de Seguros inválido.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && corretorSeguro.tipoPessoa === "PF" && corretorSeguro.cpf && !validarCPF(corretorSeguro.cpf)) {
      erros.corretorSeguroDocumento = "CPF do Corretor de Seguros inválido.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && !corretorSeguro.email?.trim()) {
      erros.corretorSeguroEmail = "E-mail do Corretor de Seguros é obrigatório.";
    }

    if (tiposGarantia.includes("Fiador") && !fiador.razaoSocial?.trim()) {
      erros.fiador = "Dados do Fiador são obrigatórios.";
    }

    if (tiposGarantia.includes("Fiador") && !form.fiadorTipoGarantia) {
      erros.fiadorTipoGarantia = "Informe se o Fiador é COM IMÓVEL ou SEM IMÓVEL.";
    }

    return erros;
  }, [form, seguradora, corretorSeguro, fiador]);

  // ── Salvar / Enviar para Aprovação
  const handleSalvar = useCallback(async () => {
    const erros = validar();

    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);

      window.scrollTo({
        top: document.body.scrollHeight - 600,
        behavior: "smooth",
      });

      return;
    }

    setErrosForm({});

    const tk = localStorage.getItem("token");
    const criando = !editandoId;

    const url = editandoId ? `/api/propostas/${editandoId}` : "/api/propostas";

    const method = editandoId ? "PUT" : "POST";

    setSalvando(true);

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify(montarBody()),
      });

      const resultado = await res.json();

      if (!res.ok) {
        mostrarSnack(resultado.erro || "Erro.", "error");

        return;
      }

      if (criando && garantiaDocsPendentes.length > 0) {
        for (const pendente of garantiaDocsPendentes) {
          const fd = new FormData();

          fd.append("arquivo", pendente.file);

          try {
            const rUpload = await fetch(`/api/propostas/${resultado.id}/garantia-imobiliaria/upload`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${tk}`,
              },
              body: fd,
            });

            if (!rUpload.ok) {
              throw new Error();
            }
          } catch {
            mostrarSnack(`Proposta salva, mas houve falha ao enviar "${pendente.file.name}".`, "error");
          }
        }
      }

      mostrarSnack("Proposta salva com sucesso");

      handleCancelarForm();
      carregarPropostas();
    } catch {
      mostrarSnack("Erro de conexão.", "error");
    } finally {
      setSalvando(false);
    }
  }, [validar, montarBody, editandoId, garantiaDocsPendentes, mostrarSnack, handleCancelarForm, carregarPropostas]);
  // ── Validação completa para envio à aprovação
  const validarParaAprovacao = useCallback(() => {
    const erros = {};
    if (!imovelSelecionado) erros.imovel = "Selecione um imóvel.";
    if (!locatarias.filter((l) => l.razaoSocial?.trim()).length) erros.locatarias = "Ao menos uma Locatária é obrigatória.";
    if (!form.tipoLocacao) erros.tipoLocacao = "Tipo de Locação é obrigatório.";
    if (!form.prazo) erros.prazo = "Prazo é obrigatório.";
    if (!form.dataInicio) erros.dataInicio = "Data de Início é obrigatória.";
    if (!form.dataFim) erros.dataFim = "Data de Término é obrigatória.";
    if (!form.vAluguelMensal) erros.vAluguelMensal = "Valor do Aluguel Mensal é obrigatório.";
    if (form.iptuAplicabilidade === "APLICÁVEL" && !form.vIPTU) {
      erros.vIPTU = "IPTU é obrigatório.";
    }
    if (!form.mesReajuste) erros.mesReajuste = "Mês de Reajuste é obrigatório.";
    if (!form.indiceReajusteId) erros.indiceReajusteId = "Índice de Reajuste é obrigatório.";
    if (!form.tipoVencimento) erros.tipoVencimento = "Tipo de Vencimento é obrigatório.";
    if (!form.diaVencimento) erros.diaVencimento = "Dia de Vencimento é obrigatório.";
    const tiposGarantia = garantiasDe(form.garantia);
    if (!tiposGarantia.length) erros.garantia = "Garantia é obrigatória.";
    if (form.vDesconto && !form.descrDesconto?.trim()) erros.descrDesconto = "Descrição do Desconto é obrigatória quando há Desconto.";
    if (form.boleto === "SIM" && !form.boletoQuantidade) erros.boletoQuantidade = "Quantidade de Boletos é obrigatória.";
    if (form.boleto === "SIM" && !form.vBoleto) erros.vBoleto = "Valor do Boleto é obrigatório.";
    if (tiposGarantia.includes("Caução") && !form.qtdeAluguelCaucao) erros.qtdeAluguelCaucao = "Quantidade de Aluguéis é obrigatória.";
    if (tiposGarantia.includes("Caução") && !form.vCaucao) erros.vCaucao = "Valor do Caução é obrigatório.";
    if (tiposGarantia.includes("Seguro Fiança") && !seguradora.razaoSocial?.trim()) {
      erros.seguradora = "Dados da Seguradora são obrigatórios.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && seguradora.tipoPessoa === "PJ" && seguradora.cnpj && !validarCNPJ(seguradora.cnpj)) {
      erros.seguradora = "CNPJ da Seguradora inválido.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && seguradora.tipoPessoa === "PF" && seguradora.cpf && !validarCPF(seguradora.cpf)) {
      erros.seguradora = "CPF da Seguradora inválido.";
    }

    /* CORRETOR DE SEGUROS */
    if (tiposGarantia.includes("Seguro Fiança") && !corretorSeguro.razaoSocial?.trim()) {
      erros.corretorSeguroNome = "Nome / Razão Social do Corretor de Seguros é obrigatório.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && corretorSeguro.tipoPessoa === "PJ" && corretorSeguro.cnpj && !validarCNPJ(corretorSeguro.cnpj)) {
      erros.corretorSeguroDocumento = "CNPJ do Corretor de Seguros inválido.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && corretorSeguro.tipoPessoa === "PF" && corretorSeguro.cpf && !validarCPF(corretorSeguro.cpf)) {
      erros.corretorSeguroDocumento = "CPF do Corretor de Seguros inválido.";
    }

    if (tiposGarantia.includes("Seguro Fiança") && !corretorSeguro.email?.trim()) {
      erros.corretorSeguroEmail = "E-mail do Corretor de Seguros é obrigatório.";
    }

    /* FIADOR */
    if (tiposGarantia.includes("Fiador") && !fiador.razaoSocial?.trim()) {
      erros.fiador = "Dados do Fiador são obrigatórios.";
    }

    /* OBRIGA ESCOLHER COM IMÓVEL OU SEM IMÓVEL */
    if (tiposGarantia.includes("Fiador") && !form.fiadorTipoGarantia) {
      erros.fiadorTipoGarantia = "Informe se o Fiador é COM IMÓVEL ou SEM IMÓVEL.";
    }

    if (tiposGarantia.includes("Fiador") && fiador.tipoPessoa === "PJ" && fiador.cnpj && !validarCNPJ(fiador.cnpj)) {
      erros.fiador = "CNPJ do Fiador inválido.";
    }

    if (tiposGarantia.includes("Fiador") && fiador.tipoPessoa === "PF" && fiador.cpf && !validarCPF(fiador.cpf)) {
      erros.fiador = "CPF do Fiador inválido.";
    }

    return erros;
  }, [imovelSelecionado, form, locadoras, locatarias, seguradora, corretorSeguro, fiador]);

  // ── Enviar para Aprovação
  const handleEnviarAprovacao = useCallback(() => {
    const erros = validarParaAprovacao();

    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);

      const mensagens = Object.values(erros).filter((mensagem) => typeof mensagem === "string" && mensagem.trim());

      mostrarSnack(
        mensagens[0] || "Existem campos obrigatórios ou inválidos que precisam ser corrigidos antes de enviar a proposta para aprovação.",
        "warning",
      );

      setAbaAtiva("dados");

      window.scrollTo({
        top: document.body.scrollHeight - 600,
        behavior: "smooth",
      });

      return;
    }

    setErrosForm({});
    setErrosForm({});
    const tk = localStorage.getItem("token");
    // Salva o form antes de enviar para garantir que o banco está atualizado
    fetch(`/api/propostas/${editandoId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify(montarBody()),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          mostrarSnack(d.erro || "Erro ao salvar.", "error");
          return;
        }
        return fetch(`/api/propostas/${editandoId}/enviar-aprovacao`, {
          method: "POST",
          headers: { Authorization: `Bearer ${tk}` },
        });
      })
      .then(async (res) => {
        if (!res) return;
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Proposta enviada para aprovação!");
        setEmAprovacao(true);
        setSomenteLeitura(true);
        carregarPropostas();
        carregarAprovacao();
        setAbaAtiva("aprovacao");
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  }, [validarParaAprovacao, editandoId, montarBody, mostrarSnack, carregarPropostas, carregarAprovacao]);

  // ── Reenviar para aprovação após resolver pendência de garantia
  const handleEnviarPendenciaGarantia = useCallback(async () => {
    if (!editandoId) return;

    const erros = validarPendenciaGarantia();

    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);
      return;
    }

    setErrosForm({});
    setSalvando(true);

    const tk = localStorage.getItem("token");

    try {
      const bodyAtual = montarBody();

      const resSalvar = await fetch(`/api/propostas/${editandoId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify(bodyAtual),
      });

      const dadosSalvar = await resSalvar.json().catch(() => ({}));

      if (!resSalvar.ok) {
        mostrarSnack(dadosSalvar.erro || "Erro ao salvar a garantia.", "error");
        return;
      }

      const resEnviar = await fetch(`/api/propostas/${editandoId}/enviar-aprovacao`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tk}`,
        },
      });

      const dadosEnviar = await resEnviar.json().catch(() => ({}));

      if (!resEnviar.ok) {
        mostrarSnack(dadosEnviar.erro || "Erro ao reenviar a proposta para aprovação.", "error");
        return;
      }

      mostrarSnack("Pendência resolvida. Proposta reenviada para aprovação!", "success");

      setEditandoPendenciaGarantia(false);
      setSomenteLeitura(true);
      setEmAprovacao(true);
      setAbaAtiva("aprovacao");

      carregarPropostas();
      carregarAprovacao();
    } catch (erro) {
      console.error("Erro ao reenviar proposta após pendência de garantia:", erro);

      mostrarSnack("Erro de conexão.", "error");
    } finally {
      setSalvando(false);
    }
  }, [editandoId, validarPendenciaGarantia, montarBody, mostrarSnack, carregarPropostas, carregarAprovacao]);

  // ── Inutilizar (propostas nunca são excluídas)
  const handleInutilizar = useCallback(
    (motivo) => {
      const { id } = confirmInutilizar;
      if (!motivo?.trim()) {
        mostrarSnack("Motivo é obrigatório.", "error");
        return;
      }
      setConfirmInutilizar({ aberto: false, id: null });
      const tk = localStorage.getItem("token");
      fetch(`/api/propostas/${id}/inutilizar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ motivo }),
      })
        .then(async (res) => {
          if (!res.ok) {
            const d = await res.json();
            mostrarSnack(d.erro || "Erro ao inutilizar.", "error");
            return;
          }
          if (editandoId === id) handleCancelarForm();
          mostrarSnack("Proposta inutilizada.", "warning");
          carregarPropostas();
        })
        .catch(() => mostrarSnack("Erro de conexão.", "error"));
    },
    [confirmInutilizar, editandoId, mostrarSnack, handleCancelarForm, carregarPropostas],
  );

  const handleAbrirInutilizar = useCallback((id) => {
    document.activeElement?.blur();
    setConfirmInutilizar({ aberto: true, id });
  }, []);

  // ── Ativar (reverte uma proposta inutilizada, volta pra "Em Digitação")
  const handleAtivar = useCallback(() => {
    const { id, motivo } = confirmAtivar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setConfirmAtivar({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" });
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${id}/ativar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro ao ativar.", "error");
          return;
        }
        if (editandoId === id) handleCancelarForm();
        mostrarSnack("Proposta ativada.", "success");
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  }, [confirmAtivar, editandoId, mostrarSnack, handleCancelarForm, carregarPropostas]);

  const handleAbrirAtivar = useCallback((id, motivoInutilizacao) => {
    document.activeElement?.blur();
    setConfirmAtivar({ aberto: true, id, motivoInutilizacao: motivoInutilizacao ?? "", motivo: "" });
  }, []);

  // ── Aprovar proposta (direto, sem dialog)
  const handleAprovar = useCallback(() => {
    const tk = localStorage.getItem("token");
    setProcessandoAprovacao(true);
    fetch(`/api/propostas/${editandoId}/aprovar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Proposta aprovada!");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setProcessandoAprovacao(false));
  }, [editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── Avançar status geral (AguardandoAssinatura / Concluido)
  const handleAtualizarStatus = useCallback(
    (novoStatus) => {
      const tk = localStorage.getItem("token");
      fetch(`/api/propostas/${editandoId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ statusGeral: novoStatus }),
      })
        .then(async (res) => {
          if (!res.ok) {
            const d = await res.json();
            mostrarSnack(d.erro || "Erro.", "error");
            return;
          }
          mostrarSnack(`Status atualizado para: ${STATUS_GERAL_CONFIG[novoStatus]?.label ?? novoStatus}`);
          carregarAprovacao();
          carregarPropostas();
        })
        .catch(() => mostrarSnack("Erro de conexão.", "error"));
    },
    [editandoId, mostrarSnack, carregarAprovacao, carregarPropostas],
  );

  // ── Responder reprovação
  const handleResponder = useCallback(() => {
    const { resposta, arquivo } = dialogResponder;
    if (!resposta?.trim()) {
      mostrarSnack("Resposta é obrigatória.", "error");
      return;
    }
    setDialogResponder({ aberto: false, resposta: "", pergunta: "", arquivo: null });
    const tk = localStorage.getItem("token");
    setProcessandoAprovacao(true);
    const fd = new FormData();
    fd.append("resposta", resposta);
    if (arquivo) fd.append("arquivo", arquivo);
    fetch(`/api/propostas/${editandoId}/responder`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tk}` },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Resposta enviada.", "info");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setProcessandoAprovacao(false));
  }, [dialogResponder, editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── Reprovar proposta (sempre definitivo)
  const handleReprovar = useCallback(() => {
    const { motivo } = dialogReprovar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setDialogReprovar({ aberto: false, motivo: "" });
    const tk = localStorage.getItem("token");
    setProcessandoAprovacao(true);
    fetch(`/api/propostas/${editandoId}/reprovar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Proposta reprovada.", "warning");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setProcessandoAprovacao(false));
  }, [dialogReprovar, editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── Questionar proposta
  const handleQuestionar = useCallback(() => {
    const { motivo, idsUsuarioResposta, arquivo } = dialogQuestionar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    if (!idsUsuarioResposta.length) {
      mostrarSnack("Selecione ao menos um usuário para responder.", "error");
      return;
    }
    setDialogQuestionar({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
    const tk = localStorage.getItem("token");
    setProcessandoAprovacao(true);
    const fd = new FormData();
    fd.append("motivo", motivo);
    fd.append("idsUsuarioResposta", JSON.stringify(idsUsuarioResposta));
    if (arquivo) fd.append("arquivo", arquivo);
    fetch(`/api/propostas/${editandoId}/questionar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tk}` },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Questão enviada, aguardando resposta.", "info");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setProcessandoAprovacao(false));
  }, [dialogQuestionar, editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── Solicitar revisão
  const handleRevisar = useCallback(() => {
    const { motivo } = confirmRevisar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setConfirmRevisar({ aberto: false, motivo: "" });
    const tk = localStorage.getItem("token");
    setProcessandoAprovacao(true);
    fetch(`/api/propostas/${editandoId}/revisar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Proposta retornada para digitação.", "info");
        setEmAprovacao(false);
        setSomenteLeitura(false);
        setAbaAtiva("proposta");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setProcessandoAprovacao(false));
  }, [confirmRevisar, editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── Reabrir Proposta (invalida o fluxo de aprovação da Proposta, Fichas e Análise de
  // Crédito de uma vez, voltando a Proposta pra "Em Digitação")
  const handleReabrirProposta = useCallback(() => {
    const { motivo } = confirmReabrir;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setConfirmReabrir({ aberto: false, motivo: "" });
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${editandoId}/reabrir`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack("Proposta reaberta e retornada para digitação.", "info");
        setEmAprovacao(false);
        setSomenteLeitura(false);
        setAbaAtiva("proposta");
        carregarAprovacao();
        carregarPropostas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  }, [confirmReabrir, editandoId, mostrarSnack, carregarAprovacao, carregarPropostas]);

  // ── helpers
  const fieldSx = (readOnly, erro, upper = false) => ({
    "& .MuiInputLabel-root": { color: erro ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
    "& .MuiOutlinedInput-root": {
      backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
      color: "var(--ig-t-ffffff)",
      "& fieldset": { borderColor: readOnly ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
      "&:hover fieldset": { borderColor: readOnly ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-888888)" },
    },
    "& input": { color: "var(--ig-t-ffffff)", ...(upper ? { textTransform: "uppercase" } : {}) },
    "& .MuiFormHelperText-root": { color: "#f44336" },
  });

  const selectSx = (readOnly) => ({
    backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
    color: "var(--ig-t-ffffff)",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: readOnly ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: readOnly ? "var(--ig-b-333333)" : "var(--ig-b-888888)" },
    "& .MuiSvgIcon-root": { color: readOnly ? "transparent" : "var(--ig-t-ffffff)" },
  });

  // Bloqueio geral do formulário.
  const ro = somenteLeitura;

  // Durante a resolução de pendência:
  // somente GARANTIA poderá ser editada.
  const roGarantia = somenteLeitura && !editandoPendenciaGarantia;

  const propostaComPendenciaGarantia = !!editandoId && aprovacaoInfo.statusGeral === "ComPendencias";

  // Imóvel/Locadoras/Locatárias ficam travados assim que a proposta já passou por algum
  // fluxo de aprovação
  const jaPassouPorAprovacaoAlgumaVez = useMemo(() => aprovacaoInfo.historicos.some((h) => h.processoTipo === "Proposta"), [aprovacaoInfo.historicos]);
  const roImovelLocador = ro || jaPassouPorAprovacaoAlgumaVez;

  const autorizacoesProposta = useMemo(() => aprovacaoInfo.autorizacoes.filter((a) => a.processoTipo === "Proposta"), [aprovacaoInfo.autorizacoes]);
  const historicosProposta = useMemo(() => aprovacaoInfo.historicos.filter((h) => h.processoTipo === "Proposta"), [aprovacaoInfo.historicos]);

  // Botão "Reabrir Proposta": visível quando a Proposta, as Fichas ou a Análise de Crédito
  // estiverem Reprovadas — reabre os 3 fluxos de uma vez. Autorização
  // é a mesma de qualquer Revisar: precisa ser membro de alguma alçada de aprovação.
  const podeReabrirProposta =
    editandoId &&
    podeCadastrar &&
    aprovacaoInfo.usuarioEhAprovador &&
    (aprovacaoInfo.statusGeral === "Reprovada" || aprovacaoInfo.statusFichas === "Reprovada" || aprovacaoInfo.statusAnaliseCredito === "Reprovada");

  // ── Campos obrigatórios vazios
  // Somente campos realmente obrigatórios ficam vermelhos
  // automaticamente ao visualizar uma proposta existente.
  const camposObrigatoriosVisuais = new Set([
    "imovel",
    "tipoLocacao",
    "prazo",
    "dataInicio",
    "dataFim",
    "vAluguelMensal",
    "mesReajuste",
    "indiceReajusteId",
    "tipoVencimento",
    "diaVencimento",
  ]);

  const erroEfetivo = (campo, valor) => {
    const vazio = valor === null || valor === undefined || valor === "";

    if (errosForm[campo]) {
      return errosForm[campo];
    }

    if (editandoId && camposObrigatoriosVisuais.has(campo) && vazio) {
      return "Campo obrigatório não preenchido.";
    }

    return undefined;
  };

  // ── Linhas APLICÁVEL / NÃO APLICÁVEL do bloco VALORES
  const linhasAplicaveisValores = [
    {
      chave: "iptu",
      labelAplicabilidade: "IPTU?",
      labelValor: "IPTU",
      aplicabilidade: "iptuAplicabilidade",
      valor: "vIPTU",
      tipo: "iptuTipoVencimento",
      dia: "iptuDiaVencimento",
      mes: "iptuMesReajuste",
      primeiro: "iptuPrimeiroVencimento",
    },
    {
      chave: "condominio",
      labelAplicabilidade: "Condomínio?",
      labelValor: "Condomínio",
      aplicabilidade: "condominioAplicabilidade",
      valor: "vCondominio",
      tipo: "condominioTipoVencimento",
      dia: "condominioDiaVencimento",
      mes: "condominioMesReajuste",
      primeiro: "condominioPrimeiroVencimento",
    },
    {
      chave: "internet",
      labelAplicabilidade: "Internet?",
      labelValor: "Internet",
      aplicabilidade: "internetAplicabilidade",
      valor: "vInternet",
      tipo: "internetTipoVencimento",
      dia: "internetDiaVencimento",
      mes: "internetMesReajuste",
      indice: "internetIndiceReajusteId",
      primeiro: "internetPrimeiroVencimento",
    },
    {
      chave: "totem",
      labelAplicabilidade: "Totem?",
      labelValor: "Totem",
      aplicabilidade: "totemAplicabilidade",
      valor: "vTotem",
      tipo: "totemTipoVencimento",
      dia: "totemDiaVencimento",
      mes: "totemMesReajuste",
      indice: "totemIndiceReajusteId",
      primeiro: "totemPrimeiroVencimento",
    },
  ];

  // ── Linhas de medição do bloco VALORES
  const linhasMedicaoValores = [
    {
      chave: "agua",
      labelMedicao: "Tipo Medição",
      labelValor: "Água e Esgoto",
      medicao: "aguaEsgotoTipoMedicao",
      opcoes: TIPOS_MEDICAO_AGUA,
      valor: "vAguaEsgoto",
      tipo: "aguaEsgotoTipoVencimento",
      dia: "aguaEsgotoDiaVencimento",
      primeiro: "aguaEsgotoPrimeiroVencimento",
    },
    {
      chave: "energia",
      labelMedicao: "Tipo Medição",
      labelValor: "Energia",
      medicao: "energiaTipoMedicao",
      opcoes: TIPOS_MEDICAO_ENERGIA,
      valor: "vEnergia",
      tipo: "energiaTipoVencimento",
      dia: "energiaDiaVencimento",
      primeiro: "energiaPrimeiroVencimento",
    },
  ];

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
            }}
          >
            Proposta Comercial
          </Typography>

          <Button
            type="button"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleTemplatePC}
            sx={{
              width: "auto",
              height: 40,
              backgroundColor: "#e65100",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              whiteSpace: "nowrap",

              "&:hover": {
                backgroundColor: "#bf360c",
              },
            }}
          >
            TEMPLATE PC
          </Button>
        </Box>

        {filtroDashboard && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={
                filtroDashboard.tipo === "statusAprovacao"
                  ? `Filtrado por: ${LABEL_STATUS_APROVACAO[filtroDashboard.valor] ?? filtroDashboard.valor}${filtroDashboard.geral ? " (todos os usuários)" : ""}`
                  : "Filtrado por: Minhas Propostas"
              }
              onDelete={handleLimparFiltroDashboard}
              sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }}
            />
          </Box>
        )}

        {/* Filtro + Copiar + CSV */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Propostas Comerciais — combine termos com %"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                color: "var(--ig-t-ffffff)",
              },
              "& input::placeholder": {
                color: "var(--ig-t-ffffff)",
                opacity: 1,
                fontWeight: 500,
                fontSize: "1rem",
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* Minimizar */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* Grid de propostas */}
        <GradeDePropostas
          gridMinimizado={gridMinimizado}
          carregando={carregando}
          propostas={propostas}
          total={total}
          pagina={pagina}
          porPagina={porPagina}
          editandoId={editandoId}
          podeVisualizar={podeVisualizar}
          podeInutilizar={podeInutilizar}
          onVisualizar={handleVisualizar}
          onNavigateFicha={handleNavigateFicha}
          onImprimir={handleImprimirProposta}
          onAbrirInutilizar={handleAbrirInutilizar}
          onAbrirAtivar={handleAbrirAtivar}
          setPagina={setPagina}
          setPorPagina={setPorPagina}
        />

        {/* ── Formulário inferior ─────────────────────────────────────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between", mb: 3, gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                <Typography variant="h6" sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                  {editandoPendenciaGarantia
                    ? "Editar Pendência da Garantia"
                    : editandoId && somenteLeitura
                      ? "Visualizar Proposta"
                      : editandoId
                        ? "Editar Proposta"
                        : "Nova Proposta Comercial"}
                </Typography>
                {editandoId && (
                  <Typography variant="body2" sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 500 }}>
                    {codigoProposta}
                  </Typography>
                )}
              </Box>
              {editandoId && (
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Status da Proposta:</Typography>
                    {chipStatusGeral(aprovacaoInfo.statusGeral ?? "EmDigitacao")}
                  </Box>
                  {aprovacaoInfo.statusGeral === "AguardandoAssinatura" && podeCadastrar && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleAtualizarStatus("Concluido")}
                      sx={{ borderColor: "#004d40", color: "var(--ig-t-80cbc4)", "&:hover": { backgroundColor: "rgba(0,77,64,0.1)" }, fontWeight: "bold" }}
                    >
                      Concluir
                    </Button>
                  )}
                </Box>
              )}
            </Box>
            {propostaComPendenciaGarantia && podeCadastrar && !editandoPendenciaGarantia && (
              <Button
                variant="contained"
                startIcon={<FaEdit />}
                onClick={() => {
                  setEditandoPendenciaGarantia(true);
                  setAbaAtiva("proposta");
                }}
                sx={{
                  width: "auto",
                  backgroundColor: "#ed6c02",

                  "&:hover": {
                    backgroundColor: "#e65100",
                  },

                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                Editar Pendências
              </Button>
            )}

            {podeReabrirProposta && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    document.activeElement?.blur();
                    setConfirmReabrir({ aberto: true, motivo: "" });
                  }}
                  sx={{
                    width: "auto",
                    borderColor: "#e65100",
                    color: "#e65100",
                    "&:hover": { backgroundColor: "rgba(230,81,0,0.08)", borderColor: "#bf360c" },
                    fontWeight: "bold",
                  }}
                >
                  Reabrir Proposta
                </Button>
              </Box>
            )}

            <Tabs
              value={abaAtiva}
              onChange={(_, v) => setAbaAtiva(v)}
              sx={{
                mb: 3,
                borderBottom: "1px solid var(--ig-b-333333)",
                "& .MuiTab-root": { color: "var(--ig-t-aaaaaa)", textTransform: "none", fontWeight: 600 },
                "& .MuiTab-root.Mui-selected": { color: "#1998a8" },
                "& .MuiTabs-indicator": { backgroundColor: "#1998a8" },
              }}
            >
              <Tab label="Dados da Proposta" value="proposta" />
              {editandoId && <Tab label="Aprovação" value="aprovacao" />}
              {editandoId && aprovacaoInfo.motivoInutilizacao && <Tab label="Eventos" value="eventos" />}
            </Tabs>

            {abaAtiva === "proposta" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* === Código (read-only, somente em propostas existentes) === */}
                {editandoId && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                    <TextField
                      label="Código"
                      size="small"
                      inputRef={codigoInputRef}
                      value={codigoProposta}
                      slotProps={{ input: { readOnly: true } }}
                      sx={{ minWidth: 130, ...fieldSx(true) }}
                    />
                    {!emAprovacao && podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={handleEnviarAprovacao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        Enviar para Aprovação
                      </Button>
                    )}
                  </Box>
                )}

                {/* === Imóvel === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    IMÓVEL
                  </Typography>
                  <CampoSelecionar
                    label="Imóvel *"
                    inputRef={imovelInputRef}
                    valor={imovelSelecionado ? [imovelSelecionado.apelido, imovelSelecionado.endereco].filter(Boolean).join(" – ") : ""}
                    onAbrir={() => setModalImovelAberto(true)}
                    onLimpar={() => {
                      setImovelSelecionado(null);
                      setForm((p) => ({ ...p, tipoLocacao: "" }));
                    }}
                    readOnly={roImovelLocador}
                    erro={erroEfetivo("imovel", imovelSelecionado?.id)}
                    fullWidth
                    sx={{ mb: imovelSelecionado ? 2 : 0 }}
                  />

                  <ModalSelecionarImovel
                    open={modalImovelAberto}
                    onClose={() => setModalImovelAberto(false)}
                    onSelect={(im) => handleSelecionarImovel(null, im)}
                  />

                  {/* Card do imóvel selecionado */}
                  {imovelSelecionado && (
                    <Box
                      sx={{
                        backgroundColor: "var(--ig-f-252525)",
                        borderRadius: 1,
                        p: 2,
                        border: "1px solid var(--ig-b-333333)",
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: 1.5,
                      }}
                    >
                      {[
                        [
                          "Endereço",
                          [
                            imovelSelecionado.endereco,
                            imovelSelecionado.numero,
                            imovelSelecionado.complemento,
                            imovelSelecionado.bairro,
                            imovelSelecionado.cidade,
                            imovelSelecionado.uf,
                          ]
                            .filter(Boolean)
                            .join(", "),
                          true,
                        ],
                        ["Matrícula RGI", imovelSelecionado.numeroMatricula || "—"],
                        ["ÁREA TERRENO M² MATRÍCULA", formatMetragemExibicaoBR(imovelSelecionado.areaTerreno)],
                        ["ÁREA CONSTRUÍDA M² MATRÍCULA", formatMetragemExibicaoBR(imovelSelecionado.areaConstruida)],
                        ["ÁREA CONSTRUÍDA M² IPTU", formatMetragemExibicaoBR(areaConstruidaIptu)],
                        ["Proprietário", imovelSelecionado.nomeProprietarioMatricula || "—"],
                        ["Status", imovelSelecionado.status || "—"],
                      ].map(([label, valor, largo]) => (
                        <Box key={label} sx={largo ? { gridColumn: "1 / -1" } : undefined}>
                          <Typography variant="caption" sx={{ color: "var(--ig-t-888888)", display: "block" }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "var(--ig-t-ffffff)", fontWeight: 500 }}>
                            {valor}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* === Dados da Locação do Imóvel === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "var(--ig-t-aaaaaa)",
                      fontWeight: 700,
                      mb: 1.5,
                      borderBottom: "1px solid var(--ig-b-2a2a2a)",
                      pb: 0.5,
                      letterSpacing: 0.5,
                    }}
                  >
                    DADOS DA LOCAÇÃO DO IMÓVEL
                  </Typography>

                  <Grid container spacing={2}>
                    {/* =====================================================
      ÁREA LOCADA IGUAL AO IMÓVEL?
  ===================================================== */}

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        select
                        label="Área Locada igual ao Imóvel ?"
                        size="small"
                        fullWidth
                        value={form.areaLocada}
                        onChange={(e) => {
                          if (ro) return;

                          setForm((prev) => ({
                            ...prev,
                            areaLocada: e.target.value,
                          }));
                        }}
                        disabled={ro}
                        sx={fieldSx(ro, false)}
                      >
                        <MenuItem value="">Selecione</MenuItem>

                        <MenuItem value="SIM">SIM</MenuItem>

                        <MenuItem value="NÃO">NÃO</MenuItem>
                      </TextField>
                    </Grid>

                    {/* =====================================================
      COMPLEMENTO
  ===================================================== */}

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        label="Complemento"
                        size="small"
                        fullWidth
                        value={form.complementoLocacao}
                        slotProps={{
                          input: {
                            readOnly: ro,
                          },
                          htmlInput: {
                            maxLength: 255,
                          },
                        }}
                        onChange={(e) => {
                          if (ro) return;

                          setForm((prev) => ({
                            ...prev,
                            complementoLocacao: e.target.value.toUpperCase(),
                          }));
                        }}
                        sx={fieldSx(ro, false, true)}
                      />
                    </Grid>

                    {/* =====================================================
      ÁREA LOCADA - TERRENO
  ===================================================== */}

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        label="Área Locada - Terreno M²"
                        size="small"
                        fullWidth
                        value={form.areaTotalLocada}
                        slotProps={{
                          input: {
                            readOnly: ro || form.areaLocada === "SIM",
                          },
                          htmlInput: {
                            inputMode: "decimal",
                          },
                        }}
                        onChange={handleMetragemLocadaChange("areaTotalLocada")}
                        onBlur={handleMetragemLocadaBlur("areaTotalLocada")}
                        sx={fieldSx(ro || form.areaLocada === "SIM", false)}
                      />
                    </Grid>

                    {/* =====================================================
      ÁREA LOCADA - CONSTRUÇÃO
  ===================================================== */}

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        label="Área Locada - Construção M²"
                        size="small"
                        fullWidth
                        value={form.areaConstruidaTotalLocada}
                        slotProps={{
                          input: {
                            readOnly: ro,
                          },
                          htmlInput: {
                            inputMode: "decimal",
                          },
                        }}
                        onChange={handleMetragemLocadaChange("areaConstruidaTotalLocada")}
                        onBlur={handleMetragemLocadaBlur("areaConstruidaTotalLocada")}
                        sx={fieldSx(ro, false)}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* === Tipo de Locação === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "var(--ig-t-aaaaaa)",
                      fontWeight: 700,
                      mb: 1.5,
                      borderBottom: "1px solid var(--ig-b-2a2a2a)",
                      pb: 0.5,
                      letterSpacing: 0.5,
                    }}
                  >
                    TIPO DE LOCAÇÃO
                  </Typography>

                  <Grid container spacing={2}>
                    {/* RURAL / URBANO - automático do imóvel */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl size="small" fullWidth error={!!erroEfetivo("tipoLocacao", form.tipoLocacao)}>
                        <InputLabel
                          sx={{
                            color: erroEfetivo("tipoLocacao", form.tipoLocacao) ? "#f44336" : "var(--ig-t-ffffff)",
                            fontWeight: "bold",
                          }}
                        >
                          Tipo de Locação*
                        </InputLabel>

                        <Select label="Tipo de Locação*" value={form.tipoLocacao} inputProps={{ readOnly: true }} sx={selectSx(true)}>
                          <MenuItem value="">
                            <em>Selecione</em>
                          </MenuItem>

                          {TIPOS_LOCACAO.map((t) => (
                            <MenuItem key={t} value={t}>
                              {t}
                            </MenuItem>
                          ))}
                        </Select>

                        {erroEfetivo("tipoLocacao", form.tipoLocacao) && (
                          <FormHelperText sx={{ color: "#f44336" }}>{erroEfetivo("tipoLocacao", form.tipoLocacao)}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Tipo complementar do imóvel */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel
                          sx={{
                            color: "var(--ig-t-ffffff)",
                            fontWeight: "bold",
                          }}
                        >
                          Tipo do Imóvel
                        </InputLabel>

                        <Select
                          label="Tipo do Imóvel"
                          value={form.tipoImovelLocacao}
                          onChange={(e) => {
                            if (ro) return;

                            setForm((prev) => ({
                              ...prev,
                              tipoImovelLocacao: e.target.value,
                            }));
                          }}
                          inputProps={{
                            readOnly: ro,
                          }}
                          sx={selectSx(ro)}
                        >
                          <MenuItem value="">
                            <em>Selecione</em>
                          </MenuItem>

                          {TIPOS_IMOVEL_LOCACAO.map((tipo) => (
                            <MenuItem key={tipo} value={tipo}>
                              {tipo}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {/* === Locadoras === */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid var(--ig-b-2a2a2a)",
                      pb: 0.5,
                      mb: 0,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "var(--ig-t-aaaaaa)",
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      LOCADORAS
                    </Typography>

                    {!roImovelLocador && podeCadastrar && (
                      <Button
                        type="button"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setLocadorSelecionado(null);
                          setEmailNovoLocador("");
                          setModalProprietarioAberto(true);
                        }}
                        sx={{
                          backgroundColor: "#1998a8",
                          color: "#fff",
                          minHeight: 32,
                          height: 32,
                          px: 1.5,
                          width: "auto",
                          textTransform: "none",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",

                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        }}
                      >
                        Adicionar Locadora
                      </Button>
                    )}
                  </Box>

                  <SublistaFichas
                    fichas={locadoras}
                    setFichas={setLocadoras}
                    somenteLeitura={roImovelLocador}
                    podeCadastrar={false}
                    permitirEditarEmail
                    permitirRemover
                    usarBuscaProprietario
                    proprietariosBusca={proprietariosLocador}
                    titulo=""
                    mostrarSnack={mostrarSnack}
                  />

                  <ModalSelecionarProprietario
                    open={modalProprietarioAberto}
                    onClose={() => setModalProprietarioAberto(false)}
                    proprietarios={proprietariosLocador}
                    onSelect={(proprietario) => {
                      handleAdicionarLocador(proprietario);
                    }}
                  />
                </Box>

                {/* === Locatárias === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 0, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    LOCATÁRIAS*
                  </Typography>
                  {(errosForm.locatarias || (somenteLeitura && !locatarias.filter((l) => l.razaoSocial?.trim()).length)) && (
                    <Typography sx={{ color: "#f44336", fontSize: "0.75rem", mt: 0.5 }}>
                      {errosForm.locatarias || "Ao menos uma Locatária é obrigatória."}
                    </Typography>
                  )}
                  <SublistaFichas
                    fichas={locatarias}
                    setFichas={setLocatarias}
                    somenteLeitura={roImovelLocador}
                    podeCadastrar={podeCadastrar}
                    titulo=""
                    mostrarSnack={mostrarSnack}
                  />
                </Box>

                {/* === Prazo e Datas === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    PRAZO E DATAS
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label="Prazo (meses)*"
                        size="small"
                        type="number"
                        fullWidth
                        sx={fieldSx(ro, erroEfetivo("prazo", form.prazo))}
                        value={form.prazo}
                        error={!!erroEfetivo("prazo", form.prazo)}
                        helperText={erroEfetivo("prazo", form.prazo) || ""}
                        slotProps={{ input: { readOnly: ro } }}
                        onChange={(e) => {
                          if (!ro) {
                            const prazo = e.target.value;
                            setForm((p) => {
                              const novaDataFim = calcularDataFim(p.dataInicio, prazo) || p.dataFim;
                              return { ...p, prazo, dataFim: novaDataFim };
                            });
                            setErrosForm((p) => ({ ...p, prazo: undefined, dataFim: undefined }));
                          }
                        }}
                        onBlur={() => {
                          if (!ro && !form.prazo) setErrosForm((p) => ({ ...p, prazo: "Prazo é obrigatório." }));
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label="Data Início*"
                        size="small"
                        type="date"
                        fullWidth
                        sx={fieldSx(ro, erroEfetivo("dataInicio", form.dataInicio))}
                        value={form.dataInicio}
                        error={!!erroEfetivo("dataInicio", form.dataInicio)}
                        helperText={erroEfetivo("dataInicio", form.dataInicio) || ""}
                        slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                        onChange={(e) => {
                          if (ro) return;

                          const dataInicio = limitarAnoData(e.target.value);

                          setForm((p) => {
                            const novaDataFim = calcularDataFim(dataInicio, p.prazo) || p.dataFim;

                            const next = {
                              ...p,
                              dataInicio,
                              dataFim: novaDataFim,
                            };

                            // ALUGUEL
                            next.primeiroVencimento =
                              p.tipoPrimeiroVencimento === "IMEDIATO" ? calcularSugestaoPrimeiroVencimento(dataInicio, p.tipoVencimento, p.diaVencimento) : "";

                            // IPTU
                            if (p.iptuTipoVencimento && p.iptuDiaVencimento) {
                              next.iptuPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(dataInicio, p.iptuTipoVencimento, p.iptuDiaVencimento);
                            }

                            // CONDOMÍNIO
                            if (p.condominioTipoVencimento && p.condominioDiaVencimento) {
                              next.condominioPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(
                                dataInicio,
                                p.condominioTipoVencimento,
                                p.condominioDiaVencimento,
                              );
                            }

                            // ÁGUA E ESGOTO
                            if (p.aguaEsgotoTipoVencimento && p.aguaEsgotoDiaVencimento) {
                              next.aguaEsgotoPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(
                                dataInicio,
                                p.aguaEsgotoTipoVencimento,
                                p.aguaEsgotoDiaVencimento,
                              );
                            }

                            // ENERGIA
                            if (p.energiaTipoVencimento && p.energiaDiaVencimento) {
                              next.energiaPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(dataInicio, p.energiaTipoVencimento, p.energiaDiaVencimento);
                            }

                            // INTERNET
                            if (p.internetTipoVencimento && p.internetDiaVencimento) {
                              next.internetPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(
                                dataInicio,
                                p.internetTipoVencimento,
                                p.internetDiaVencimento,
                              );
                            }

                            // TOTEM
                            if (p.totemTipoVencimento && p.totemDiaVencimento) {
                              next.totemPrimeiroVencimento = calcularSugestaoPrimeiroVencimento(dataInicio, p.totemTipoVencimento, p.totemDiaVencimento);
                            }

                            return next;
                          });

                          setErrosForm((p) => ({
                            ...p,
                            dataInicio: undefined,
                            dataFim: undefined,
                          }));
                        }}
                        onBlur={() => {
                          if (!ro && !form.dataInicio) setErrosForm((p) => ({ ...p, dataInicio: "Data de Início é obrigatória." }));
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label="Data Fim*"
                        size="small"
                        type="date"
                        fullWidth
                        sx={fieldSx(true, erroEfetivo("dataFim", form.dataFim))}
                        value={form.dataFim}
                        error={!!erroEfetivo("dataFim", form.dataFim)}
                        helperText={erroEfetivo("dataFim", form.dataFim) || "Calculada automaticamente a partir do Prazo e da Data Início."}
                        slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* === Valores === */}
                <Box
                  sx={{
                    "& .MuiInputLabel-root": {
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "clip",
                      maxWidth: "none",
                      fontSize: "0.82rem",
                    },

                    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "clip",
                      maxWidth: "none",
                    },

                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "var(--ig-t-aaaaaa)",
                      fontWeight: 700,
                      mb: 1.5,
                      borderBottom: "1px solid var(--ig-b-2a2a2a)",
                      pb: 0.5,
                      letterSpacing: 0.5,
                    }}
                  >
                    VALORES (R$)
                  </Typography>

                  {/* =========================================================
      ALUGUEL
      ========================================================= */}
                  <Box
                    sx={{
                      display: "grid",

                      gridTemplateColumns: {
                        xs: "1fr",

                        sm: "repeat(2, minmax(190px, 1fr))",

                        lg: "repeat(3, minmax(190px, 1fr))",

                        xl: `
    minmax(180px, 1.05fr)
    minmax(190px, 1.10fr)
    minmax(190px, 1.10fr)
    minmax(180px, 1.05fr)
    minmax(240px, 1.50fr)
    minmax(220px, 1.35fr)
  `,
                      },

                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="Aluguel Mensal*"
                      size="small"
                      fullWidth
                      value={form.vAluguelMensal}
                      sx={fieldSx(ro, erroEfetivo("vAluguelMensal", form.vAluguelMensal))}
                      error={!!erroEfetivo("vAluguelMensal", form.vAluguelMensal)}
                      helperText={erroEfetivo("vAluguelMensal", form.vAluguelMensal) || ""}
                      slotProps={{
                        input: {
                          readOnly: ro,
                        },
                      }}
                      onChange={(e) => {
                        onChangeValor("vAluguelMensal", ro, setForm)(e);

                        setErrosForm((prev) => ({
                          ...prev,
                          vAluguelMensal: undefined,
                        }));
                      }}
                    />

                    <FormControl size="small" fullWidth error={!!erroEfetivo("tipoVencimento", form.tipoVencimento)}>
                      <InputLabel
                        sx={{
                          color: erroEfetivo("tipoVencimento", form.tipoVencimento) ? "#f44336" : "var(--ig-t-ffffff)",
                          fontWeight: "bold",
                        }}
                      >
                        Tipo Vencimento*
                      </InputLabel>

                      <Select
                        label="Tipo Vencimento*"
                        value={form.tipoVencimento}
                        inputProps={{
                          readOnly: ro,
                        }}
                        sx={selectSx(ro)}
                        onChange={(e) => {
                          if (ro) return;

                          const tipoVencimento = e.target.value;

                          setForm((prev) => ({
                            ...prev,

                            tipoVencimento,

                            primeiroVencimento:
                              prev.tipoPrimeiroVencimento === "IMEDIATO"
                                ? calcularSugestaoPrimeiroVencimento(prev.dataInicio, tipoVencimento, prev.diaVencimento)
                                : "",
                          }));

                          setErrosForm((prev) => ({
                            ...prev,
                            tipoVencimento: undefined,
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>

                        <MenuItem value="Pós">PÓS</MenuItem>

                        <MenuItem value="Corrente">CORRENTE</MenuItem>
                      </Select>

                      {erroEfetivo("tipoVencimento", form.tipoVencimento) && (
                        <FormHelperText>{erroEfetivo("tipoVencimento", form.tipoVencimento)}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl size="small" fullWidth error={!!erroEfetivo("diaVencimento", form.diaVencimento)}>
                      <InputLabel
                        sx={{
                          color: erroEfetivo("diaVencimento", form.diaVencimento) ? "#f44336" : "var(--ig-t-ffffff)",

                          fontWeight: "bold",
                        }}
                      >
                        Dia Vencimento*
                      </InputLabel>

                      <Select
                        label="Dia Vencimento*"
                        value={form.diaVencimento}
                        inputProps={{
                          readOnly: ro,
                        }}
                        sx={selectSx(ro)}
                        onChange={(e) => {
                          if (ro) return;

                          const diaVencimento = e.target.value;

                          setForm((prev) => ({
                            ...prev,

                            diaVencimento,

                            primeiroVencimento:
                              prev.tipoPrimeiroVencimento === "IMEDIATO"
                                ? calcularSugestaoPrimeiroVencimento(prev.dataInicio, prev.tipoVencimento, diaVencimento)
                                : "",
                          }));

                          setErrosForm((prev) => ({
                            ...prev,
                            diaVencimento: undefined,
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>

                        {Array.from(
                          {
                            length: 31,
                          },
                          (_, i) => i + 1,
                        ).map((dia) => (
                          <MenuItem key={dia} value={dia}>
                            {dia}
                          </MenuItem>
                        ))}
                      </Select>

                      {erroEfetivo("diaVencimento", form.diaVencimento) && <FormHelperText>{erroEfetivo("diaVencimento", form.diaVencimento)}</FormHelperText>}
                    </FormControl>

                    <FormControl size="small" fullWidth error={!!erroEfetivo("mesReajuste", form.mesReajuste)}>
                      <InputLabel
                        sx={{
                          color: erroEfetivo("mesReajuste", form.mesReajuste) ? "#f44336" : "var(--ig-t-ffffff)",
                          fontWeight: "bold",
                        }}
                      >
                        Mês Reajuste*
                      </InputLabel>

                      <Select
                        label="Mês Reajuste*"
                        value={form.mesReajuste}
                        inputProps={{
                          readOnly: ro,
                        }}
                        sx={selectSx(ro)}
                        onChange={(e) => {
                          if (ro) return;

                          setForm((prev) => ({
                            ...prev,
                            mesReajuste: e.target.value,
                          }));

                          setErrosForm((prev) => ({
                            ...prev,
                            mesReajuste: undefined,
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>

                        {NOMES_MESES.map((nome, index) => {
                          const mes = String(index + 1).padStart(2, "0");

                          return (
                            <MenuItem key={mes} value={mes}>
                              {nome}
                            </MenuItem>
                          );
                        })}
                      </Select>

                      {erroEfetivo("mesReajuste", form.mesReajuste) && <FormHelperText>{erroEfetivo("mesReajuste", form.mesReajuste)}</FormHelperText>}
                    </FormControl>

                    <FormControl size="small" fullWidth error={!!erroEfetivo("indiceReajusteId", form.indiceReajusteId)}>
                      <InputLabel
                        sx={{
                          color: erroEfetivo("indiceReajusteId", form.indiceReajusteId) ? "#f44336" : "var(--ig-t-ffffff)",
                          fontWeight: "bold",
                        }}
                      >
                        Índice Reajuste*
                      </InputLabel>

                      <Select
                        label="Índice Reajuste*"
                        value={form.indiceReajusteId}
                        inputProps={{
                          readOnly: ro,
                        }}
                        sx={selectSx(ro)}
                        onChange={(e) => {
                          if (ro) return;

                          setForm((prev) => ({
                            ...prev,
                            indiceReajusteId: e.target.value,
                          }));

                          setErrosForm((prev) => ({
                            ...prev,
                            indiceReajusteId: undefined,
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>

                        {indicesReajuste.map((indice) => (
                          <MenuItem key={indice.id} value={indice.id}>
                            {indice.descricao}
                          </MenuItem>
                        ))}
                      </Select>

                      {erroEfetivo("indiceReajusteId", form.indiceReajusteId) && (
                        <FormHelperText>{erroEfetivo("indiceReajusteId", form.indiceReajusteId)}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl size="small" fullWidth>
                      <InputLabel
                        sx={{
                          color: "var(--ig-t-ffffff)",
                          fontWeight: "bold",
                        }}
                      >
                        Tipo 1º Vencimento
                      </InputLabel>

                      <Select
                        label="Tipo 1º Vencimento"
                        value={form.tipoPrimeiroVencimento}
                        inputProps={{
                          readOnly: ro,
                        }}
                        sx={selectSx(ro)}
                        onChange={(e) => {
                          if (ro) return;

                          const tipoPrimeiroVencimento = e.target.value;

                          setForm((prev) => ({
                            ...prev,

                            tipoPrimeiroVencimento,

                            primeiroVencimento:
                              tipoPrimeiroVencimento === "IMEDIATO"
                                ? calcularSugestaoPrimeiroVencimento(prev.dataInicio, prev.tipoVencimento, prev.diaVencimento)
                                : "",
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>

                        {TIPOS_PRIMEIRO_VENCIMENTO.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {tipo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* =========================================================
      CARÊNCIA + DESCRIÇÃO + 1º VENCIMENTO
      ========================================================= */}
                  <Box
                    sx={{
                      display: "grid",

                      gridTemplateColumns: {
                        xs: "1fr",

                        sm: "1fr",

                        md: `
          minmax(260px, 1.3fr)
          minmax(420px, 4fr)
          minmax(230px, 1.6fr)
        `,
                      },

                      gap: 2,
                      mb: 2,
                    }}
                  >
                    {/* CARÊNCIA */}
                    <TextField
                      label="Carência do Aluguel em Dias"
                      size="small"
                      type="number"
                      fullWidth
                      value={form.carencia}
                      sx={fieldSx(ro, false)}
                      slotProps={{
                        input: {
                          readOnly: ro,
                        },
                        htmlInput: {
                          maxLength: 5,
                        },
                      }}
                      onChange={(e) => {
                        if (ro) return;

                        setForm((prev) => ({
                          ...prev,
                          carencia: e.target.value.slice(0, 5),
                        }));
                      }}
                    />

                    {/* DESCRIÇÃO DO DESCONTO */}
                    <TextField
                      label={`Descrição do Desconto do Aluguel${form.vDesconto ? "*" : ""}`}
                      size="small"
                      fullWidth
                      value={form.descrDesconto}
                      sx={fieldSx(ro, errosForm.descrDesconto, true)}
                      error={!!errosForm.descrDesconto}
                      helperText={errosForm.descrDesconto || ""}
                      slotProps={{
                        input: {
                          readOnly: ro,
                        },
                        htmlInput: {
                          maxLength: 255,
                        },
                      }}
                      onChange={(e) => {
                        if (ro) return;

                        setForm((prev) => ({
                          ...prev,
                          descrDesconto: e.target.value.toUpperCase(),
                        }));

                        setErrosForm((prev) => ({
                          ...prev,
                          descrDesconto: undefined,
                        }));
                      }}
                    />

                    {/* 1º VENCIMENTO */}
                    {form.tipoPrimeiroVencimento === "NÃO IMEDIATO" ? (
                      <TextField
                        label="1º Vencimento"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={textoPrimeiroVencimentoNaoImediato(form.tipoVencimento, form.diaVencimento)}
                        slotProps={{
                          input: {
                            readOnly: true,
                          },
                        }}
                        sx={fieldSx(true, false)}
                      />
                    ) : (
                      <TextField
                        label="1º Vencimento"
                        size="small"
                        fullWidth
                        type="date"
                        value={form.primeiroVencimento || ""}
                        disabled={ro}
                        sx={fieldSx(ro, false)}
                        slotProps={{
                          input: {
                            readOnly: ro,
                          },

                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        onChange={(e) => {
                          if (ro) return;

                          setForm((prev) => ({
                            ...prev,

                            primeiroVencimento: limitarAnoData(e.target.value),
                          }));
                        }}
                      />
                    )}
                  </Box>

                  {/* =========================================================
    IPTU / CONDOMÍNIO
    ========================================================= */}
                  {linhasAplicaveisValores
                    .filter((linha) => ["iptu", "condominio"].includes(linha.chave))
                    .map((linha) => {
                      const bloqueado = ro || form[linha.aplicabilidade] !== "APLICÁVEL";

                      return (
                        <Box
                          key={linha.chave}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              md: "repeat(3, minmax(0, 1fr))",
                              lg: "repeat(6, minmax(0, 1fr))",
                            },
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          {/* APLICÁVEL / NÃO APLICÁVEL */}
                          <FormControl size="small" fullWidth disabled={ro}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              {linha.labelAplicabilidade}
                            </InputLabel>

                            <Select
                              label={linha.labelAplicabilidade}
                              value={form[linha.aplicabilidade]}
                              sx={selectSx(ro)}
                              onChange={(e) => {
                                if (ro) return;

                                const valor = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.aplicabilidade]: valor,

                                  ...(valor !== "APLICÁVEL"
                                    ? {
                                        [linha.valor]: "",
                                        [linha.tipo]: "",
                                        [linha.dia]: "",
                                        [linha.mes]: "",
                                        [linha.primeiro]: "",
                                      }
                                    : {}),
                                }));

                                setErrosForm((prev) => ({
                                  ...prev,
                                  [linha.valor]: undefined,
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {OPCOES_APLICABILIDADE.map((opcao) => (
                                <MenuItem key={opcao} value={opcao}>
                                  {opcao}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* VALOR */}
                          <TextField
                            label={linha.labelValor}
                            size="small"
                            fullWidth
                            value={form[linha.valor]}
                            disabled={bloqueado}
                            sx={fieldSx(bloqueado, errosForm[linha.valor])}
                            error={!!errosForm[linha.valor]}
                            helperText={errosForm[linha.valor] || ""}
                            slotProps={{
                              input: {
                                readOnly: bloqueado,
                              },
                            }}
                            onChange={(e) => {
                              onChangeValor(linha.valor, bloqueado, setForm)(e);

                              setErrosForm((prev) => ({
                                ...prev,
                                [linha.valor]: undefined,
                              }));
                            }}
                          />

                          {/* TIPO VENCIMENTO */}
                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Tipo Vencimento
                            </InputLabel>

                            <Select
                              label="Tipo Vencimento"
                              value={form[linha.tipo]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) return;

                                const tipoVencimento = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.tipo]: tipoVencimento,

                                  [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, tipoVencimento, prev[linha.dia]),
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              <MenuItem value="Pós">PÓS</MenuItem>

                              <MenuItem value="Corrente">CORRENTE</MenuItem>
                            </Select>
                          </FormControl>

                          {/* DIA VENCIMENTO */}
                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Dia Vencimento
                            </InputLabel>

                            <Select
                              label="Dia Vencimento"
                              value={form[linha.dia]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) return;

                                const diaVencimento = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.dia]: diaVencimento,

                                  [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, prev[linha.tipo], diaVencimento),
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {Array.from(
                                {
                                  length: 31,
                                },
                                (_, i) => i + 1,
                              ).map((dia) => (
                                <MenuItem key={dia} value={dia}>
                                  {dia}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* MÊS REAJUSTE */}
                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Mês Reajuste
                            </InputLabel>

                            <Select
                              label="Mês Reajuste"
                              value={form[linha.mes]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) {
                                  return;
                                }

                                setForm((prev) => ({
                                  ...prev,
                                  [linha.mes]: e.target.value,
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {NOMES_MESES.map((nome, index) => {
                                const mes = String(index + 1).padStart(2, "0");

                                return (
                                  <MenuItem key={mes} value={mes}>
                                    {nome}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {/* 1º VENCIMENTO */}
                          <TextField
                            label="1º Vencimento"
                            size="small"
                            fullWidth
                            type="date"
                            value={form[linha.primeiro]}
                            disabled={bloqueado}
                            sx={fieldSx(bloqueado, false)}
                            slotProps={{
                              input: {
                                readOnly: bloqueado,
                              },
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            onChange={(e) => {
                              if (bloqueado) {
                                return;
                              }

                              setForm((prev) => ({
                                ...prev,

                                [linha.primeiro]: limitarAnoData(e.target.value),
                              }));
                            }}
                          />
                        </Box>
                      );
                    })}

                  {/* =========================================================
      ÁGUA E ESGOTO / ENERGIA
      ========================================================= */}
                  {linhasMedicaoValores.map((linha) => {
                    const medicaoSelecionada = form[linha.medicao];

                    const bloqueadoPorMedicao = medicaoBloqueiaDemaisCampos(linha.chave, medicaoSelecionada);

                    const bloqueado = ro || !medicaoSelecionada || bloqueadoPorMedicao;

                    return (
                      <Box
                        key={linha.chave}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                            lg: "repeat(5, minmax(0, 1fr))",
                          },
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        {/* TIPO MEDIÇÃO */}
                        <FormControl size="small" fullWidth disabled={ro}>
                          <InputLabel
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              fontWeight: "bold",
                            }}
                          >
                            {linha.labelMedicao}
                          </InputLabel>

                          <Select
                            label={linha.labelMedicao}
                            value={form[linha.medicao]}
                            sx={selectSx(ro)}
                            onChange={(e) => {
                              if (ro) return;

                              const valor = e.target.value;

                              const bloquearDemais = medicaoBloqueiaDemaisCampos(linha.chave, valor);

                              setForm((prev) => ({
                                ...prev,

                                [linha.medicao]: valor,

                                ...(!valor || bloquearDemais
                                  ? {
                                      [linha.valor]: "",
                                      [linha.tipo]: "",
                                      [linha.dia]: "",
                                      [linha.primeiro]: "",
                                    }
                                  : {}),
                              }));

                              setErrosForm((prev) => ({
                                ...prev,
                                [linha.valor]: undefined,
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>Selecione</em>
                            </MenuItem>

                            {linha.opcoes.map((opcao) => (
                              <MenuItem key={opcao} value={opcao}>
                                {opcao}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* VALOR */}
                        <TextField
                          label={linha.labelValor}
                          size="small"
                          fullWidth
                          value={form[linha.valor]}
                          disabled={bloqueado}
                          sx={fieldSx(bloqueado, errosForm[linha.valor])}
                          error={!!errosForm[linha.valor]}
                          helperText={errosForm[linha.valor] || ""}
                          slotProps={{
                            input: {
                              readOnly: bloqueado,
                            },
                          }}
                          onChange={(e) => {
                            onChangeValor(linha.valor, bloqueado, setForm)(e);

                            setErrosForm((prev) => ({
                              ...prev,
                              [linha.valor]: undefined,
                            }));
                          }}
                        />

                        {/* TIPO VENCIMENTO */}
                        <FormControl size="small" fullWidth disabled={bloqueado}>
                          <InputLabel
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              fontWeight: "bold",
                            }}
                          >
                            Tipo Vencimento
                          </InputLabel>

                          <Select
                            label="Tipo Vencimento"
                            value={form[linha.tipo]}
                            sx={selectSx(bloqueado)}
                            onChange={(e) => {
                              if (bloqueado) return;

                              const tipoVencimento = e.target.value;

                              setForm((prev) => ({
                                ...prev,

                                [linha.tipo]: tipoVencimento,

                                [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, tipoVencimento, prev[linha.dia]),
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>Selecione</em>
                            </MenuItem>

                            <MenuItem value="Pós">PÓS</MenuItem>

                            <MenuItem value="Corrente">CORRENTE</MenuItem>
                          </Select>
                        </FormControl>

                        {/* DIA VENCIMENTO */}
                        <FormControl size="small" fullWidth disabled={bloqueado}>
                          <InputLabel
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              fontWeight: "bold",
                            }}
                          >
                            Dia Vencimento
                          </InputLabel>

                          <Select
                            label="Dia Vencimento"
                            value={form[linha.dia]}
                            sx={selectSx(bloqueado)}
                            onChange={(e) => {
                              if (bloqueado) return;

                              const diaVencimento = e.target.value;

                              setForm((prev) => ({
                                ...prev,

                                [linha.dia]: diaVencimento,

                                [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, prev[linha.tipo], diaVencimento),
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>Selecione</em>
                            </MenuItem>

                            {Array.from(
                              {
                                length: 31,
                              },
                              (_, i) => i + 1,
                            ).map((dia) => (
                              <MenuItem key={dia} value={dia}>
                                {dia}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* 1º VENCIMENTO */}
                        <TextField
                          label="1º Vencimento"
                          size="small"
                          fullWidth
                          type="date"
                          value={form[linha.primeiro]}
                          disabled={bloqueado}
                          sx={fieldSx(bloqueado, false)}
                          slotProps={{
                            input: {
                              readOnly: bloqueado,
                            },

                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          onChange={(e) => {
                            if (bloqueado) {
                              return;
                            }

                            setForm((prev) => ({
                              ...prev,

                              [linha.primeiro]: limitarAnoData(e.target.value),
                            }));
                          }}
                        />
                      </Box>
                    );
                  })}

                  {/* =========================================================
      INTERNET / TOTEM
      ========================================================= */}
                  {linhasAplicaveisValores
                    .filter((linha) => ["internet", "totem"].includes(linha.chave))
                    .map((linha) => {
                      const bloqueado = ro || form[linha.aplicabilidade] !== "APLICÁVEL";

                      return (
                        <Box
                          key={linha.chave}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              md: "repeat(3, minmax(0, 1fr))",
                              lg: "repeat(7, minmax(0, 1fr))",
                            },
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <FormControl size="small" fullWidth disabled={ro}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              {linha.labelAplicabilidade}
                            </InputLabel>

                            <Select
                              label={linha.labelAplicabilidade}
                              value={form[linha.aplicabilidade]}
                              sx={selectSx(ro)}
                              onChange={(e) => {
                                if (ro) return;

                                const valor = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.aplicabilidade]: valor,

                                  ...(valor !== "APLICÁVEL"
                                    ? {
                                        [linha.valor]: "",
                                        [linha.tipo]: "",
                                        [linha.dia]: "",
                                        [linha.mes]: "",
                                        ...(linha.indice
                                          ? {
                                              [linha.indice]: "",
                                            }
                                          : {}),
                                        [linha.primeiro]: "",
                                      }
                                    : {}),
                                }));

                                setErrosForm((prev) => ({
                                  ...prev,
                                  [linha.valor]: undefined,
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {OPCOES_APLICABILIDADE.map((opcao) => (
                                <MenuItem key={opcao} value={opcao}>
                                  {opcao}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <TextField
                            label={linha.labelValor}
                            size="small"
                            fullWidth
                            value={form[linha.valor]}
                            disabled={bloqueado}
                            sx={fieldSx(bloqueado, errosForm[linha.valor])}
                            error={!!errosForm[linha.valor]}
                            helperText={errosForm[linha.valor] || ""}
                            slotProps={{
                              input: {
                                readOnly: bloqueado,
                              },
                            }}
                            onChange={(e) => {
                              onChangeValor(linha.valor, bloqueado, setForm)(e);

                              setErrosForm((prev) => ({
                                ...prev,
                                [linha.valor]: undefined,
                              }));
                            }}
                          />

                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Tipo Vencimento
                            </InputLabel>

                            <Select
                              label="Tipo Vencimento"
                              value={form[linha.tipo]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) return;

                                const tipoVencimento = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.tipo]: tipoVencimento,

                                  [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, tipoVencimento, prev[linha.dia]),
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              <MenuItem value="Pós">PÓS</MenuItem>

                              <MenuItem value="Corrente">CORRENTE</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Dia Vencimento
                            </InputLabel>

                            <Select
                              label="Dia Vencimento"
                              value={form[linha.dia]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) return;

                                const diaVencimento = e.target.value;

                                setForm((prev) => ({
                                  ...prev,

                                  [linha.dia]: diaVencimento,

                                  [linha.primeiro]: calcularSugestaoPrimeiroVencimento(prev.dataInicio, prev[linha.tipo], diaVencimento),
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                                <MenuItem key={dia} value={dia}>
                                  {dia}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* MÊS REAJUSTE */}
                          <FormControl size="small" fullWidth disabled={bloqueado}>
                            <InputLabel
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "bold",
                              }}
                            >
                              Mês Reajuste
                            </InputLabel>

                            <Select
                              label="Mês Reajuste"
                              value={form[linha.mes]}
                              sx={selectSx(bloqueado)}
                              onChange={(e) => {
                                if (bloqueado) return;

                                setForm((prev) => ({
                                  ...prev,
                                  [linha.mes]: e.target.value,
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Selecione</em>
                              </MenuItem>

                              {NOMES_MESES.map((nome, index) => {
                                const mes = String(index + 1).padStart(2, "0");

                                return (
                                  <MenuItem key={mes} value={mes}>
                                    {nome}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {/* 1º VENCIMENTO */}
                          <TextField
                            label="1º Vencimento"
                            size="small"
                            fullWidth
                            type="date"
                            value={form[linha.primeiro]}
                            disabled={bloqueado}
                            sx={fieldSx(bloqueado, false)}
                            slotProps={{
                              input: {
                                readOnly: bloqueado,
                              },

                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            onChange={(e) => {
                              if (bloqueado) return;

                              setForm((prev) => ({
                                ...prev,

                                [linha.primeiro]: limitarAnoData(e.target.value),
                              }));
                            }}
                          />

                          {/* ÍNDICE REAJUSTE */}
                          {linha.indice && (
                            <FormControl size="small" fullWidth disabled={bloqueado}>
                              <InputLabel
                                sx={{
                                  color: "var(--ig-t-ffffff)",
                                  fontWeight: "bold",
                                }}
                              >
                                Índice Reajuste
                              </InputLabel>

                              <Select
                                label="Índice Reajuste"
                                value={form[linha.indice]}
                                sx={selectSx(bloqueado)}
                                onChange={(e) => {
                                  if (bloqueado) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    [linha.indice]: e.target.value,
                                  }));
                                }}
                              >
                                <MenuItem value="">
                                  <em>Selecione</em>
                                </MenuItem>

                                {indicesReajuste.map((indice) => (
                                  <MenuItem key={indice.id} value={indice.id}>
                                    {indice.descricao}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      );
                    })}
                </Box>

                {/* === Boleto (antes da garantia) === */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    BOLETO
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        select
                        label="Boleto"
                        size="small"
                        fullWidth
                        value={form.boleto || ""}
                        sx={fieldSx(ro, erroEfetivo("boleto", form.boleto))}
                        slotProps={{ input: { readOnly: ro } }}
                        onChange={(e) => {
                          if (ro) return;
                          const valor = e.target.value;
                          // "Não" limpa quantidade e valor.
                          setForm((p) => ({ ...p, boleto: valor, ...(valor === "SIM" ? {} : { boletoQuantidade: "", vBoleto: "" }) }));
                          setErrosForm((p) => ({ ...p, boleto: undefined, boletoQuantidade: undefined, vBoleto: undefined }));
                        }}
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="SIM">Sim</MenuItem>
                        <MenuItem value="NÃO">Não</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label={form.boleto === "SIM" ? "Quantidade*" : "Quantidade"}
                        size="small"
                        fullWidth
                        value={form.boletoQuantidade ?? ""}
                        sx={fieldSx(ro || form.boleto !== "SIM", erroEfetivo("boletoQuantidade", form.boletoQuantidade))}
                        error={form.boleto === "SIM" && !!erroEfetivo("boletoQuantidade", form.boletoQuantidade)}
                        helperText={form.boleto === "SIM" ? erroEfetivo("boletoQuantidade", form.boletoQuantidade) || "" : ""}
                        slotProps={{ input: { readOnly: ro || form.boleto !== "SIM" }, htmlInput: { inputMode: "numeric", maxLength: 4 } }}
                        onChange={(e) => {
                          if (ro || form.boleto !== "SIM") return;
                          const digitos = e.target.value.replace(/\D/g, "").slice(0, 4);
                          setForm((p) => ({ ...p, boletoQuantidade: digitos }));
                          setErrosForm((p) => ({ ...p, boletoQuantidade: undefined }));
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label={form.boleto === "SIM" ? "Valor*" : "Valor"}
                        size="small"
                        fullWidth
                        value={form.vBoleto ?? ""}
                        sx={fieldSx(ro || form.boleto !== "SIM", erroEfetivo("vBoleto", form.vBoleto))}
                        error={form.boleto === "SIM" && !!erroEfetivo("vBoleto", form.vBoleto)}
                        helperText={form.boleto === "SIM" ? erroEfetivo("vBoleto", form.vBoleto) || "" : ""}
                        slotProps={{
                          input: {
                            readOnly: ro || form.boleto !== "SIM",
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          },
                          htmlInput: { inputMode: "numeric" },
                        }}
                        onChange={(e) => {
                          if (form.boleto !== "SIM") return;
                          onChangeValor("vBoleto", ro, setForm)(e);
                          setErrosForm((p) => ({ ...p, vBoleto: undefined }));
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* === Garantia === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    GARANTIA*
                  </Typography>
                  <RadioGroup
                    row
                    value={form.garantia || ""}
                    sx={{ mb: 1, gap: 2 }}
                    onChange={(e) => {
                      if (roGarantia) return;
                      const anterior = form.garantia;
                      const novo = e.target.value;
                      setForm((p) => {
                        const next = { ...p, garantia: novo };
                        if (anterior === "Caução" && novo !== "Caução") {
                          next.vCaucao = "";
                          next.qtdeAluguelCaucao = "";
                        }
                        if (anterior === "Imobiliária" && novo !== "Imobiliária") {
                          next.garantiaDescricaoImovel = "";
                          next.garantiaCadastroMunicipal = "";
                          next.garantiaSetor = "";
                          next.garantiaQuarteirao = "";

                          next.garantiaTipoImovel = "";
                          next.garantiaRgi = "";
                          next.garantiaMatricula = "";
                          next.garantiaCartorio = "";
                          next.garantiaLivro = "";
                          next.garantiaFolha = "";
                          next.garantiaEndereco = "";
                          next.garantiaNumero = "";
                          next.garantiaBairro = "";
                          next.garantiaCidade = "";
                          next.garantiaUf = "";
                          next.garantiaCep = "";
                          next.garantiaComplemento = "";
                          next.garantiaUrlGoogleMaps = "";
                        }
                        return next;
                      });
                      if (anterior === "Fiador" && novo !== "Fiador") {
                        setFiador({
                          ...fichaVazia,
                          tipoPessoa: "PF",
                        });

                        setForm((prev) => ({
                          ...prev,

                          fiadorTipoGarantia: "",

                          fiadorImovelTipo: "",
                          fiadorImovelCidadeCartorioRgi: "",
                          fiadorImovelNomeCartorioRgi: "",
                          fiadorImovelRgi: "",
                          fiadorImovelMatricula: "",
                          fiadorImovelCartorio: "",
                          fiadorImovelLivro: "",
                          fiadorImovelFolha: "",
                          fiadorImovelEndereco: "",
                          fiadorImovelNumero: "",
                          fiadorImovelBairro: "",
                          fiadorImovelCidade: "",
                          fiadorImovelUf: "",
                          fiadorImovelCep: "",
                          fiadorImovelComplemento: "",
                          fiadorImovelUrlGoogleMaps: "",
                        }));
                      }
                      if (anterior === "Seguro Fiança" && novo !== "Seguro Fiança") {
                        setSeguradora({
                          ...fichaVazia,
                          tipoPessoa: "PJ",
                        });

                        setCorretorSeguro({
                          ...fichaVazia,
                          tipoPessoa: "PF",
                        });
                      }
                      if (anterior === "Fiador" && novo !== "Fiador") {
                        setFiador({
                          ...fichaVazia,
                          tipoPessoa: "PF",
                        });

                        setForm((prev) => ({
                          ...prev,

                          fiadorTipoGarantia: "",

                          fiadorImovelTipo: "",
                          fiadorImovelCidadeCartorioRgi: "",
                          fiadorImovelNomeCartorioRgi: "",
                          fiadorImovelRgi: "",
                          fiadorImovelMatricula: "",
                          fiadorImovelCartorio: "",
                          fiadorImovelLivro: "",
                          fiadorImovelFolha: "",
                          fiadorImovelEndereco: "",
                          fiadorImovelNumero: "",
                          fiadorImovelBairro: "",
                          fiadorImovelCidade: "",
                          fiadorImovelUf: "",
                          fiadorImovelCep: "",
                          fiadorImovelComplemento: "",
                          fiadorImovelUrlGoogleMaps: "",
                        }));
                      }

                      if (anterior === "Seguro Fiança" && novo !== "Seguro Fiança") {
                        setSeguradora({
                          ...fichaVazia,
                          tipoPessoa: "PJ",
                        });

                        setCorretorSeguro({
                          ...fichaVazia,
                          tipoPessoa: "PF",
                        });
                      }

                      if (anterior === "Imobiliária" && novo !== "Imobiliária") {
                        setCaucionante({
                          ...fichaVazia,
                          tipoPessoa: "PJ",
                        });

                        setGarantiaDocsPendentes([]);
                        setArquivoGarantia(null);
                      }

                      setErrosForm((p) => ({
                        ...p,
                        garantia: undefined,
                      }));
                    }}
                  >
                    {TIPOS_GARANTIA.map((g) => (
                      <FormControlLabel
                        key={g}
                        disabled={roGarantia}
                        value={g}
                        control={<Radio sx={{ color: "var(--ig-t-888888)", "&.Mui-checked": { color: "#c00" } }} />}
                        label={(LABELS_GARANTIA[g] || g).toUpperCase()}
                        sx={{ color: "var(--ig-t-ffffff)" }}
                      />
                    ))}
                  </RadioGroup>
                  {erroEfetivo("garantia", form.garantia) && (
                    <FormHelperText sx={{ color: "#f44336", mb: 1 }}>{erroEfetivo("garantia", form.garantia)}</FormHelperText>
                  )}

                  {garantiasDe(form.garantia).includes("Caução") && (
                    <Box sx={{ backgroundColor: "var(--ig-f-252525)", borderRadius: 1, p: 2, border: "1px solid var(--ig-b-333333)", mb: 2 }}>
                      <Typography variant="body2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1.5, letterSpacing: 0.5 }}>
                        CAUÇÃO EM DINHEIRO
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Quantidade de Aluguéis*"
                            size="small"
                            type="number"
                            fullWidth
                            sx={fieldSx(roGarantia, erroEfetivo("qtdeAluguelCaucao", form.qtdeAluguelCaucao))}
                            value={form.qtdeAluguelCaucao}
                            error={!!erroEfetivo("qtdeAluguelCaucao", form.qtdeAluguelCaucao)}
                            helperText={erroEfetivo("qtdeAluguelCaucao", form.qtdeAluguelCaucao) || ""}
                            slotProps={{
                              input: {
                                readOnly: roGarantia,
                              },
                            }}
                            onChange={(e) => {
                              if (roGarantia) return;
                              const digits = e.target.value.replace(/\D/g, "");
                              setForm((p) => ({ ...p, qtdeAluguelCaucao: digits }));
                              setErrosForm((p) => ({ ...p, qtdeAluguelCaucao: undefined }));
                            }}
                            onBlur={() => {
                              if (!roGarantia && !form.qtdeAluguelCaucao)
                                setErrosForm((p) => ({ ...p, qtdeAluguelCaucao: "Quantidade de Aluguéis é obrigatória." }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Valor Caução (R$)*"
                            size="small"
                            fullWidth
                            sx={fieldSx(true, erroEfetivo("vCaucao", form.vCaucao))}
                            value={form.vCaucao}
                            error={!!erroEfetivo("vCaucao", form.vCaucao)}
                            helperText={erroEfetivo("vCaucao", form.vCaucao) || "Calculado automaticamente (Qtde × Aluguel Mensal)"}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {garantiasDe(form.garantia).includes("Seguro Fiança") && (
                    <Box
                      sx={{
                        backgroundColor: "var(--ig-f-252525)",
                        borderRadius: 1,
                        p: 2,
                        border: "1px solid var(--ig-b-333333)",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            erroEfetivo("seguradora", seguradora.razaoSocial) ||
                            errosForm.corretorSeguroNome ||
                            errosForm.corretorSeguroDocumento ||
                            errosForm.corretorSeguroEmail
                              ? "#f44336"
                              : "var(--ig-t-cccccc)",
                          fontWeight: 600,
                          mb: 1.5,
                          letterSpacing: 0.5,
                        }}
                      >
                        SEGURO FIANÇA
                        {errosForm.seguradora ? ` — ${errosForm.seguradora}` : ""}
                      </Typography>

                      {/* SEGURADORA */}
                      <FormPessoaUnica
                        label="Seguradora"
                        value={seguradora}
                        onChange={handleChangeSeguradora}
                        onBlurObrigatorio={handleBlurObrigatorioSeguradora}
                        erro={!!erroEfetivo("seguradora", seguradora.razaoSocial)}
                        somenteLeitura={roGarantia}
                        podeCadastrar={podeCadastrar}
                        tipoFixo
                        ocultarTipo
                        mostrarEmail={false}
                        mostrarSnack={mostrarSnack}
                      />

                      {/* CORRETOR DE SEGUROS */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: "1px solid var(--ig-b-333333)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "var(--ig-t-aaaaaa)",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            mb: 1,
                          }}
                        >
                          CORRETOR DE SEGUROS
                        </Typography>

                        <FormPessoaUnica
                          label="Corretor de Seguros"
                          value={corretorSeguro}
                          onChange={handleChangeCorretorSeguro}
                          onBlurObrigatorio={handleBlurObrigatorioCorretorSeguro}
                          erro={errosForm.corretorSeguroNome || false}
                          erroDocumento={errosForm.corretorSeguroDocumento || ""}
                          erroEmail={errosForm.corretorSeguroEmail || ""}
                          somenteLeitura={roGarantia}
                          podeCadastrar={podeCadastrar}
                          tipoAuto
                          ocultarTipo
                          labelDocumentoAuto="Documento do Corretor de Seguros"
                          labelEmail="E-mail do Corretor de Seguros"
                          mostrarSnack={mostrarSnack}
                        />
                      </Box>
                    </Box>
                  )}

                  {garantiasDe(form.garantia).includes("Fiador") && (
                    <Box
                      sx={{
                        backgroundColor: "var(--ig-f-252525)",
                        borderRadius: 1,
                        p: 2,
                        border: "1px solid var(--ig-b-333333)",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: erroEfetivo("fiador", fiador.razaoSocial) ? "#f44336" : "var(--ig-t-cccccc)",
                          fontWeight: 600,
                          mb: 1.5,
                          letterSpacing: 0.5,
                        }}
                      >
                        FIADOR
                        {errosForm.fiador ? ` — ${errosForm.fiador}` : ""}
                      </Typography>

                      {/* DADOS DO FIADOR */}
                      <FormPessoaUnica
                        label="Fiador"
                        value={fiador}
                        onChange={handleChangeFiador}
                        onBlurObrigatorio={handleBlurObrigatorioFiador}
                        erro={!!erroEfetivo("fiador", fiador.razaoSocial)}
                        somenteLeitura={roGarantia}
                        podeCadastrar={podeCadastrar}
                        tipoFixo
                        tipoAuto
                        mostrarSnack={mostrarSnack}
                      />

                      {/* COM IMÓVEL / SEM IMÓVEL */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: "1px solid var(--ig-b-333333)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "var(--ig-t-aaaaaa)",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            mb: 0.5,
                          }}
                        >
                          GARANTIA DO FIADOR
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={form.fiadorTipoGarantia === "COM_IMOVEL"}
                                disabled={roGarantia}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  const marcado = e.target.checked;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorTipoGarantia: marcado ? "COM_IMOVEL" : "",
                                  }));

                                  setErrosForm((prev) => ({
                                    ...prev,
                                    fiadorTipoGarantia: undefined,
                                  }));
                                }}
                                sx={{
                                  color: "var(--ig-t-888888)",

                                  "&.Mui-checked": {
                                    color: "#1998a8",
                                  },
                                }}
                              />
                            }
                            label="COM IMÓVEL"
                            sx={{
                              color: "var(--ig-t-ffffff)",
                            }}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={form.fiadorTipoGarantia === "SEM_IMOVEL"}
                                disabled={roGarantia}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  const marcado = e.target.checked;

                                  setForm((prev) => ({
                                    ...prev,

                                    fiadorTipoGarantia: marcado ? "SEM_IMOVEL" : "",

                                    ...(marcado
                                      ? {
                                          fiadorImovelTipo: "",
                                          fiadorImovelCidadeCartorioRgi: "",
                                          fiadorImovelNomeCartorioRgi: "",
                                          fiadorImovelRgi: "",
                                          fiadorImovelMatricula: "",
                                          fiadorImovelCartorio: "",
                                          fiadorImovelLivro: "",
                                          fiadorImovelFolha: "",
                                          fiadorImovelEndereco: "",
                                          fiadorImovelNumero: "",
                                          fiadorImovelBairro: "",
                                          fiadorImovelCidade: "",
                                          fiadorImovelUf: "",
                                          fiadorImovelCep: "",
                                          fiadorImovelComplemento: "",
                                          fiadorImovelUrlGoogleMaps: "",
                                        }
                                      : {}),
                                  }));

                                  setErrosForm((prev) => ({
                                    ...prev,
                                    fiadorTipoGarantia: undefined,
                                  }));
                                }}
                                sx={{
                                  color: "var(--ig-t-888888)",

                                  "&.Mui-checked": {
                                    color: "#1998a8",
                                  },
                                }}
                              />
                            }
                            label="SEM IMÓVEL"
                            sx={{
                              color: "var(--ig-t-ffffff)",
                            }}
                          />
                        </Box>

                        {errosForm.fiadorTipoGarantia && (
                          <FormHelperText
                            sx={{
                              color: "#f44336",
                            }}
                          >
                            {errosForm.fiadorTipoGarantia}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* IMÓVEL DO FIADOR */}
                      {form.fiadorTipoGarantia === "COM_IMOVEL" && (
                        <Box
                          sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid var(--ig-b-333333)",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "var(--ig-t-aaaaaa)",
                              fontSize: "0.78rem",
                              fontWeight: 700,
                              mb: 1.5,
                            }}
                          >
                            IMÓVEL EM GARANTIA DO FIADOR
                          </Typography>

                          <Grid container spacing={2}>
                            {/* TIPO DO IMÓVEL */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Tipo do Imóvel"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelTipo}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelTipo: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* CIDADE DO CARTÓRIO RGI */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Cidade do Cartório RGI"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelCidadeCartorioRgi}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelCidadeCartorioRgi: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* NOME DO CARTÓRIO RGI */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Nome do Cartório RGI"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelNomeCartorioRgi}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 255,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelNomeCartorioRgi: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* MATRÍCULA RGI */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Matrícula RGI"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelRgi}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 50,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelRgi: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* MATRÍCULA IPTU */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Matrícula do IPTU"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelMatricula}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 50,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelMatricula: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* CARTÓRIO DE REGISTRO */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextField
                                label="Cartório de Registro N°"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelCartorio}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 255,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelCartorio: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* CEP */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 2,
                              }}
                            >
                              <TextField
                                label="CEP"
                                size="small"
                                fullWidth
                                placeholder="00000-000"
                                sx={fieldSx(roGarantia)}
                                value={form.fiadorImovelCep}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 9,
                                  },
                                }}
                                onChange={async (e) => {
                                  if (roGarantia) return;

                                  const cepFormatado = mascararCEP(e.target.value);

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelCep: cepFormatado,
                                  }));

                                  if (apenasDigitos(cepFormatado).length === 8) {
                                    const dados = await buscarEnderecoPorCep(cepFormatado);

                                    if (dados) {
                                      setForm((prev) => ({
                                        ...prev,

                                        fiadorImovelEndereco: dados.endereco ? dados.endereco.toUpperCase() : prev.fiadorImovelEndereco,

                                        fiadorImovelBairro: dados.bairro ? dados.bairro.toUpperCase() : prev.fiadorImovelBairro,

                                        fiadorImovelCidade: dados.cidade ? dados.cidade.toUpperCase() : prev.fiadorImovelCidade,

                                        fiadorImovelUf: dados.uf ? dados.uf.toUpperCase() : prev.fiadorImovelUf,
                                      }));
                                    }
                                  }
                                }}
                              />
                            </Grid>

                            {/* ENDEREÇO */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 10,
                              }}
                            >
                              <TextField
                                label="Endereço do Imóvel em Garantia"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelEndereco}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 255,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelEndereco: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* NÚMERO */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 2,
                              }}
                            >
                              <TextField
                                label="Número"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelNumero}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 20,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelNumero: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* COMPLEMENTO */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 5,
                              }}
                            >
                              <TextField
                                label="Complemento"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelComplemento}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelComplemento: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* BAIRRO */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 5,
                              }}
                            >
                              <TextField
                                label="Bairro"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelBairro}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelBairro: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* CIDADE */}
                            <Grid
                              size={{
                                xs: 12,
                                sm: 10,
                              }}
                            >
                              <TextField
                                label="Cidade"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelCidade}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelCidade: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* UF */}
                            <Grid
                              size={{
                                xs: 4,
                                sm: 2,
                              }}
                            >
                              <TextField
                                label="UF"
                                size="small"
                                fullWidth
                                sx={fieldSx(roGarantia, false, true)}
                                value={form.fiadorImovelUf}
                                slotProps={{
                                  input: {
                                    readOnly: roGarantia,
                                  },
                                  htmlInput: {
                                    maxLength: 2,
                                  },
                                }}
                                onChange={(e) => {
                                  if (roGarantia) return;

                                  setForm((prev) => ({
                                    ...prev,
                                    fiadorImovelUf: e.target.value.toUpperCase(),
                                  }));
                                }}
                              />
                            </Grid>

                            {/* GOOGLE MAPS */}
                            <Grid size={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "flex-start",
                                }}
                              >
                                <TextField
                                  label="Link do Imóvel no Google Maps"
                                  size="small"
                                  fullWidth
                                  sx={fieldSx(roGarantia)}
                                  placeholder="https://www.google.com/maps/..."
                                  value={form.fiadorImovelUrlGoogleMaps}
                                  slotProps={{
                                    input: {
                                      readOnly: roGarantia,
                                    },
                                    htmlInput: {
                                      maxLength: 500,
                                    },
                                  }}
                                  onChange={(e) => {
                                    if (roGarantia) return;

                                    setForm((prev) => ({
                                      ...prev,
                                      fiadorImovelUrlGoogleMaps: e.target.value,
                                    }));
                                  }}
                                />

                                {form.fiadorImovelUrlGoogleMaps?.trim() && (
                                  <>
                                    <Button
                                      variant="contained"
                                      startIcon={<FaCopy />}
                                      onClick={async () => {
                                        const ok = await copiarTexto(form.fiadorImovelUrlGoogleMaps);

                                        mostrarSnack(ok ? "Link copiado!" : "Não foi possível copiar.", ok ? "success" : "error");
                                      }}
                                      sx={{
                                        width: "auto",
                                        minWidth: 110,
                                        height: 40,
                                        mt: "2px",
                                        backgroundColor: "#2196f3",

                                        "&:hover": {
                                          backgroundColor: "#1976d2",
                                        },

                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Copiar
                                    </Button>

                                    <Button
                                      variant="contained"
                                      startIcon={<FaGlobe />}
                                      onClick={() => window.open(form.fiadorImovelUrlGoogleMaps, "_blank", "noopener,noreferrer")}
                                      sx={{
                                        width: "auto",
                                        minWidth: 110,
                                        height: 40,
                                        mt: "2px",
                                        backgroundColor: "#f97316",

                                        "&:hover": {
                                          backgroundColor: "#ea580c",
                                        },

                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Abrir
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  )}

                  {garantiasDe(form.garantia).includes("Imobiliária") && (
                    <Box sx={{ backgroundColor: "var(--ig-f-252525)", borderRadius: 1, p: 2, border: "1px solid var(--ig-b-333333)", mb: 2 }}>
                      <Typography variant="body2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1.5, letterSpacing: 0.5 }}>
                        CAUÇÃO EM IMÓVEL
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "var(--ig-t-cccccc)",
                            fontWeight: 600,
                            mb: 1.5,
                            letterSpacing: 0.5,
                          }}
                        >
                          CAUCIONANTE
                        </Typography>

                        <FormPessoaUnica
                          label="Caucionante"
                          value={caucionante}
                          onChange={handleChangeCaucionante}
                          onBlurObrigatorio={handleBlurObrigatorioCaucionante}
                          erro={!!erroEfetivo("caucionante", caucionante.razaoSocial)}
                          somenteLeitura={roGarantia}
                          podeCadastrar={podeCadastrar}
                          tipoFixo
                          ocultarTipo
                          mostrarEmail={false}
                          mostrarSnack={mostrarSnack}
                        />
                      </Box>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Tipo do Imóvel"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro, false, true)}
                            value={form.garantiaTipoImovel}
                            slotProps={{ input: { readOnly: roGarantia }, htmlInput: { maxLength: 100 } }}
                            onChange={(e) => {
                              if (!roGarantia) setForm((p) => ({ ...p, garantiaTipoImovel: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Matrícula RGI"
                            size="small"
                            fullWidth
                            sx={fieldSx(roGarantia, false, true)}
                            value={form.garantiaRgi}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaRgi: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Matrícula do IPTU"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro, false, true)}
                            value={form.garantiaMatricula}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaMatricula: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="Descrição do Imóvel"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            sx={fieldSx(roGarantia, false, true)}
                            value={form.garantiaDescricaoImovel}
                            slotProps={{ input: { readOnly: roGarantia } }}
                            onChange={(e) => {
                              if (!roGarantia)
                                setForm((p) => ({
                                  ...p,
                                  garantiaDescricaoImovel: e.target.value.toUpperCase(),
                                }));
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Cadastro Municipal"
                            size="small"
                            fullWidth
                            sx={fieldSx(roGarantia, false, true)}
                            value={form.garantiaCadastroMunicipal}
                            slotProps={{
                              input: { readOnly: roGarantia },
                              htmlInput: { maxLength: 100 },
                            }}
                            onChange={(e) => {
                              if (!roGarantia)
                                setForm((p) => ({
                                  ...p,
                                  garantiaCadastroMunicipal: e.target.value.toUpperCase(),
                                }));
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Setor"
                            size="small"
                            fullWidth
                            sx={fieldSx(roGarantia, false, true)}
                            value={form.garantiaSetor}
                            slotProps={{
                              input: { readOnly: roGarantia },
                              htmlInput: { maxLength: 100 },
                            }}
                            onChange={(e) => {
                              if (!roGarantia)
                                setForm((p) => ({
                                  ...p,
                                  garantiaSetor: e.target.value.toUpperCase(),
                                }));
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Quarteirão"
                            size="small"
                            fullWidth
                            sx={fieldSx(roGarantia, false, true)}
                            value={form.garantiaQuarteirao}
                            slotProps={{
                              input: { readOnly: roGarantia },
                              htmlInput: { maxLength: 100 },
                            }}
                            onChange={(e) => {
                              if (!roGarantia)
                                setForm((p) => ({
                                  ...p,
                                  garantiaQuarteirao: e.target.value.toUpperCase(),
                                }));
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <TextField
                            label="Cartório de Registro N°"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro, false, true)}
                            value={form.garantiaCartorio}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaCartorio: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2, md: 2 }}>
                          <TextField
                            label="CEP"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro)}
                            placeholder="00000-000"
                            value={form.garantiaCep}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 9 } }}
                            onChange={async (e) => {
                              if (ro) return;
                              const cepFormatado = mascararCEP(e.target.value);
                              setForm((p) => ({ ...p, garantiaCep: cepFormatado }));
                              if (apenasDigitos(cepFormatado).length === 8) {
                                const dados = await buscarEnderecoPorCep(cepFormatado);
                                if (dados) {
                                  setForm((p) => ({
                                    ...p,
                                    garantiaEndereco: dados.endereco ? dados.endereco.toUpperCase() : p.garantiaEndereco,
                                    garantiaBairro: dados.bairro ? dados.bairro.toUpperCase() : p.garantiaBairro,
                                    garantiaCidade: dados.cidade ? dados.cidade.toUpperCase() : p.garantiaCidade,
                                    garantiaUf: dados.uf ? dados.uf.toUpperCase() : p.garantiaUf,
                                  }));
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 10, md: 10 }}>
                          <TextField
                            label="Endereço do Imóvel em Garantia"
                            size="small"
                            fullWidth
                            sx={{ ...fieldSx(ro, false, true), minWidth: 200 }}
                            value={form.garantiaEndereco}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaEndereco: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2, md: 2 }}>
                          <TextField
                            label="Número"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro, false, true)}
                            value={form.garantiaNumero}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 20 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaNumero: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5, md: 5 }}>
                          <TextField
                            label="Complemento"
                            size="small"
                            fullWidth
                            sx={{ ...fieldSx(ro, false, true), minWidth: 200 }}
                            value={form.garantiaComplemento}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaComplemento: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5, md: 5 }}>
                          <TextField
                            label="Bairro"
                            size="small"
                            fullWidth
                            sx={{ ...fieldSx(ro, false, true), minWidth: 200 }}
                            value={form.garantiaBairro}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaBairro: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 10, md: 10 }}>
                          <TextField
                            label="Cidade"
                            size="small"
                            fullWidth
                            sx={{ ...fieldSx(ro, false, true), minWidth: 200 }}
                            value={form.garantiaCidade}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaCidade: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, sm: 2, md: 2 }}>
                          <TextField
                            label="UF"
                            size="small"
                            fullWidth
                            sx={fieldSx(ro, false, true)}
                            value={form.garantiaUf}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 2 } }}
                            onChange={(e) => {
                              if (!ro) setForm((p) => ({ ...p, garantiaUf: e.target.value.toUpperCase() }));
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <TextField
                              label="Link do Imóvel no Google Maps"
                              size="small"
                              fullWidth
                              sx={fieldSx(ro)}
                              placeholder="https://www.google.com/maps/..."
                              value={form.garantiaUrlGoogleMaps}
                              slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                              onChange={(e) => {
                                if (!ro) setForm((p) => ({ ...p, garantiaUrlGoogleMaps: e.target.value }));
                              }}
                            />
                            {form.garantiaUrlGoogleMaps?.trim() && (
                              <>
                                <Button
                                  variant="contained"
                                  startIcon={<FaCopy />}
                                  onClick={async () => {
                                    const ok = await copiarTexto(form.garantiaUrlGoogleMaps);
                                    mostrarSnack(ok ? "Link copiado!" : "Não foi possível copiar.", ok ? "success" : "error");
                                  }}
                                  sx={{
                                    width: "auto",
                                    minWidth: 110,
                                    height: 40,
                                    mt: "2px",
                                    backgroundColor: "#2196f3",
                                    "&:hover": { backgroundColor: "#1976d2" },
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Copiar
                                </Button>
                                <Button
                                  variant="contained"
                                  startIcon={<FaGlobe />}
                                  onClick={() => window.open(form.garantiaUrlGoogleMaps, "_blank", "noopener,noreferrer")}
                                  sx={{
                                    width: "auto",
                                    minWidth: 110,
                                    height: 40,
                                    mt: "2px",
                                    backgroundColor: "#f97316",
                                    "&:hover": { backgroundColor: "#ea580c" },
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Abrir
                                </Button>
                              </>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Documentos de comprovação */}
                      <ChecklistGarantiaImobiliaria
                        ro={roGarantia}
                        editandoId={editandoId}
                        arquivoGarantia={arquivoGarantia}
                        enviando={enviandoGarantiaDoc}
                        garantiaDocs={garantiaDocs}
                        garantiaDocsPendentes={garantiaDocsPendentes}
                        onSelecionarArquivo={handleSelecionarArquivoGarantia}
                        onEnviarOuAdicionar={editandoId ? handleUploadGarantiaDoc : handleAdicionarGarantiaDocPendente}
                        onVisualizar={handleVisualizarGarantiaDoc}
                        onDownload={handleDownloadGarantiaDoc}
                        onDelete={handleDeleteGarantiaDoc}
                        onVisualizarPendente={handleVisualizarGarantiaDocPendente}
                        onRemoverPendente={handleRemoverGarantiaDocPendente}
                      />
                    </Box>
                  )}
                </Box>

                {/* === Condições === */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
                  >
                    CONDIÇÕES
                  </Typography>
                  <TextField
                    size="small"
                    label="ENTREGA DA CHAVE NA ASSINATURA DO CONTRATO"
                    value={entregaChaveNaAssinatura}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    sx={{
                      ...fieldSx(true, false),
                      width: "100%",
                      maxWidth: "520px",
                      mb: 2,
                    }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    label="Condições"
                    value={form.consideracoes}
                    slotProps={{ input: { readOnly: ro } }}
                    onChange={(e) => !ro && setForm((p) => ({ ...p, consideracoes: e.target.value.toUpperCase() }))}
                    sx={{ ...fieldSx(ro, false), "& textarea": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
                  />
                </Box>

                {/* === Botões === */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  {/* =====================================================
      VISUALIZAÇÃO NORMAL
      ===================================================== */}
                  {editandoId && somenteLeitura && !editandoPendenciaGarantia && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",

                          "&:hover": {
                            backgroundColor: "var(--ig-f-255-255-255-a005)",
                          },

                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Fechar
                      </Button>

                      {!emAprovacao && podeCadastrar && (
                        <Button
                          variant="contained"
                          startIcon={<FaEdit />}
                          onClick={handleHabilitarEdicao}
                          sx={{
                            width: "auto",
                            backgroundColor: "#1998a8",

                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },

                            fontWeight: "bold",
                            py: 1,
                            px: 3,
                          }}
                        >
                          Editar
                        </Button>
                      )}

                      {!emAprovacao && podeInutilizar && (
                        <Button
                          variant="contained"
                          startIcon={<BlockIcon />}
                          onClick={() => handleAbrirInutilizar(editandoId)}
                          sx={{
                            width: "auto",
                            backgroundColor: "#d32f2f",

                            "&:hover": {
                              backgroundColor: "#c62828",
                            },

                            fontWeight: "bold",
                            py: 1,
                            px: 3,
                          }}
                        >
                          Inutilizar
                        </Button>
                      )}

                      {emAprovacao && aprovacaoInfo.statusGeral === "Inutilizada" && podeInutilizar && (
                        <Button
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => handleAbrirAtivar(editandoId, aprovacaoInfo.motivoInutilizacao)}
                          sx={{
                            width: "auto",
                            backgroundColor: "#2e7d32",

                            "&:hover": {
                              backgroundColor: "#1b5e20",
                            },

                            fontWeight: "bold",
                            py: 1,
                            px: 3,
                          }}
                        >
                          Ativar
                        </Button>
                      )}
                    </>
                  )}

                  {/* =====================================================
      EDIÇÃO DA PENDÊNCIA DE GARANTIA
      ===================================================== */}
                  {editandoId && editandoPendenciaGarantia && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",

                          "&:hover": {
                            backgroundColor: "var(--ig-f-255-255-255-a005)",
                          },

                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Fechar
                      </Button>

                      {podeCadastrar && (
                        <Button
                          variant="contained"
                          startIcon={
                            salvando ? (
                              <CircularProgress
                                size={16}
                                sx={{
                                  color: "var(--ig-t-ffffff)",
                                }}
                              />
                            ) : (
                              <SendIcon />
                            )
                          }
                          onClick={handleEnviarPendenciaGarantia}
                          disabled={salvando}
                          sx={{
                            width: "auto",
                            backgroundColor: "#1998a8",

                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },

                            fontWeight: "bold",
                            py: 1,
                            px: 4,
                          }}
                        >
                          {salvando ? "Enviando..." : "Enviar PC"}
                        </Button>
                      )}
                    </>
                  )}

                  {/* =====================================================
      EDIÇÃO NORMAL / NOVA PROPOSTA
      ===================================================== */}
                  {!somenteLeitura && !editandoPendenciaGarantia && (
                    <>
                      {editandoId && (
                        <Button
                          variant="outlined"
                          startIcon={<FaTimes />}
                          onClick={handleCancelarForm}
                          sx={{
                            width: "auto",
                            borderColor: "var(--ig-b-555555)",
                            color: "var(--ig-t-cccccc)",

                            "&:hover": {
                              backgroundColor: "var(--ig-f-255-255-255-a005)",
                            },

                            fontWeight: "bold",
                            px: 3,
                          }}
                        >
                          Cancelar
                        </Button>
                      )}

                      {podeCadastrar && (
                        <>
                          {!emAprovacao && (
                            <Button
                              variant="contained"
                              startIcon={
                                salvando ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{
                                      color: "var(--ig-t-ffffff)",
                                    }}
                                  />
                                ) : (
                                  <FaSave />
                                )
                              }
                              onClick={handleSalvar}
                              disabled={salvando}
                              sx={{
                                width: "auto",
                                backgroundColor: "#2e7d32",

                                "&:hover": {
                                  backgroundColor: "#1b5e20",
                                },

                                fontWeight: "bold",
                                py: 1,
                                px: 4,
                              }}
                            >
                              {salvando ? "Salvando..." : editandoId ? "Atualizar" : "Cadastrar"}
                            </Button>
                          )}

                          {editandoId && !emAprovacao && (
                            <Button
                              variant="contained"
                              startIcon={<SendIcon />}
                              onClick={handleEnviarAprovacao}
                              sx={{
                                width: "auto",
                                backgroundColor: "#1998a8",

                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },

                                fontWeight: "bold",
                                py: 1,
                                px: 4,
                              }}
                            >
                              Enviar para Aprovação
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {abaAtiva === "aprovacao" && editandoId && (
              <>
                <PainelAprovacao
                  titulo="Proposta"
                  statusField={aprovacaoInfo.statusGeral}
                  statusNaoIniciado="EmDigitacao"
                  mostrarCabecalho={false}
                  mostrarBotaoEnviar={false}
                  aprovacao={aprovacaoInfo.aprovacao}
                  autorizacoes={autorizacoesProposta}
                  historicos={historicosProposta}
                  idUsuarioLogado={userId}
                  idCriador={aprovacaoInfo.idUsuario}
                  revisarPorAlcada
                  usuarioEmAlcada={aprovacaoInfo.usuarioEhAprovador}
                  usuarioEhAprovador={aprovacaoInfo.usuarioEhAprovador}
                  reabrirProcessoBloqueado={aprovacaoInfo.statusFichas === "EmAprovacao" || aprovacaoInfo.statusAnaliseCredito === "EmAprovacao"}
                  onAprovar={handleAprovar}
                  onAbrirReprovar={() => {
                    document.activeElement?.blur();
                    setDialogReprovar({ aberto: true, motivo: "" });
                  }}
                  onAbrirQuestionar={() => {
                    document.activeElement?.blur();
                    setDialogQuestionar({ aberto: true, motivo: "", idsUsuarioResposta: [], arquivo: null });
                  }}
                  onAbrirResponder={(pergunta) => setDialogResponder({ aberto: true, resposta: "", pergunta, arquivo: null })}
                  onConfirmarRevisar={() => {
                    document.activeElement?.blur();
                    setConfirmRevisar({ aberto: true, motivo: "" });
                  }}
                  processando={processandoAprovacao}
                  podeEditar={podeCadastrar}
                  rotaAnexoBase="/api/propostas"
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelarForm}
                    sx={{
                      width: "auto",
                      borderColor: "var(--ig-b-555555)",
                      color: "var(--ig-t-cccccc)",
                      "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                      fontWeight: "bold",
                      px: 3,
                    }}
                  >
                    Fechar
                  </Button>
                </Box>
              </>
            )}

            {abaAtiva === "eventos" && editandoId && aprovacaoInfo.motivoInutilizacao && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ backgroundColor: "var(--ig-f-2a1f1f)", border: "1px solid var(--ig-b-4e342e)", borderRadius: 1, p: 2 }}>
                  <Typography sx={{ color: "var(--ig-t-cccccc)", fontWeight: 700, fontSize: "0.8rem", mb: 0.5, letterSpacing: 0.5 }}>
                    HISTÓRICO DE INUTILIZAÇÃO
                  </Typography>
                  <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.85rem" }}>
                    Inutilizada em <strong style={{ color: "var(--ig-t-ffffff)" }}>{formatarDataHora(aprovacaoInfo.dataInutilizacao)}</strong>
                    {aprovacaoInfo.usuarioInutilizacao && (
                      <>
                        {" "}
                        por <strong style={{ color: "var(--ig-t-ffffff)" }}>{aprovacaoInfo.usuarioInutilizacao.nome}</strong>
                      </>
                    )}{" "}
                    — Motivo: <strong style={{ color: "var(--ig-t-ffffff)" }}>{aprovacaoInfo.motivoInutilizacao}</strong>
                  </Typography>
                  {aprovacaoInfo.dataReativacao && (
                    <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.85rem", mt: 0.5 }}>
                      Reativada em <strong style={{ color: "var(--ig-t-ffffff)" }}>{formatarDataHora(aprovacaoInfo.dataReativacao)}</strong>
                      {aprovacaoInfo.usuarioReativacao && (
                        <>
                          {" "}
                          por <strong style={{ color: "var(--ig-t-ffffff)" }}>{aprovacaoInfo.usuarioReativacao.nome}</strong>
                        </>
                      )}
                      {aprovacaoInfo.motivoReativacao && (
                        <>
                          {" "}
                          — Motivo: <strong style={{ color: "var(--ig-t-ffffff)" }}>{aprovacaoInfo.motivoReativacao}</strong>
                        </>
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      <DialogResponderAprovacao
        aberto={dialogResponder.aberto}
        resposta={dialogResponder.resposta}
        pergunta={dialogResponder.pergunta}
        onChangeResposta={(resposta) => setDialogResponder((p) => ({ ...p, resposta }))}
        arquivo={dialogResponder.arquivo}
        onChangeArquivo={(arquivo) => setDialogResponder((p) => ({ ...p, arquivo }))}
        onCancelar={() => setDialogResponder({ aberto: false, resposta: "", pergunta: "", arquivo: null })}
        onConfirmar={handleResponder}
        titulo="Responder Reprovação"
        contentText="Escreva sua resposta. Ela ficará registrada no histórico junto com a reprovação."
        labelCampo="Resposta"
        rows={6}
        maxLength={255}
        labelConfirmar="Enviar Resposta"
        corConfirmar="#e65100"
        processando={processandoAprovacao}
      />

      <DialogReprovarAprovacao
        aberto={dialogReprovar.aberto}
        motivo={dialogReprovar.motivo}
        onChangeMotivo={(motivo) => setDialogReprovar((p) => ({ ...p, motivo }))}
        onCancelar={() => setDialogReprovar({ aberto: false, motivo: "" })}
        onConfirmar={handleReprovar}
        titulo="Reprovar Proposta"
        contentText='Informe o motivo da reprovação. Esta ação é definitiva e o status da proposta passará para "Reprovada".'
        labelConfirmar="Confirmar"
        corConfirmar="#d32f2f"
        processando={processandoAprovacao}
      />

      <DialogQuestionarAprovacao
        aberto={dialogQuestionar.aberto}
        motivo={dialogQuestionar.motivo}
        onChangeMotivo={(motivo) => setDialogQuestionar((p) => ({ ...p, motivo }))}
        idsUsuarioResposta={dialogQuestionar.idsUsuarioResposta}
        onChangeUsuariosResposta={(idsUsuarioResposta) => setDialogQuestionar((p) => ({ ...p, idsUsuarioResposta }))}
        arquivo={dialogQuestionar.arquivo}
        onChangeArquivo={(arquivo) => setDialogQuestionar((p) => ({ ...p, arquivo }))}
        usuariosAtivos={usuariosAprovadores}
        idUsuarioAtual={userId}
        onCancelar={() => setDialogQuestionar({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null })}
        onConfirmar={handleQuestionar}
        titulo="Questionar Proposta"
        contentText="Informe o motivo e indique o(s) usuário(s) que devem responder. A proposta ficará aguardando a resposta de todos antes de prosseguir."
        rows={5}
        maxLength={255}
        labelSelect="Usuários que devem responder"
        labelConfirmar="Enviar Questão"
        corConfirmar="#7b1fa2"
        processando={processandoAprovacao}
      />

      <DialogReprovarAprovacao
        aberto={confirmRevisar.aberto}
        motivo={confirmRevisar.motivo}
        onChangeMotivo={(motivo) => setConfirmRevisar((p) => ({ ...p, motivo }))}
        onCancelar={() => setConfirmRevisar({ aberto: false, motivo: "" })}
        onConfirmar={handleRevisar}
        titulo="Solicitar Revisão"
        contentText='Ao revisar, todas as aprovações do fluxo atual serão invalidadas e a proposta voltará para "Em Digitação". Informe o motivo da revisão:'
        rows={4}
        maxLength={255}
        labelConfirmar="Confirmar"
        corConfirmar="#e65100"
        processando={processandoAprovacao}
      />

      <DialogReprovarAprovacao
        aberto={confirmReabrir.aberto}
        motivo={confirmReabrir.motivo}
        onChangeMotivo={(motivo) => setConfirmReabrir((p) => ({ ...p, motivo }))}
        onCancelar={() => setConfirmReabrir({ aberto: false, motivo: "" })}
        onConfirmar={handleReabrirProposta}
        titulo="Reabrir Proposta"
        contentText='Isso vai invalidar o fluxo de aprovação da Proposta, das Fichas de Cadastro e da Análise de Crédito, voltando a proposta para "Em Digitação". Informe o motivo:'
        rows={4}
        maxLength={255}
        labelConfirmar="Confirmar"
        corConfirmar="#e65100"
      />

      {/* Dialog de inutilização */}
      <DialogInutilizarProposta
        aberto={confirmInutilizar.aberto}
        onCancelar={() => setConfirmInutilizar({ aberto: false, id: null })}
        onConfirmar={handleInutilizar}
      />

      <DialogReprovarAprovacao
        aberto={confirmAtivar.aberto}
        motivo={confirmAtivar.motivo}
        onChangeMotivo={(motivo) => setConfirmAtivar((p) => ({ ...p, motivo }))}
        onCancelar={() => setConfirmAtivar({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" })}
        onConfirmar={handleAtivar}
        titulo="Ativar Proposta"
        contentText={`A proposta voltará para o status "Em Digitação".${confirmAtivar.motivoInutilizacao ? ` Motivo da inutilização: ${confirmAtivar.motivoInutilizacao}` : ""} Informe o motivo da reativação:`}
        rows={4}
        maxLength={255}
        labelConfirmar="Ativar"
        corConfirmar="#2e7d32"
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, aberto: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Template de impressão — oculto na tela; conteúdo copiado para iframe ao imprimir */}
      <div style={{ display: "none" }}>
        {propostaParaImprimir && (
          <PropostaPrintTemplate
            proposta={propostaParaImprimir}
            quadroResumo={quadroResumoParaImprimir}
            modoTemplate={Boolean(propostaParaImprimir?.__templatePC)}
          />
        )}
      </div>
    </ThemeProvider>
  );
}
